import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  Suspense
} from 'react';
import { createPortal } from 'react-dom';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { usePageTitle } from '@/hooks/usePageTitle';
import { SharedNavbar } from '@/components/SharedNavbar';
import { GlobalToolsExpandingPanel } from '@/components/GlobalToolsExpandingPanel';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PageTransition } from '@/components/PageTransition';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from '@/components/ui/hover-card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';

// Add smooth fade animation styles and hide scrollbars
if (
  typeof document !== 'undefined' &&
  !document.getElementById('matrix-animations')
) {
  const style = document.createElement('style');
  style.id = 'matrix-animations';
  style.textContent = `
    @keyframes pulse-subtle {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.9; }
    }
    .animate-pulse-subtle {
      animation: pulse-subtle 3s ease-in-out infinite;
    }
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .animate-fadeIn {
      animation: fadeIn 0.5s ease-out;
    }
    /* Hide scrollbar for Chrome, Safari and Opera */
    .hide-scrollbar::-webkit-scrollbar {
      display: none;
    }
    /* Hide scrollbar for IE, Edge and Firefox */
    .hide-scrollbar {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
  `;
  document.head.appendChild(style);
}

import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  gcsUploadFile,
  gcsDeleteFile,
  gcsGetSignedReadUrl,
  gcsGetPublicUrl
} from '@/lib/gcsStorage';
import { parseAiFlowResponse } from '@/lib/parseAiFlowResponse';
import { matchMemberWithCandidates, matchRequirementWithCandidates } from '@/lib/certificateSmartMatch';
import { useGcsUpload } from '@/hooks/useGcsUpload';
import { useSpaceData } from '@/hooks/useSpaceData';
import { useMatrixModals } from '@/hooks/useMatrixModals';
import { useMatrixData } from '@/hooks/useMatrixData';
import { useMatrixFilters } from '@/hooks/useMatrixFilters';
import { useMatrixViews } from '@/hooks/useMatrixViews';
import { userQualificationsService } from '@/lib/userQualificationsService';
import {
  buildQualificationMaps,
  findQualification,
  calculateEffectiveStatus
} from '@/lib/qualificationMatcher';
import { siteComplianceService } from '@/lib/siteComplianceService';
import { useMatrixBroadcast } from '@/hooks/useMatrixBroadcast';
import { useComplianceStats } from '@/hooks/useComplianceStats';
// Removed AppShell3 - using custom layout
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { SpaceSelectorDialog } from '@/components/SpaceSelectorDialog';
import { toast } from 'sonner';
import {
  Grid3X3,
  List,
  UserPlus,
  FileText,
  BarChart3,
  Plus,
  X,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Calendar,
  Eye,
  EyeOff,
  Lock,
  Users,
  Shield,
  LayoutGrid,
  Upload,
  FileUp,
  Info,
  Building2,
  MapPin,
  Monitor,
  DollarSign,
  PoundSterling,
  CalendarClock,
  FileCheck,
  MoreVertical,
  Edit2,
  Check,
  Search,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  RotateCcw,
  Sparkles,
  Minus,
  Columns,
  Award,
  CreditCard,
  ExternalLink,
  CalendarCheck
} from 'lucide-react';
// Removed RadialDock - using expanding tile instead
// Lazy load modals for better performance - they're heavy and rarely used immediately
const UploadModal = lazyWithRetry(() => import('@/components/modals/UploadModal'));
const BookingModal = lazyWithRetry(() => import('@/components/modals/BookingModal'));
const QuoteModalV2 = lazyWithRetry(() => import('@/components/modals/QuoteModalV2'));
const DetailsModal = lazyWithRetry(() => import('@/components/modals/DetailsModal'));
const RemoveModal = lazyWithRetry(() => import('@/components/modals/RemoveModal'));
const CrossSpaceQualificationModal = lazyWithRetry(
  () => import('@/components/modals/CrossSpaceQualificationModal')
);
const AddQualification = lazyWithRetry(() =>
  import('@/components/modals/qualifications/AddQualification').then(
    module => ({ default: module.AddQualification })
  )
);
const ProfileQuickQuotePopup = lazyWithRetry(
  () => import('@/components/modals/ProfileQuickQuotePopup')
);
const EditQualification = lazyWithRetry(() =>
  import('@/components/modals/qualifications/EditQualification').then(
    module => ({ default: module.EditQualification })
  )
);

// Import extracted matrix components
import {
  MatrixGrid,
  SortableTab,
  SortableFieldItem,
  EditableMatrixName,
  // Dialog components
  DeleteViewDialog,
  AddMemberDialog,
  AddQualificationDialog,
  AddMembersToQualificationDialog,
  SmartUploadConfirmationDialog,
  BulkUploadConfirmationDialog,
  BulkUploadResultsDialog,
  // Qualification members panel
  QualificationMembersPanel,
  fetchCompanyMembersWithQualification
} from './components';

// ColumnHeader and MatrixCell components have been extracted to ./components/
// and are now used via the MatrixGrid component

const MatrixPage = ({
  stackId,
  stackName,
  stackImage,
  stackProjects,
  isStackView = false
}) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, company } = useAuth();
  const { getViewableUrl } = useGcsUpload();
  const [globalToolsOpen, setGlobalToolsOpen] = useState(false);
  const handleGlobalToolsToggle = useCallback(
    () => setGlobalToolsOpen(prev => !prev),
    []
  );

  // For stack view, use stackProjects; otherwise use normal projectId
  const effectiveProjectId = isStackView ? null : projectId;

  // Memoize projectIds to prevent infinite re-renders in useEffect
  const projectIds = useMemo(() => {
    return isStackView ? stackProjects?.map(p => p.id) : [projectId];
  }, [isStackView, stackProjects, projectId]);

  // Use the cached space data hook (only for non-stack views)
  // Pass company.id to validate the space belongs to the user's current company
  const {
    space,
    spaceTools,
    loading: spaceLoading,
    accessDenied
  } = useSpaceData(effectiveProjectId, userProfile?.id, company?.id);

  // Set page title
  usePageTitle(
    isStackView
      ? `${stackName} Matrix`
      : space?.name
        ? `${space.name} Matrix`
        : 'Matrix'
  );

  // Redirect if user doesn't have access (e.g., switched companies)
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);

  // Consolidated modal state management
  const {
    // State
    selectedCell,
    dialogOpen,
    uploadModalOpen,
    bookingModalOpen,
    quoteModalOpen,
    quoteDetails,
    detailsModalOpen,
    removeModalOpen,
    crossSpaceModalOpen,
    addMemberDialogOpen,
    addQualificationDialogOpen,
    isAddQualificationModalOpen,
    addMembersDialogOpen,
    selectedQualificationForAdd,
    deleteViewDialogOpen,
    viewToDelete,
    contextMenuOpen,
    contextMenuPosition,
    contextMenuCell,
    headerContextMenuOpen,
    headerContextMenuPosition,
    headerContextMenuQual,
    certificatesModalOpen,
    selectedMemberCertificates,
    spaceSelectorOpen,
    pendingAssignment,
    validationWarning,
    dockOpen,
    dockPosition,
    dockCell,
    hideFieldsOpen,
    filterOpen,
    smartUploadConfirmation,
    bulkUploadConfirmation,
    bulkUploadResults,
    // Setters (backward compatible)
    setSelectedCell,
    setDialogOpen,
    setUploadModalOpen,
    setBookingModalOpen,
    setQuoteModalOpen,
    setQuoteDetails,
    setDetailsModalOpen,
    setRemoveModalOpen,
    setCrossSpaceModalOpen,
    setAddMemberDialogOpen,
    setAddQualificationDialogOpen,
    setIsAddQualificationModalOpen,
    setAddMembersDialogOpen,
    setSelectedQualificationForAdd,
    setDeleteViewDialogOpen,
    setViewToDelete,
    setContextMenuOpen,
    setContextMenuPosition,
    setContextMenuCell,
    setHeaderContextMenuOpen,
    setHeaderContextMenuPosition,
    setHeaderContextMenuQual,
    setCertificatesModalOpen,
    setSelectedMemberCertificates,
    setSpaceSelectorOpen,
    setPendingAssignment,
    setValidationWarning,
    setDockOpen,
    setDockPosition,
    setDockCell,
    setHideFieldsOpen,
    setFilterOpen,
    setSmartUploadConfirmation,
    setBulkUploadConfirmation,
    setBulkUploadResults,
    // Action creators
    openCellDialog,
    closeCellDialog,
    openContextMenu,
    closeContextMenu,
    openHeaderContextMenu,
    closeHeaderContextMenu,
    openCertificatesModal,
    closeCertificatesModal,
    openSpaceSelector,
    closeSpaceSelector,
    openDock,
    closeDock,
    openAddMembersDialog,
    closeAddMembersDialog,
    openDeleteViewDialog,
    closeDeleteViewDialog
  } = useMatrixModals();

  // Quick Quote Popup state
  const [quickQuotePopupOpen, setQuickQuotePopupOpen] = useState(false);
  const [quickQuoteCellData, setQuickQuoteCellData] = useState(null);

  // Mark as Booked confirmation dialog state
  const [markBookedConfirmOpen, setMarkBookedConfirmOpen] = useState(false);
  const [markBookedCell, setMarkBookedCell] = useState(null);

  // Matrix data fetching - uses v3 RPC with client-side enrichment
  const {
    loading: matrixLoading,
    error: matrixError,
    data: hookMatrixData,
    requirements: hookRequirements,
    teamMembers: hookTeamMembers,
    qualifications: hookQualifications,
    assignmentMap,
    requirementStatsMap,
    summary: matrixSummary,
    refetch: refetchMatrixData
  } = useMatrixData(effectiveProjectId, {
    projectIds: isStackView ? projectIds : null,
    projects: isStackView ? stackProjects : null,
    isStackView,
    version: 'v4'
  });

  // SINGLE SOURCE OF TRUTH: Compliance stats from RPC
  // This replaces all inline compliance calculations to ensure consistency across the app
  // Now supports both single projects AND stack views using the unified hook
  const {
    data: complianceStatsData,
    loading: complianceStatsLoading,
    refetch: refetchComplianceStats
  } = useComplianceStats(effectiveProjectId, {
    enabled: !!effectiveProjectId || (isStackView && projectIds?.length > 0),
    projectIds: isStackView ? projectIds : null,
    isStackView
  });

  // Matrix filtering - uses staged memoization for performance
  const {
    // State
    searchInput,
    debouncedSearch,
    activeFilters,
    hiddenColumns,
    hiddenRows,
    columnOrder,
    rowOrder,
    // Filtered data (renamed to avoid conflict with space filtering)
    filteredRequirements: hookFilteredRequirements,
    filteredTeamMembers: hookFilteredTeamMembers,
    // Actions
    setSearchInput,
    toggleFilter,
    setFilter,
    toggleColumn,
    toggleRow,
    showAllColumns,
    showAllRows,
    hideAllColumns,
    hideAllRows,
    clearActiveFilters,
    setColumnOrder,
    setRowOrder,
    clearFilters,
    resetToDefaults,
    applyViewFilters,
    getCurrentFilters,
    hasChangesFrom
  } = useMatrixFilters(
    hookRequirements || [],
    hookTeamMembers || [],
    assignmentMap
  );

  // Matrix views management - handles saved views, auto-save, CRUD operations
  const {
    views,
    activeViewId,
    activeView,
    isLoading: viewsLoading,
    savingStatus,
    isViewOwner,
    setActiveViewId,
    loadViews: reloadViews,
    saveViewFilters,
    autoSaveFilters,
    createView,
    renameView,
    deleteView,
    toggleViewPublic,
    reorderViews,
    duplicateView
  } = useMatrixViews(projectId, userProfile?.id, { companyId: company?.id });

  // Space filter for stack view
  const [selectedSpaceFilter, setSelectedSpaceFilter] = useState('all');

  // Track cells currently being assigned (for loading spinners)
  const [assigningCells, setAssigningCells] = useState(new Set());
  const addAssigningCell = useCallback((key) => {
    setAssigningCells(prev => { const next = new Set(prev); next.add(key); return next; });
  }, []);
  const removeAssigningCell = useCallback((key) => {
    setAssigningCells(prev => { const next = new Set(prev); next.delete(key); return next; });
  }, []);

  // Space assignment picker for global/stack view (when clicking empty cell)
  const [spaceAssignPicker, setSpaceAssignPicker] = useState(null);
  // { member, requirement, spaces: [...], confirm: null | spaceOption }
  const [spaceAssignLoading, setSpaceAssignLoading] = useState(false);

  // Initialize viewMode from navigation state if provided (prevents AnimatePresence race condition)
  const [viewMode, setViewMode] = useState(() => {
    const navViewMode = location.state?.viewMode;
    return navViewMode || 'grid';
  }); // grid, qualifications, or employees
  const [rowsSearchTerm, setRowsSearchTerm] = useState('');
  const [qualificationsSortBy, setQualificationsSortBy] = useState('name-asc'); // Sort option for qualifications
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState(''); // Search for filtering employees and qualifications
  // Initialize expandedAccordionId from navigation state if provided
  const [expandedAccordionId, setExpandedAccordionId] = useState(() => {
    const qualificationId = location.state?.qualificationId;
    const memberId = location.state?.memberId;
    if (qualificationId) return `item-${qualificationId}`;
    if (memberId) return `item-${memberId}`;
    return null;
  }); // For controlling which accordion is open in qualifications/employees view
  const [selectedMembersToAdd, setSelectedMembersToAdd] = useState([]);
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [nameColumnExpanded, setNameColumnExpanded] = useState(true);
  const [requirements, setRequirements] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [siteRequirements, setSiteRequirements] = useState([]); // Site compliance requirements
  const [siteRequirementsLoading, setSiteRequirementsLoading] = useState(false);
  const [matrixName, setMatrixName] = useState('My Matrix');
  const [dataLoading, setDataLoading] = useState(true);
  const [uploadFile, setUploadFile] = useState(null);
  const [bookingNotes, setBookingNotes] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [certificateNumber, setCertificateNumber] = useState('');
  const fileInputRef = useRef(null);
  const [coveragePerspective, setCoveragePerspective] = useState('requirement'); // 'requirement' or 'employee'
  // New booking form states
  const [preferredDate, setPreferredDate] = useState('');
  const [datePreference, setDatePreference] = useState('specific'); // 'specific' or 'month'
  const [preferredMonth, setPreferredMonth] = useState('');
  const [delegatesCount, setDelegatesCount] = useState(1);
  const [trainingLocation, setTrainingLocation] = useState('');
  // Add member dialog state (data only - open/close handled by useMatrixModals)
  const [availableMembers, setAvailableMembers] = useState([]);
  // Add qualification dialog state (data only - open/close handled by useMatrixModals)
  const [availableQualifications, setAvailableQualifications] = useState([]);
  // Row highlighting state
  const [highlightedRows, setHighlightedRows] = useState(new Set());
  // Column highlighting state
  const [highlightedColumns, setHighlightedColumns] = useState(new Set());

  // GAP analysis popup states
  const [openEmployeeGapId, setOpenEmployeeGapId] = useState(null);
  const [openQualificationGapId, setOpenQualificationGapId] = useState(null);

  // Modal states (open/close handled by useMatrixModals, data states kept here)
  const [existingQuotes, setExistingQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);

  // AI validation states for manual upload
  const [validatingUpload, setValidatingUpload] = useState(false);

  // Track if we're currently dragging
  const isDraggingRef = useRef(false);

  // File drag-and-drop state
  const [dragOverCell, setDragOverCell] = useState(null);

  // Views UI state (views data handled by useMatrixViews hook)
  const [viewContextMenu, setViewContextMenu] = useState(null); // { viewId, x, y }
  const [activeTabId, setActiveTabId] = useState(null); // For drag and drop
  const [editingTabId, setEditingTabId] = useState(null); // For inline editing tab names
  const [hideFieldsTab, setHideFieldsTab] = useState('qualifications'); // 'qualifications' or 'members'
  const [hideFieldsSearch, setHideFieldsSearch] = useState(''); // Search term for hide fields dropdown
  const [activeFieldDragId, setActiveFieldDragId] = useState(null); // For field list drag and drop
  const [showTabScrollLeft, setShowTabScrollLeft] = useState(false);
  const [showTabScrollRight, setShowTabScrollRight] = useState(false);
  const tabScrollRef = useRef(null);
  const [droppedFile, setDroppedFile] = useState(null);
  const [uploadModalInitialOverride, setUploadModalInitialOverride] = useState(false);
  // savingStatus is now provided by useMatrixViews hook
  const [smartUploadProcessing, setSmartUploadProcessing] = useState(false);
  const [editQualDialogOpen, setEditQualDialogOpen] = useState(false);
  const [editQualSelected, setEditQualSelected] = useState(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const dragTimeoutRef = useRef(null);

  // Bulk upload state (open/close handled by useMatrixModals, processing states kept here)
  const [bulkUploadProcessing, setBulkUploadProcessing] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState({
    current: 0,
    total: 0
  });
  const [bulkUploadSaving, setBulkUploadSaving] = useState(false);
  const dragOverCellRef = useRef(null); // Use ref to avoid re-render issues
  const currentDragOverlayRef = useRef(null); // Track current overlay element
  const dropBannerRef = useRef(null); // Ref for the banner DOM element (portal)
  const smartMatchFallbackRef = useRef(null); // Timeout for fallback smart match
  const handleSmartUploadRef = useRef(null); // Ref to avoid stale closure
  const handleBulkSmartUploadRef = useRef(null); // Ref to avoid stale closure
  const uploadModalOpenRef = useRef(false); // Track modal state for drag handler

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8 // Require 8px movement before activating drag
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  // Sync local state from useMatrixData hook (only when data actually changes)
  // Use refs to track previous values and prevent unnecessary re-syncs
  const prevDataRef = useRef({
    requirements: null,
    teamMembers: null,
    assignments: null,
    qualifications: null
  });

  useEffect(() => {
    // Sync if we have new data and it's a different reference from what we last synced
    // Reference equality check prevents infinite loops; hook returns stable EMPTY_ARRAY when no data
    if (
      hookRequirements &&
      hookRequirements !== prevDataRef.current.requirements
    ) {
      prevDataRef.current.requirements = hookRequirements;
      setRequirements(hookRequirements);
    }
  }, [hookRequirements]);

  useEffect(() => {
    if (
      hookTeamMembers &&
      hookTeamMembers !== prevDataRef.current.teamMembers
    ) {
      prevDataRef.current.teamMembers = hookTeamMembers;
      setTeamMembers(hookTeamMembers);
    }
  }, [hookTeamMembers]);

  useEffect(() => {
    const assignments = hookMatrixData?.assignments;
    if (assignments && assignments !== prevDataRef.current.assignments) {
      prevDataRef.current.assignments = assignments;
      setAssignments(assignments);
    }
  }, [hookMatrixData?.assignments]);

  useEffect(() => {
    if (
      hookQualifications &&
      hookQualifications !== prevDataRef.current.qualifications
    ) {
      prevDataRef.current.qualifications = hookQualifications;
      setQualifications(hookQualifications);
    }
  }, [hookQualifications]);

  // Sync loading states from hook
  useEffect(() => {
    if (!matrixLoading) {
      setLoading(false);
      setDataLoading(false);
    }
  }, [matrixLoading]);

  // Removed handleDragOver to improve performance - we only update position on drop

  // Handle drag start for column reordering
  const handleDragStart = event => {
    // Prevent matrix dragging when hide fields dropdown is open
    if (hideFieldsOpen) {
      return;
    }
    isDraggingRef.current = true;
  };

  // Handle drag end for column reordering (in-grid column header drag).
  // Uses the same columnOrder state as the hide-fields dropdown so the two
  // stay in sync, and persists automatically through the active view.
  const handleDragEnd = event => {
    isDraggingRef.current = false;

    // Prevent matrix dragging when hide fields dropdown is open
    if (hideFieldsOpen) {
      return;
    }

    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = fullColumnOrder.findIndex(id => id === active.id);
    const newIndex = fullColumnOrder.findIndex(id => id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    setColumnOrder(arrayMove(fullColumnOrder, oldIndex, newIndex));
  };

  // Handle tab drag end (reordering views) - uses hook's reorderViews
  const handleTabDragEnd = async event => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setActiveTabId(null);
      return;
    }

    const activeViewItem = views.find(v => v.id === active.id);
    const overViewItem = views.find(v => v.id === over.id);

    // Prevent dragging "All" tab or dragging over "All" tab
    if (activeViewItem?.isDefault || overViewItem?.isDefault) {
      setActiveTabId(null);
      return;
    }

    const oldIndex = views.findIndex(v => v.id === active.id);
    const newIndex = views.findIndex(v => v.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      setActiveTabId(null);
      return;
    }

    // Reorder using hook - it handles both local state and database
    const newViews = arrayMove(views, oldIndex, newIndex);
    const orderedIds = newViews.filter(v => !v.isDefault).map(v => v.id);
    reorderViews(orderedIds);
    setActiveTabId(null);
  };

  // Handle renaming a view tab - uses hook's renameView
  const handleRenameView = async (viewId, newName) => {
    await renameView(viewId, newName);
  };

  // autoSaveView is now handled by useMatrixViews hook (autoSaveFilters)

  // Helper function to check if a cell can accept file drops
  const canCellAcceptDrop = cell => {
    if (!cell) return false;
    // Allow upload on ANY cell - even unassigned (will auto-assign on upload)
    return true;
  };

  // Create drag-and-drop props for a matrix cell
  const getCellDragProps = (member, requirement, status, assignment) => {
    const cell = { member, requirement, status, assignment };

    if (!canCellAcceptDrop(cell)) {
      return {}; // No drag props for non-uploadable cells
    }

    return {
      onDragEnter: e => {
        e.preventDefault();
        // DO NOT stopPropagation - let global handlers maintain top zone visibility
        const cellElement = e.currentTarget;

        // Remove previous overlay if exists
        if (
          currentDragOverlayRef.current &&
          currentDragOverlayRef.current !== cellElement
        ) {
          const oldOverlay =
            currentDragOverlayRef.current.querySelector('.drag-overlay');
          if (oldOverlay) oldOverlay.remove();
        }

        // Create and add overlay directly to DOM
        let overlay = cellElement.querySelector('.drag-overlay');
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.className = 'drag-overlay';
          overlay.style.cssText = `
            position: absolute;
            inset: 0;
            background: linear-gradient(135deg, rgba(59, 130, 246, 0.95) 0%, rgba(37, 99, 235, 0.95) 100%);
            z-index: 9999;
            pointer-events: none;
            border-radius: 12px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 12px;
            animation: overlayFadeIn 0.2s ease-out forwards, pulseGlow 2s ease-in-out infinite;
            box-shadow: inset 0 0 0 3px rgba(255, 255, 255, 0.5), 0 8px 32px rgba(59, 130, 246, 0.4);
          `;

          // Upload icon
          const uploadIcon = document.createElement('div');
          uploadIcon.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(0 1px 4px rgba(0,0,0,0.3)); animation: uploadBounce 0.6s ease-in-out infinite;">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
          `;
          uploadIcon.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: center;
          `;

          // Text container
          const textContainer = document.createElement('div');
          textContainer.style.cssText = `
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1px;
          `;

          const mainText = document.createElement('div');
          mainText.style.cssText = `
            color: white;
            font-size: 11px;
            font-weight: 700;
            text-shadow: 0 1px 4px rgba(0,0,0,0.3);
            letter-spacing: 0.2px;
          `;
          mainText.textContent = 'Drop Here';

          const subText = document.createElement('div');
          subText.style.cssText = `
            color: rgba(255, 255, 255, 0.95);
            font-size: 8px;
            font-weight: 600;
            text-shadow: 0 1px 2px rgba(0,0,0,0.25);
            text-transform: uppercase;
            letter-spacing: 0.5px;
          `;
          subText.textContent = 'AI Auto-Extract';

          textContainer.appendChild(mainText);
          textContainer.appendChild(subText);

          overlay.appendChild(uploadIcon);
          overlay.appendChild(textContainer);

          cellElement.appendChild(overlay);
          currentDragOverlayRef.current = cellElement;
        }

        dragOverCellRef.current = cell;
      },
      onDragOver: e => {
        e.preventDefault();
        // DO NOT stopPropagation - let global handlers maintain top zone visibility
        e.dataTransfer.dropEffect = 'move';
      },
      onDragLeave: e => {
        e.preventDefault();
        // DO NOT stopPropagation - let global handlers maintain top zone visibility

        // Only remove if actually leaving the cell
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;

        if (
          x < rect.left ||
          x >= rect.right ||
          y < rect.top ||
          y >= rect.bottom
        ) {
          const overlay = e.currentTarget.querySelector('.drag-overlay');
          if (overlay) {
            overlay.remove();
          }
          dragOverCellRef.current = null;
          currentDragOverlayRef.current = null;
        }
      },
      onDrop: e => {
        e.preventDefault();
        e.stopPropagation(); // Stop propagation ONLY on drop to prevent global handler

        // Cancel the global smart match fallback - this cell is handling it
        if (smartMatchFallbackRef.current) {
          clearTimeout(smartMatchFallbackRef.current);
          smartMatchFallbackRef.current = null;
        }

        // Remove overlay
        const overlay = e.currentTarget.querySelector('.drag-overlay');
        if (overlay) overlay.remove();

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
          const file = files[0];

          // Check if it's a valid file type
          const validTypes = [
            'application/pdf',
            'image/jpeg',
            'image/jpg',
            'image/png'
          ];
          if (!validTypes.includes(file.type)) {
            toast.error('Please upload a PDF, JPG, or PNG file');
            // Reset drag states including banner visibility
            setIsDraggingFiles(false);
            dragOverCellRef.current = null;
            currentDragOverlayRef.current = null;
            return;
          }

          // Set the cell and file, then open upload modal
          setSelectedCell(cell);
          setDroppedFile(file);
          setUploadModalOpen(true);

          // Reset ALL drag states including the banner visibility
          setIsDraggingFiles(false);
          dragOverCellRef.current = null;
          currentDragOverlayRef.current = null;
        }
      }
    };
  };

  // Helper to check if a cell is currently being dragged over
  const isCellDragOver = (member, requirement) => {
    const isOver =
      dragOverCell?.member?.id === member?.id &&
      dragOverCell?.requirement?.id === requirement?.id;
    if (isOver) {
    }
    return isOver;
  };

  // Smart upload - automatically match certificate to correct member and requirement
  const handleSmartUpload = async file => {
    setSmartUploadProcessing(true);
    const loadingToast = toast.loading(
      'AI is analyzing certificate and finding the right person...'
    );

    try {
      // Fetch fresh data from database - handle both single project and stack/global view
      let freshTeamMembers = [];
      let freshRequirements = [];

      if (isStackView && projectIds?.length > 0) {
        // Stack/global view: fetch from all projects and use existing data
        freshTeamMembers = teamMembers || [];
        freshRequirements = requirements || [];
      } else {
        const matrixResult = await supabase.rpc('get_matrix_data_v4', {
          p_project_id: projectId
        });

        if (matrixResult.error) {
          console.error('Error fetching matrix data:', matrixResult.error);
          toast.error('Failed to load project data', { id: loadingToast });
          setSmartUploadProcessing(false);
          return;
        }

        const matrixData = matrixResult.data;
        freshTeamMembers = matrixData.teamMembers || [];
        freshRequirements = matrixData.requirements || [];
      }

      if (freshTeamMembers.length === 0) {
        toast.error('No team members found in this project', {
          id: loadingToast
        });
        setSmartUploadProcessing(false);
        return;
      }

      if (freshRequirements.length === 0) {
        toast.error('No requirements found in this project', {
          id: loadingToast
        });
        setSmartUploadProcessing(false);
        return;
      }
      // Upload file to GCS temp folder
      let uploadResult;
      try {
        uploadResult = await gcsUploadFile(file, {
          folderPath: 'certificates/temp',
          fileName: `${Date.now()}_${file.name}`,
          contentType: file.type
        });
      } catch (uploadError) {
        console.error('Upload error:', uploadError);
        toast.error('Failed to upload file', { id: loadingToast });
        setSmartUploadProcessing(false);
        return;
      }

      // Get signed URL for doc parser
      let signedUrl;
      try {
        signedUrl = await gcsGetSignedReadUrl(uploadResult.filePath);
      } catch {
        toast.error('Failed to process file', { id: loadingToast });
        setSmartUploadProcessing(false);
        return;
      }

      // Call doc parser webhook
      const response = await fetch(
        'https://addai-flows-backend-29522465016.europe-west2.run.app/webhook/e2a909a7-6f57-461d-83b8-b1506cf3a79a/process-certificate',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            documentUrl: signedUrl,
            fileName: file.name,
            projectId: projectId,
            uploadedAt: new Date().toISOString()
          })
        }
      );

      // Parse AI extraction - soft fail if extraction fails (file is already uploaded)
      let extractedData = null;
      if (response.ok) {
        const responseData = await response.json();
        const parsed = parseAiFlowResponse(responseData);
        if (parsed.success && parsed.extractedData) {
          extractedData = parsed.extractedData;
        }
      }

      // Extract scalar fields from AI data
      const expiryDate = extractedData
        ? (Array.isArray(extractedData.expiry_date) ? extractedData.expiry_date[0] : extractedData.expiry_date)
        : null;
      const certNumber = extractedData
        ? (Array.isArray(extractedData.certificate_number) ? extractedData.certificate_number[0] : extractedData.certificate_number)
        : null;

      // Fetch company-wide pool so the dialog's search can find members/requirements
      // outside the current view (other spaces). Run in parallel for speed.
      const companyId = company?.id;
      let allMembersPool = freshTeamMembers;
      let allRequirementsPool = freshRequirements;

      if (companyId) {
        const [membersResp, reqsResp] = await Promise.all([
          supabase
            .from('user_profiles')
            .select('id, first_name, last_name, email, avatar_url, job_title, department')
            .eq('company_id', companyId),
          supabase
            .from('compliance_requirements')
            .select('id, name, course_link_id, course_link_type, project_id, projects!inner(id, name, company_id)')
            .eq('projects.company_id', companyId)
            .eq('show_on_matrix', true)
            .eq('approval_status', 'approved')
        ]);

        // Merge company-wide members with current-view members.
        // Current-view members take precedence (they have richer fields like role/department from space_members).
        if (!membersResp.error && membersResp.data) {
          const inViewIds = new Set(freshTeamMembers.map(m => m.user_id || m.id));
          const extraMembers = membersResp.data
            .filter(p => !inViewIds.has(p.id))
            .map(p => ({
              id: p.id,
              user_id: p.id,
              name: [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email,
              first_name: p.first_name,
              last_name: p.last_name,
              email: p.email,
              role: p.job_title,
              department: p.department,
              avatar_url: p.avatar_url,
              _outOfView: true
            }));
          allMembersPool = [...freshTeamMembers, ...extraMembers];
        }

        // Merge requirements: dedupe by course_link key (or name fallback) and aggregate spaces
        if (!reqsResp.error && reqsResp.data) {
          const seen = new Map();
          // Seed with current-view requirements
          for (const req of freshRequirements) {
            const key = req.course_link_id
              ? `${req.course_link_type}:${req.course_link_id}`
              : `name:${(req.name || '').toLowerCase()}`;
            seen.set(key, { ...req, _inView: true });
          }
          // Add company-wide ones that aren't already in the pool
          for (const req of reqsResp.data) {
            const key = req.course_link_id
              ? `${req.course_link_type}:${req.course_link_id}`
              : `name:${(req.name || '').toLowerCase()}`;
            const existing = seen.get(key);
            const projInfo = req.projects ? { id: req.projects.id, name: req.projects.name } : null;
            if (existing) {
              if (projInfo && !(existing.spaces || []).some(s => s.id === projInfo.id)) {
                existing.spaces = [...(existing.spaces || []), projInfo];
              }
            } else {
              seen.set(key, {
                id: req.id,
                name: req.name,
                course_link_id: req.course_link_id,
                course_link_type: req.course_link_type,
                project_id: req.project_id,
                spaces: projInfo ? [projInfo] : [],
                _outOfView: true
              });
            }
          }
          allRequirementsPool = Array.from(seen.values());
        }
      }

      // Smart-match team member and requirement using scored multi-tier matching
      const memberResult = matchMemberWithCandidates(extractedData || {}, freshTeamMembers);
      const requirementResult = matchRequirementWithCandidates(extractedData || {}, freshRequirements);

      const matchedMember = memberResult.best;
      const matchedRequirement = requirementResult.best;

      if (!matchedMember) {
        console.warn('Could not auto-match team member from extracted data:', extractedData?.first_name, extractedData?.last_name);
      }
      if (!matchedRequirement) {
        console.warn('Could not auto-match qualification from extracted data:', extractedData?.course_title);
      }

      // Determine assignment scope based on view type
      // Single space: one project; Stack/Global: multiple projects (intersect member & requirement presence)
      let assignmentScope = {
        mode: isStackView ? 'stack' : 'single',
        eligibleSpaces: [],   // [{id, name}] — spaces where both member & requirement coexist
        assignedSpaces: [],   // [{id, name}] — eligible spaces that already have an assignment
        unassignedSpaces: []  // [{id, name}] — eligible spaces needing new assignment
      };

      if (matchedMember && matchedRequirement) {
        if (isStackView && projectIds?.length > 0) {
          // Stack/global: figure out which spaces the member is in AND which the requirement is set up in
          const projectNameMap = new Map();
          (stackProjects || []).forEach(p => projectNameMap.set(p.id, p.name));

          // Spaces where requirement exists (from deduped req.spaces)
          const reqSpaceIds = new Set(
            (matchedRequirement.spaces || [{ id: matchedRequirement.project_id }])
              .map(s => s.id)
              .filter(Boolean)
          );

          // Spaces where the member exists
          const { data: memberSpaces } = await supabase
            .from('space_members')
            .select('project_id')
            .eq('user_id', matchedMember.user_id)
            .in('project_id', projectIds);
          const memberSpaceIds = new Set((memberSpaces || []).map(sm => sm.project_id));

          // Intersection = eligible spaces
          const eligibleIds = projectIds.filter(pid => reqSpaceIds.has(pid) && memberSpaceIds.has(pid));

          // Find existing assignments. The requirement.id is the canonical one in stack view;
          // we need to query by all mapped requirement IDs (one per space). Use course_link fields
          // as a robust fallback — but primary is requirement_id across eligible spaces.
          const { data: existingAssignments } = await supabase
            .from('compliance_assignments')
            .select('project_id, assigned, requirement_id')
            .eq('team_member_id', matchedMember.user_id)
            .in('project_id', eligibleIds.length > 0 ? eligibleIds : projectIds);

          const assignedIds = new Set(
            (existingAssignments || [])
              .filter(a => a.assigned === true)
              .map(a => a.project_id)
          );

          const toSpace = id => ({ id, name: projectNameMap.get(id) || 'Unknown space' });
          assignmentScope.eligibleSpaces = eligibleIds.map(toSpace);
          assignmentScope.assignedSpaces = eligibleIds.filter(id => assignedIds.has(id)).map(toSpace);
          assignmentScope.unassignedSpaces = eligibleIds.filter(id => !assignedIds.has(id)).map(toSpace);
        } else {
          // Single space
          const { data: existingAssignment } = await supabase
            .from('compliance_assignments')
            .select('id, assigned')
            .eq('project_id', projectId)
            .eq('team_member_id', matchedMember.id)
            .eq('requirement_id', matchedRequirement.id)
            .maybeSingle();

          const spaceName = space?.name || 'this space';
          const spaceEntry = { id: projectId, name: spaceName };
          assignmentScope.eligibleSpaces = [spaceEntry];
          if (existingAssignment?.assigned === true) {
            assignmentScope.assignedSpaces = [spaceEntry];
          } else {
            assignmentScope.unassignedSpaces = [spaceEntry];
          }
        }
      }

      // Show confirmation dialog - user can fix any unmatched fields
      if (!extractedData) {
        toast.info(
          'Could not read document — please select the person and qualification manually',
          { id: loadingToast }
        );
      } else if (memberResult.ambiguous) {
        toast.info('Multiple possible matches — please confirm the person', { id: loadingToast });
      } else if (matchedMember && matchedRequirement) {
        toast.success(
          `Matched: ${matchedMember.name} → ${matchedRequirement.name}`,
          { id: loadingToast }
        );
      } else if (matchedMember) {
        toast.info(
          `Matched person: ${matchedMember.name} — please select the qualification`,
          { id: loadingToast }
        );
      } else if (matchedRequirement) {
        toast.info(
          `Matched qualification: ${matchedRequirement.name} — please select the person`,
          { id: loadingToast }
        );
      } else {
        toast.info(
          'Could not auto-match — please select the person and qualification',
          { id: loadingToast }
        );
      }

      setSmartUploadConfirmation({
        member: matchedMember || null,
        requirement: matchedRequirement || null,
        memberCandidates: memberResult.candidates,
        requirementCandidates: requirementResult.candidates,
        memberAmbiguous: memberResult.ambiguous || false,
        memberPool: allMembersPool,
        requirementPool: allRequirementsPool,
        extractedData: extractedData || null,
        expiryDate: expiryDate,
        certificateNumber: certNumber,
        tempFilePath: uploadResult.filePath,
        fileName: file.name,
        assignmentScope
      });
    } catch (error) {
      console.error('Smart upload error:', error);
      toast.error('Failed to process certificate', { id: loadingToast });
    } finally {
      setSmartUploadProcessing(false);
    }
  };

  // Confirm and save the smart upload - receives edited values from dialog
  const handleConfirmSmartUpload = async editedValues => {
    if (!smartUploadConfirmation) return;

    const { tempFilePath, fileName } = smartUploadConfirmation;
    // Use edited values from dialog (user may have changed member/requirement)
    const { expiryDate, certificateNumber, member: editedMember, requirement: editedRequirement } = editedValues || {};
    const member = editedMember || smartUploadConfirmation.member;
    const requirement = editedRequirement || smartUploadConfirmation.requirement;
    const savingToast = toast.loading('Saving certificate...');

    try {
      // Use the temp file path directly - it's already uploaded
      // The temp path is fine to use as the evidence URL
      const finalPath = tempFilePath;

      // Get the team member's user_id
      if (!member?.user_id) {
        throw new Error(
          'Cannot save qualification: team member not linked to user profile'
        );
      }

      // Determine which spaces to assign this cert to
      // In single-space mode, that's just the current project.
      // In stack/global mode, it's all eligible spaces (where both member & requirement coexist).
      const scope = smartUploadConfirmation.assignmentScope;
      const targetSpaces =
        scope?.mode === 'stack'
          ? (scope.eligibleSpaces || [])
          : [{ id: projectId }];

      if (targetSpaces.length === 0) {
        throw new Error(
          'No eligible space found — member is not in any space that has this requirement set up'
        );
      }

      // For each target space, ensure the requirement exists there and create an assignment if missing.
      // In stack view the matched requirement is a canonical one; we need to look up the per-space
      // requirement_id via course_link fields (or matching name as fallback).
      for (const targetSpace of targetSpaces) {
        // Find the requirement ID in this specific space
        let spaceRequirementId = requirement.id;

        if (scope?.mode === 'stack') {
          // Try course_link match first (most reliable), fall back to name
          let spaceReqQuery = supabase
            .from('compliance_requirements')
            .select('id')
            .eq('project_id', targetSpace.id);

          if (requirement.course_link_id && requirement.course_link_type) {
            spaceReqQuery = spaceReqQuery
              .eq('course_link_id', requirement.course_link_id)
              .eq('course_link_type', requirement.course_link_type);
          } else {
            spaceReqQuery = spaceReqQuery.eq('name', requirement.name);
          }

          const { data: spaceReq } = await spaceReqQuery.maybeSingle();
          if (spaceReq?.id) {
            spaceRequirementId = spaceReq.id;
          } else {
            // Requirement not in this space — shouldn't happen because we filtered eligibleSpaces,
            // but skip defensively
            console.warn(`Requirement not found in space ${targetSpace.id}, skipping`);
            continue;
          }
        }

        // Check for existing assignment in this space
        const { data: existingAssignment } = await supabase
          .from('compliance_assignments')
          .select('id, assigned')
          .eq('project_id', targetSpace.id)
          .eq('team_member_id', member.user_id)
          .eq('requirement_id', spaceRequirementId)
          .maybeSingle();

        if (!existingAssignment) {
          const { error: assignError } = await supabase
            .from('compliance_assignments')
            .insert({
              project_id: targetSpace.id,
              team_member_id: member.user_id,
              requirement_id: spaceRequirementId,
              assigned: true
            });
          if (assignError) {
            console.error(`Error creating assignment in space ${targetSpace.id}:`, assignError);
            throw new Error(`Failed to create assignment in ${targetSpace.name || 'space'}`);
          }
        } else if (existingAssignment.assigned !== true) {
          // Reactivate
          await supabase
            .from('compliance_assignments')
            .update({ assigned: true })
            .eq('id', existingAssignment.id);
        }
      }

      // Try to use userQualificationsService to sync across all spaces
      // If it fails (e.g., due to missing course_links), that's OK - the assignment is already created
      const result = await userQualificationsService.upsertUserQualification({
        userId: member.user_id,
        requirementId: requirement.id,
        status: 'completed',
        completedDate: new Date().toISOString(),
        expiryDate: expiryDate,
        evidenceUrl: finalPath,
        certificateNumber: certificateNumber || null,
        notes: null,
        assignedBy: userProfile?.id || member.user_id
      });

      const spaceCount = targetSpaces.length;
      const toastMsg = scope?.mode === 'stack' && spaceCount > 1
        ? `Certificate saved and applied to ${spaceCount} spaces`
        : result.success
          ? 'Certificate uploaded and synced across all spaces'
          : 'Certificate uploaded successfully';
      toast.success(toastMsg, { id: savingToast });
      await fetchData();
      setSmartUploadConfirmation(null);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save certificate', { id: savingToast });
    }
  };

  // Decline the smart upload and clean up temp file
  const handleDeclineSmartUpload = async () => {
    if (!smartUploadConfirmation) return;

    try {
      // Delete temp file from GCS
      await gcsDeleteFile(smartUploadConfirmation.tempFilePath);

      toast.info('Certificate upload cancelled');
      setSmartUploadConfirmation(null);
    } catch (error) {
      console.error('Cleanup error:', error);
      setSmartUploadConfirmation(null);
    }
  };

  // Bulk smart upload - processes multiple files sequentially, shows confirmation before saving
  const handleBulkSmartUpload = async files => {
    setBulkUploadConfirmation(null);

    try {
      // Fetch fresh data once
      let freshTeamMembers = [];
      let freshRequirements = [];

      if (isStackView && projectIds?.length > 0) {
        freshTeamMembers = teamMembers || [];
        freshRequirements = requirements || [];
      } else {
        const matrixResult = await supabase.rpc('get_matrix_data_v4', {
          p_project_id: projectId
        });
        if (matrixResult.error) {
          toast.error('Failed to load project data');
          return;
        }
        const matrixData = matrixResult.data;
        freshTeamMembers = matrixData.teamMembers || [];
        freshRequirements = matrixData.requirements || [];
      }

      if (freshTeamMembers.length === 0 || freshRequirements.length === 0) {
        toast.error('No team members or requirements found');
        return;
      }

      // Open dialog immediately with raw files — dialog handles upload + AI parsing per row
      setBulkUploadConfirmation({
        files: Array.from(files),
        teamMembers: freshTeamMembers,
        requirements: freshRequirements,
        projectId: projectId,
      });
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast.error('Bulk upload encountered an error');
    }
  };

  // Confirm and save all bulk uploads
  // `entriesToSave` is passed from the dialog with user-overridden member/requirement selections
  const handleConfirmBulkUpload = async (entriesToSave) => {
    if (!entriesToSave?.length) return;

    setBulkUploadSaving(true);
    const results = { successes: [], failures: [] };

    try {
      for (const entry of entriesToSave) {
        if (!entry.memberId || !entry.requirementId || !entry.tempFilePath) {
          continue; // skip removed/incomplete entries
        }

        try {
          // Check/create assignment
          const { data: existingAssignment } = await supabase
            .from('compliance_assignments')
            .select('id, assigned')
            .eq('project_id', projectId)
            .eq('team_member_id', entry.memberId)
            .eq('requirement_id', entry.requirementId)
            .single();

          if (!existingAssignment) {
            const { error: insertError } = await supabase
              .from('compliance_assignments')
              .insert({
                project_id: projectId,
                team_member_id: entry.memberId,
                requirement_id: entry.requirementId,
                assigned: true
              });
            if (insertError) throw new Error('Failed to create assignment');
          } else if (!existingAssignment.assigned) {
            const { error: updateError } = await supabase
              .from('compliance_assignments')
              .update({ assigned: true })
              .eq('id', existingAssignment.id);
            if (updateError) throw new Error('Failed to update assignment');
          }

          // Use evidence_files from dialog (includes grouped supporting files)
          const evidenceFiles = (entry.evidenceFiles || [{
            url: entry.tempFilePath,
            file_name: entry.fileName,
            file_type: 'unknown',
            uploaded_at: new Date().toISOString(),
            uploaded_by: userProfile?.id || null,
            is_primary: true,
            doc_type: 'certificate'
          }]).map(f => ({ ...f, uploaded_by: f.uploaded_by || userProfile?.id || null }));

          // Sync to user_qualifications
          const result = await userQualificationsService.upsertUserQualification({
            userId: entry.memberId,
            requirementId: entry.requirementId,
            status: 'completed',
            completedDate: new Date().toISOString(),
            expiryDate: entry.expiryDate,
            evidenceUrl: entry.tempFilePath,
            certificateNumber: entry.certificateNumber || null,
            notes: null,
            assignedBy: userProfile?.id || entry.memberId,
            evidenceFiles
          });

          if (!result.success) throw new Error(result.error);

          results.successes.push({
            fileName: entry.fileName,
            memberName: entry.memberName,
            requirementName: entry.requirementName,
            expiryDate: entry.expiryDate
          });
        } catch (saveError) {
          console.error('Save error for', entry.fileName, saveError);
          results.failures.push({ fileName: entry.fileName, reason: 'Failed to save' });
          if (entry.tempFilePath) {
            gcsDeleteFile(entry.tempFilePath).catch(() => {});
          }
        }
      }

      await fetchData();
      setBulkUploadConfirmation(null);
      broadcastUpdate({ action: 'bulk_certificate_uploaded' });

      // Show toast summary
      const s = results.successes.length;
      const f = results.failures.length;
      if (s > 0 && f === 0) {
        toast.success(`${s} certificate${s !== 1 ? 's' : ''} saved successfully`);
      } else if (s > 0 && f > 0) {
        toast.warning(`${s} saved, ${f} failed`);
      } else {
        toast.error('Failed to save certificates');
      }
    } catch (error) {
      console.error('Bulk save error:', error);
      toast.error('Failed to save certificates');
    } finally {
      setBulkUploadSaving(false);
    }
  };

  // Cancel bulk upload and clean up temp files
  const handleCancelBulkUpload = async () => {
    const entries = bulkUploadConfirmation?.entries || [];
    for (const entry of entries) {
      if (entry.tempFilePath) {
        gcsDeleteFile(entry.tempFilePath).catch(e => console.error('Cleanup error:', e));
      }
    }
    toast.info('Bulk upload cancelled');
    setBulkUploadConfirmation(null);
  };

  /*
   * DRAG-AND-DROP USAGE INSTRUCTIONS:
   *
   * To enable file drag-and-drop on matrix cells, spread the getCellDragProps
   * function result onto your cell element:
   *
   * Example:
   * <div
   *   {...getCellDragProps(member, requirement, status, assignment)}
   *   className={`cell ${isCellDragOver(member, requirement) ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
   * >
   *   {/* Cell content *\/}
   * </div>
   *
   * The drag-over styling (dotted outline) can be applied using the isCellDragOver helper.
   * Only cells with status: 'missing', 'expired', 'soon', or 'valid' will accept drops.
   * When a file is dropped, it will automatically:
   * 1. Validate the file type (PDF, JPG, PNG only)
   * 2. Open the upload modal with that cell selected
   * 3. Start processing the file with AI extraction
   */

  // Handle navigation state from qualifications/team page - scroll to item after data loads
  useEffect(() => {
    const qualificationId = location.state?.qualificationId;
    const memberId = location.state?.memberId;
    const targetId = qualificationId || memberId;
    const dataAttr = qualificationId
      ? 'data-qualification-id'
      : 'data-member-id';

    if (targetId) {
      // Scroll to the expanded item after data loads and accordion animation completes
      setTimeout(() => {
        const element = document.querySelector(`[${dataAttr}="${targetId}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // If not found immediately, try again after another delay (data might still be loading)
          setTimeout(() => {
            const retryElement = document.querySelector(
              `[${dataAttr}="${targetId}"]`
            );
            if (retryElement) {
              retryElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
          }, 500);
        }
      }, 600);

      // Clear the location state after processing to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Keep smart upload refs current so capture-phase handlers avoid stale closures
  handleSmartUploadRef.current = handleSmartUpload;
  handleBulkSmartUploadRef.current = handleBulkSmartUpload;
  uploadModalOpenRef.current = uploadModalOpen;

  // Global file drag detection
  // Robust approach: Use dragover continuous events to maintain visibility
  // The drop zone stays visible while ANY drag activity is detected
  useEffect(() => {
    let hideTimeout = null;
    let isShowing = false;

    const showDropZone = () => {
      // Clear any pending hide
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
      if (!isShowing) {
        isShowing = true;
        setIsDraggingFiles(true);
      }
    };

    const scheduleHide = (delay = 100) => {
      // Schedule hide after a delay - will be cancelled if more drag events come
      if (hideTimeout) clearTimeout(hideTimeout);
      hideTimeout = setTimeout(() => {
        isShowing = false;
        setIsDraggingFiles(false);
        hideTimeout = null;
      }, delay);
    };

    const handleDragEnter = e => {
      if (e.dataTransfer?.types?.includes('Files')) {
        e.preventDefault();
        // Don't show page-level drop zone when upload modal is open
        if (!uploadModalOpenRef.current) showDropZone();
      }
    };

    const handleDragOver = e => {
      if (e.dataTransfer?.types?.includes('Files')) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        // Don't show page-level drop zone when upload modal is open
        if (!uploadModalOpenRef.current) showDropZone();
      }
    };

    const handleDragLeave = e => {
      // Only schedule hide when leaving the document entirely
      // (relatedTarget is null when leaving the browser window)
      if (
        e.relatedTarget === null &&
        e.dataTransfer?.types?.includes('Files')
      ) {
        scheduleHide(100);
      }
    };

    // Helper to validate files and run smart match via refs (avoids stale closures)
    const processSmartMatch = async files => {
      const validTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ];
      const validFiles = files.filter(f => validTypes.includes(f.type));
      const invalidFiles = files.filter(f => !validTypes.includes(f.type));

      if (invalidFiles.length > 0) {
        toast.error(
          `${invalidFiles.length} file(s) skipped - only PDF, JPG, PNG allowed`
        );
      }
      if (validFiles.length === 0) return;

      if (validFiles.length === 1) {
        await handleSmartUploadRef.current?.(validFiles[0]);
      } else {
        await handleBulkSmartUploadRef.current?.(validFiles);
      }
    };

    const handleDrop = e => {
      // Always prevent default to stop browser opening the file
      e.preventDefault();

      // Reset all drag state
      if (hideTimeout) clearTimeout(hideTimeout);
      hideTimeout = null;
      isShowing = false;
      setIsDraggingFiles(false);
      setDragOverCell(null);
      document.querySelectorAll('.drag-overlay').forEach(el => el.remove());

      if (!e.dataTransfer?.types?.includes('Files')) return;

      // If the upload modal is open, don't intercept - let the modal handle the drop
      if (uploadModalOpenRef.current) return;

      // Check if drop landed on the banner (React portal events don't work on body portals)
      const banner = dropBannerRef.current;
      if (banner && banner.contains(e.target)) {
        // Banner drop — handle smart match immediately
        e.stopPropagation();
        const files = Array.from(e.dataTransfer.files || []);
        if (files.length > 0) processSmartMatch(files);
        return;
      }

      // For all other drops, schedule a fallback smart match.
      // Cell onDrop handlers fire after capture phase and will cancel this if they handle it.
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length > 0) {
        if (smartMatchFallbackRef.current)
          clearTimeout(smartMatchFallbackRef.current);
        smartMatchFallbackRef.current = setTimeout(() => {
          smartMatchFallbackRef.current = null;
          processSmartMatch(files);
        }, 50);
      }
    };

    const handleDragEnd = e => {
      if (hideTimeout) clearTimeout(hideTimeout);
      hideTimeout = null;
      isShowing = false;
      setIsDraggingFiles(false);
      setDragOverCell(null);
      dragOverCellRef.current = null;
      currentDragOverlayRef.current = null;
      document.querySelectorAll('.drag-overlay').forEach(el => el.remove());
    };

    // All on capture phase so we intercept before any element can let the browser open files
    window.addEventListener('dragenter', handleDragEnter, true);
    window.addEventListener('dragover', handleDragOver, true);
    window.addEventListener('dragleave', handleDragLeave, true);
    window.addEventListener('drop', handleDrop, true);
    window.addEventListener('dragend', handleDragEnd, true);

    return () => {
      if (hideTimeout) clearTimeout(hideTimeout);
      if (smartMatchFallbackRef.current)
        clearTimeout(smartMatchFallbackRef.current);
      window.removeEventListener('dragenter', handleDragEnter, true);
      window.removeEventListener('dragover', handleDragOver, true);
      window.removeEventListener('dragleave', handleDragLeave, true);
      window.removeEventListener('drop', handleDrop, true);
      window.removeEventListener('dragend', handleDragEnd, true);
    };
  }, []);

  // Fetch existing quotes for the selected requirement
  useEffect(() => {
    const fetchExistingQuotes = async () => {
      if (!selectedCell?.requirement?.name || !projectId) {
        setLoadingQuotes(false);
        return;
      }

      setLoadingQuotes(true);
      try {
        // Fetch quotes for this specific course/requirement
        const { data, error } = await supabase
          .from('training_quotes')
          .select('*')
          .eq('project_id', projectId)
          .ilike('course_name', `%${selectedCell.requirement.name}%`)
          .order('created_date', { ascending: false })
          .limit(5);

        if (!error && data) {
          setExistingQuotes(data);
        } else {
          setExistingQuotes([]);
        }
      } catch (err) {
        console.error('Error fetching quotes:', err);
      } finally {
        setLoadingQuotes(false);
      }
    };

    if (dialogOpen && selectedCell) {
      fetchExistingQuotes();
    } else {
      // Clear quotes when dialog closes
      setExistingQuotes([]);
      setLoadingQuotes(false);
    }
  }, [dialogOpen, selectedCell?.requirement?.name, projectId]);

  // Alias for backward compatibility - useMatrixData hook handles fetching and broadcast
  const fetchData = refetchMatrixData;

  // Get broadcastUpdate from the existing hook for manual broadcasts after mutations
  const { broadcastUpdate } = useMatrixBroadcast(projectId, refetchMatrixData);

  // Update project state and matrix name when space data loads
  useEffect(() => {
    if (isStackView) {
      // Stack view: Set matrix name to stack name
      setProject({ name: stackName, id: stackId });
      setMatrixName(stackName || 'Stack Matrix');
    } else if (space) {
      setProject(space);
      // Load matrix name from database or default to project name
      const loadedMatrixName = space.matrix_name || space.name || 'My Matrix';
      setMatrixName(loadedMatrixName);
    }
  }, [space, isStackView, stackName, stackId]);

  // Load site compliance requirements when projectId changes
  useEffect(() => {
    const loadSiteRequirements = async () => {
      if (!projectId || isStackView) return;

      setSiteRequirementsLoading(true);
      try {
        const result = await siteComplianceService.loadProjectSiteCompliance(
          projectId,
          { approvedOnly: true }
        );
        if (result.success) {
          setSiteRequirements(result.requirements || []);
        } else {
          console.error('Failed to load site requirements:', result.error);
          setSiteRequirements([]);
        }
      } catch (error) {
        console.error('Error loading site requirements:', error);
        setSiteRequirements([]);
      } finally {
        setSiteRequirementsLoading(false);
      }
    };

    loadSiteRequirements();
  }, [projectId, isStackView]);

  // Load saved views is now handled by useMatrixViews hook

  // Apply view filters when switching between views
  // Only trigger on activeViewId change to prevent infinite loops
  const prevActiveViewIdRef = useRef(activeViewId);
  const skipAutoSaveRef = useRef(false);

  useEffect(() => {
    // Only run when activeViewId actually changes
    if (prevActiveViewIdRef.current === activeViewId) return;
    prevActiveViewIdRef.current = activeViewId;

    // Skip the next auto-save since we're applying saved filters
    skipAutoSaveRef.current = true;

    if (activeViewId === 'all') {
      // "All" view always shows everything with no filters
      resetToDefaults();
    } else if (activeView?.filters) {
      // Apply saved filters from the view
      applyViewFilters(activeView.filters);
    } else {
      // View has no filters saved yet - clear all filters
      resetToDefaults();
    }
  }, [activeViewId, activeView?.filters, applyViewFilters, resetToDefaults]);

  // Auto-save view when filters change on custom views (handled by hook)
  // Use refs to avoid triggering on every function reference change
  const autoSaveFiltersRef = useRef(autoSaveFilters);
  autoSaveFiltersRef.current = autoSaveFilters;

  useEffect(() => {
    // Skip if we just applied view filters (prevents save loop)
    if (skipAutoSaveRef.current) {
      skipAutoSaveRef.current = false;
      return;
    }

    // Skip if on "All" view or not the owner
    if (activeViewId === 'all' || !isViewOwner) return;

    // Use the hook's getCurrentFilters to get current state
    const currentFilters = getCurrentFilters();

    // Check if filters have changed from saved state
    if (hasChangesFrom(activeView?.filters)) {
      // Auto-save using hook (includes debouncing)
      autoSaveFiltersRef.current(activeViewId, currentFilters);
    }
  }, [
    activeViewId,
    activeFilters,
    hiddenColumns,
    hiddenRows,
    columnOrder,
    rowOrder,
    isViewOwner,
    activeView?.filters,
    getCurrentFilters,
    hasChangesFrom
  ]);

  // Refresh data when page becomes visible (e.g., after navigating away to Team page)
  // DISABLED: This was causing data to reload with different logic than initial load
  // useEffect(() => {
  //   const handleVisibilityChange = () => {
  //     if (!document.hidden && projectId) {
  //       console.log('Page became visible, refreshing assignments...');
  //       reloadAssignments();
  //     }
  //   };

  //   document.addEventListener('visibilitychange', handleVisibilityChange);

  //   return () => {
  //     document.removeEventListener('visibilitychange', handleVisibilityChange);
  //   };
  // }, [projectId, teamMembers]);

  // Handle removing member from matrix
  const handleRemoveFromMatrix = async memberId => {
    try {
      const { error } = await supabase
        .from('space_members')
        .update({ show_on_matrix: false })
        .eq('user_id', memberId)
        .eq('project_id', projectId);

      if (error) throw error;

      toast.success('Member removed from matrix');

      // Refresh data
      const { data: members, error: membersError } = await supabase
        .from('space_members')
        .select(
          `
          *,
          user_profiles!user_id (
            id,
            first_name,
            last_name,
            email,
            avatar_url,
            job_title
          )
        `
        )
        .eq('project_id', projectId)
        .eq('show_on_matrix', true);

      if (!membersError) {
        // Transform to match expected structure
        const formattedMembers = (members || []).map(sm => ({
          id: sm.user_id,
          name:
            `${sm.user_profiles.first_name || ''} ${sm.user_profiles.last_name || ''}`.trim() ||
            sm.user_profiles.email,
          email: sm.user_profiles.email,
          role: sm.user_profiles.job_title,
          department: sm.department,
          status: sm.status,
          avatar_url: sm.user_profiles.avatar_url,
          compliance_percentage: sm.compliance_percentage,
          last_active: sm.last_active,
          sites: sm.sites,
          user_id: sm.user_id,
          show_on_matrix: sm.show_on_matrix
        }));
        setTeamMembers(formattedMembers);
      }
    } catch (error) {
      console.error('Error removing member from matrix:', error);
      toast.error('Failed to remove member from matrix');
    }
  };

  // Handle adding a company member to the space and assigning a qualification
  // For stack view, targetProjectId specifies which space to add to
  const handleAddCompanyMemberToSpace = async (
    member,
    qualification,
    targetProjectId = null
  ) => {
    const effectiveProjectIdForAdd = targetProjectId || projectId;

    if (!effectiveProjectIdForAdd || !company?.id) {
      toast.error('Missing project or company information');
      throw new Error('Missing project or company information');
    }

    try {
      console.log('🌐 Adding company member to space:', {
        userId: member.userId,
        memberName: member.name,
        projectId: effectiveProjectIdForAdd,
        companyId: company.id,
        qualificationId: qualification?.id,
        isStackView
      });

      // Step 1: Add the user to space_members
      // Note: space_members role must be one of: 'Owner', 'Manager', 'Member', 'Viewer'
      // Don't copy company_members role as it uses different values
      const { data: newMember, error: addMemberError } = await supabase
        .from('space_members')
        .insert({
          project_id: effectiveProjectIdForAdd,
          user_id: member.userId,
          role: 'Member', // Default to Member for new space members
          status: 'active',
          show_on_matrix: true,
          created_date: new Date().toISOString()
        })
        .select('id, user_id, role, status')
        .single();

      if (addMemberError) {
        console.error('Error adding member to space:', addMemberError);
        toast.error(`Failed to add ${member.name} to space`);
        throw addMemberError;
      }

      console.log('✅ Added member to space:', newMember);

      // Step 2: Create compliance_assignment to assign the qualification
      if (qualification?.id) {
        const { data: existingAssignment } = await supabase
          .from('compliance_assignments')
          .select('id, assigned')
          .eq('project_id', effectiveProjectIdForAdd)
          .eq('requirement_id', qualification.id)
          .eq('team_member_id', member.userId)
          .maybeSingle();

        if (existingAssignment) {
          // Update existing assignment
          const { error: updateError } = await supabase
            .from('compliance_assignments')
            .update({ assigned: true })
            .eq('id', existingAssignment.id);

          if (updateError) throw updateError;
        } else {
          // Create new assignment
          const { error: insertError } = await supabase
            .from('compliance_assignments')
            .insert({
              project_id: effectiveProjectIdForAdd,
              requirement_id: qualification.id,
              team_member_id: member.userId,
              assigned: true
            });

          if (insertError) throw insertError;
        }
      }

      toast.success(
        `${member.name} added to space and assigned to ${qualification?.name || 'qualification'}`
      );

      // Refresh the matrix data and compliance stats to show the new member
      await fetchData();
      refetchComplianceStats();

      // Broadcast the update
      broadcastUpdate('member_added', {
        userId: member.userId,
        qualificationId: qualification?.id
      });
    } catch (error) {
      console.error('Error adding company member to space:', error);
      toast.error('Failed to add member to space');
      throw error;
    }
  };

  // Handle opening add member dialog
  const handleOpenAddMemberDialog = async () => {
    try {
      // Fetch team members that are NOT on the matrix
      const { data: members, error } = await supabase
        .from('space_members')
        .select(
          `
          *,
          user_profiles!user_id (
            id,
            first_name,
            last_name,
            email,
            avatar_url,
            job_title
          )
        `
        )
        .eq('project_id', projectId)
        .eq('show_on_matrix', false);

      if (error) throw error;

      // Transform to match expected structure
      const formattedMembers = (members || []).map(sm => ({
        id: sm.user_id,
        name:
          `${sm.user_profiles.first_name || ''} ${sm.user_profiles.last_name || ''}`.trim() ||
          sm.user_profiles.email,
        email: sm.user_profiles.email,
        role: sm.user_profiles.job_title,
        department: sm.department,
        status: sm.status,
        avatar_url: sm.user_profiles.avatar_url,
        compliance_percentage: sm.compliance_percentage,
        last_active: sm.last_active,
        sites: sm.sites,
        user_id: sm.user_id,
        show_on_matrix: sm.show_on_matrix
      }));

      setAvailableMembers(formattedMembers);
      setAddMemberDialogOpen(true);
    } catch (error) {
      console.error('Error fetching available members:', error);
      toast.error('Failed to load available members');
    }
  };

  // Handle adding member to matrix
  const handleAddToMatrix = async memberId => {
    try {
      const { error } = await supabase
        .from('space_members')
        .update({ show_on_matrix: true })
        .eq('user_id', memberId)
        .eq('project_id', projectId);

      if (error) throw error;

      toast.success('Member added to matrix');

      // Full refresh to get new row + all related data
      await fetchData();
      broadcastUpdate({ action: 'member_added_to_matrix' });

      // Update available members list
      setAvailableMembers(prev => {
        const updated = prev.filter(m => m.id !== memberId);
        if (updated.length === 0) {
          setAddMemberDialogOpen(false);
        }
        return updated;
      });
    } catch (error) {
      console.error('Error adding member to matrix:', error);
      toast.error('Failed to add member to matrix');
    }
  };

  // Handle removing qualification from matrix
  const handleRemoveQualificationFromMatrix = async qualificationId => {
    // Optimistic: remove column immediately
    const previousRequirements = requirements;
    setRequirements(prev => prev.filter(r => r.id !== qualificationId));

    try {
      const { error } = await supabase
        .from('compliance_requirements')
        .update({ show_on_matrix: false })
        .eq('id', qualificationId);

      if (error) throw error;

      toast.success('Qualification removed from matrix');

      // Full refresh to reconcile all related data
      await fetchData();
      broadcastUpdate({ action: 'qualification_removed_from_matrix' });
    } catch (error) {
      console.error('Error removing qualification from matrix:', error);
      toast.error('Failed to remove qualification from matrix');
      // Rollback optimistic update
      setRequirements(previousRequirements);
    }
  };

  // Handle opening add qualification dialog - now goes straight to AddQualification modal
  const handleOpenAddQualificationDialog = () => {
    // Skip the "Add to Matrix" dialog and go straight to the AddQualification modal
    setIsAddQualificationModalOpen(true);
  };

  // Handle new qualification created from modal
  const handleQualificationCreated = useCallback(async () => {
    try {
      // Refresh ALL matrix data to include the new qualification
      await fetchData();

      // Refresh compliance stats so the new column has stats data
      refetchComplianceStats();

      // Broadcast update to other clients
      broadcastUpdate({ action: 'qualification_added' });

      toast.success('Qualification added to matrix');
      setIsAddQualificationModalOpen(false);
    } catch (error) {
      console.error('Error refreshing matrix:', error);
      toast.error('Failed to refresh matrix');
      setIsAddQualificationModalOpen(false);
    }
  }, [fetchData, broadcastUpdate, refetchComplianceStats, projectId]);

  // Handle adding qualification to matrix
  const handleAddQualificationToMatrix = async qualificationId => {
    try {
      const { error } = await supabase
        .from('compliance_requirements')
        .update({ show_on_matrix: true })
        .eq('id', qualificationId);

      if (error) throw error;

      toast.success('Qualification added to matrix');

      // Full refresh to get new column + all related data
      await fetchData();
      refetchComplianceStats();
      broadcastUpdate({ action: 'qualification_added_to_matrix' });

      // Update available qualifications list
      setAvailableQualifications(prev => {
        const updated = prev.filter(q => q.id !== qualificationId);
        if (updated.length === 0) {
          setAddQualificationDialogOpen(false);
        }
        return updated;
      });
    } catch (error) {
      console.error('Error adding qualification to matrix:', error);
      toast.error('Failed to add qualification to matrix');
    }
  };

  // Handle confirm add members from dialog
  const handleConfirmAddMembers = async () => {
    if (selectedMembersToAdd.length === 0) return;

    try {
      // Add all selected members
      for (const memberId of selectedMembersToAdd) {
        await handleAddToMatrix(memberId);
      }

      // Clear selection and close dialog
      setSelectedMembersToAdd([]);
      setAddMemberDialogOpen(false);
    } catch (error) {
      console.error('Error adding members:', error);
      toast.error('Failed to add some members');
    }
  };

  // Handle confirm add qualification from dialog
  const handleConfirmAddQualification = async () => {
    if (!selectedQualificationForAdd) return;

    try {
      await handleAddQualificationToMatrix(selectedQualificationForAdd);

      // Clear selection and close dialog
      setSelectedQualificationForAdd(null);
      setAddQualificationDialogOpen(false);
    } catch (error) {
      console.error('Error adding qualification:', error);
      toast.error('Failed to add qualification');
    }
  };

  // Toggle row highlighting
  const toggleRowHighlight = memberId => {
    setHighlightedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) {
        newSet.delete(memberId);
      } else {
        newSet.add(memberId);
      }
      return newSet;
    });
  };

  // Toggle column highlighting
  const toggleColumnHighlight = qualificationId => {
    setHighlightedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(qualificationId)) {
        newSet.delete(qualificationId);
      } else {
        newSet.add(qualificationId);
      }
      return newSet;
    });
  };

  // Clear all highlights
  const clearAllHighlights = () => {
    setHighlightedRows(new Set());
    setHighlightedColumns(new Set());
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = e => {
      // Escape to clear all highlights
      if (
        e.key === 'Escape' &&
        (highlightedRows.size > 0 || highlightedColumns.size > 0)
      ) {
        clearAllHighlights();
        toast.success('Cleared all highlights');
      }
      // Escape to close context menu
      if (e.key === 'Escape' && contextMenuOpen) {
        setContextMenuOpen(false);
      }
      // Escape to close header context menu
      if (e.key === 'Escape' && headerContextMenuOpen) {
        setHeaderContextMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    highlightedRows,
    highlightedColumns,
    contextMenuOpen,
    headerContextMenuOpen
  ]);

  // Close context menu on click outside - removed, using overlay instead

  // Handle cell click - now opens expanding tile
  // Handle assignment in a specific space (from space picker in global/stack view)
  const handleSpaceAssignment = async (spaceOption, targetMember, targetRequirement) => {
    setSpaceAssignLoading(true);
    const cellKey = `${targetMember.id}-${targetRequirement.id}`;
    addAssigningCell(cellKey);
    try {
      // Step 1: Add user to space if not a member
      if (!spaceOption.hasMember) {
        const { error: memberErr } = await supabase
          .from('space_members')
          .insert({
            project_id: spaceOption.id,
            user_id: targetMember.user_id,
            role: 'Member',
            show_on_matrix: true,
            created_date: new Date().toISOString(),
            created_by: userProfile?.id
          });

        if (memberErr) throw memberErr;
      }

      // Step 2: Add requirement to space if it doesn't exist
      let reqId = spaceOption.requirementId;
      if (!spaceOption.hasRequirement) {
        const { data: newReq, error: createErr } = await supabase
          .from('compliance_requirements')
          .insert({
            project_id: spaceOption.id,
            name: targetRequirement.name,
            description: targetRequirement.description,
            category: targetRequirement.category || 'Training',
            priority: targetRequirement.priority || 'medium',
            status: 'active',
            renewal_period: targetRequirement.renewal_period,
            course_link_id: targetRequirement.course_link_id,
            course_link_type: targetRequirement.course_link_type,
            requirement_type: targetRequirement.requirement_type,
            show_on_matrix: true
          })
          .select('id')
          .single();

        if (createErr) throw createErr;
        reqId = newReq.id;
      }

      // Step 3: Check for existing qualification
      let existingQual = null;
      if (targetRequirement.course_link_id && targetRequirement.course_link_type) {
        const { data } = await supabase
          .from('user_qualifications')
          .select('*')
          .eq('user_id', targetMember.user_id)
          .eq('course_link_id', targetRequirement.course_link_id)
          .eq('course_link_type', targetRequirement.course_link_type)
          .maybeSingle();
        existingQual = data;
      }
      if (!existingQual) {
        const { data } = await supabase
          .from('user_qualifications')
          .select('*')
          .eq('user_id', targetMember.user_id)
          .eq('requirement_id', reqId)
          .maybeSingle();
        existingQual = data;
      }

      // Step 4: Create assignment
      const { error: assignErr } = await supabase
        .from('compliance_assignments')
        .upsert({
          project_id: spaceOption.id,
          requirement_id: reqId,
          team_member_id: targetMember.user_id,
          assigned: true
        }, { onConflict: 'project_id,requirement_id,team_member_id' })
        .select()
        .single();

      if (assignErr) throw assignErr;

      // Step 5: Create user_qualification if none exists
      if (!existingQual) {
        await supabase.from('user_qualifications').upsert({
          user_id: targetMember.user_id,
          requirement_id: reqId,
          course_link_id: targetRequirement.course_link_id,
          course_link_type: targetRequirement.course_link_type,
          status: null,
          assigned_date: new Date().toISOString(),
          assigned_by: targetMember.user_id,
          due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          notes: 'Requirement assigned',
          updated_date: new Date().toISOString()
        }, { onConflict: 'user_id,requirement_id' });
      }

      setSpaceAssignPicker(null);
      await fetchData();
      toast.success(`${targetRequirement.name} assigned in ${spaceOption.name}`);
      broadcastUpdate({ action: 'assignment_created' });
    } catch (err) {
      console.error('Error assigning in space:', err);
      toast.error('Failed to assign requirement');
    } finally {
      setSpaceAssignLoading(false);
      setAssigningCells(prev => {
        const next = new Set(prev);
        next.delete(cellKey);
        return next;
      });
    }
  };

  const handleCellClick = async (
    member,
    requirement,
    status,
    event,
    reqData = null
  ) => {
    // If context menu is open, don't process click
    if (contextMenuOpen) {
      return;
    }

    // Look up the full requirement object (the parameter only has limited fields)
    const fullRequirement =
      filteredRequirements.find(req => req.id === requirement.id) ||
      requirement;

    // Use reqData if provided (has isAssigned property from the cell)
    const isAssigned = reqData?.isAssigned ?? requirement.isAssigned;

    // If clicking an unassigned cell (isAssigned=false or status='add'/'unassigned'), assign it
    // This handles: truly unassigned, cross-space qualifications, any status where assigned=false
    if (isAssigned === false || status === 'add' || status === 'unassigned') {
      // Stop propagation if event exists
      if (event) {
        event.stopPropagation();
      }

      // In stack/global view, we need to ask which space to assign in
      if (isStackView && !projectId) {
        try {
          // Fetch in parallel: user's space memberships + all spaces with this requirement
          const [memberResult, reqResult] = await Promise.all([
            supabase
              .from('space_members')
              .select('id, project_id, projects(id, name)')
              .eq('user_id', member.user_id)
              .in('project_id', projectIds),
            supabase
              .from('compliance_requirements')
              .select('id, project_id, name, projects(id, name)')
              .eq('name', fullRequirement.name)
              .eq('show_on_matrix', true)
              .in('project_id', projectIds)
          ]);

          const memberSpaces = memberResult.data || [];
          const reqMatches = reqResult.data || [];

          const memberSpaceIds = new Set(memberSpaces.map(s => s.project_id));
          const reqByProject = new Map();
          reqMatches.forEach(r => reqByProject.set(r.project_id, r));

          // Build unified list of all relevant spaces
          const allSpaceIds = new Set([
            ...memberSpaces.map(s => s.project_id),
            ...reqMatches.map(r => r.project_id)
          ]);

          const spaces = [...allSpaceIds].map(spaceId => {
            const memberEntry = memberSpaces.find(s => s.project_id === spaceId);
            const reqEntry = reqByProject.get(spaceId);
            return {
              id: spaceId,
              name: memberEntry?.projects?.name || reqEntry?.projects?.name || spaceId,
              hasMember: memberSpaceIds.has(spaceId),
              hasRequirement: reqByProject.has(spaceId),
              requirementId: reqEntry?.id || null,
              teamMemberId: member.user_id
            };
          });

          if (spaces.length === 0) {
            toast.error('No matching spaces found');
            return;
          }

          // If only one space and both user + requirement exist, assign directly
          const readySpaces = spaces.filter(s => s.hasMember && s.hasRequirement);
          if (readySpaces.length === 1 && spaces.length === 1) {
            await handleSpaceAssignment(readySpaces[0], member, fullRequirement);
            return;
          }

          // Show picker
          setSpaceAssignPicker({ member, requirement: fullRequirement, spaces });
        } catch (err) {
          console.error('Error loading space options:', err);
          toast.error('Failed to load space options');
        }
        return;
      }

      // Single space view — assign directly
      const cellKey = `${member.id}-${fullRequirement.id}`;
      addAssigningCell(cellKey);

      const previousAssignments = assignments;
      const tempAssignment = {
        id: `temp-${Date.now()}`,
        team_member_id: member.id,
        requirement_id: fullRequirement.id,
        project_id: projectId,
        assigned: true,
        isAssigned: true,
        effectiveStatus: 'missing',
        hasQualification: false,
        _isLoading: true
      };
      setAssignments(prev => [...prev, tempAssignment]);

      try {
        // Validate member has user_id
        if (!member.user_id) {
          console.error('Member missing user_id:', member);
          toast.error(
            'Cannot assign requirement: team member not linked to user profile'
          );
          setAssignments(previousAssignments);
          return;
        }

        // Step 1: Check if user already has this qualification globally
        // Try course_link match first (cross-space), then requirement_id
        let existingQual = null;

        if (
          fullRequirement.course_link_id &&
          fullRequirement.course_link_type
        ) {
          const { data: qualByCourseLink } = await supabase
            .from('user_qualifications')
            .select('*')
            .eq('user_id', member.user_id)
            .eq('course_link_id', fullRequirement.course_link_id)
            .eq('course_link_type', fullRequirement.course_link_type)
            .maybeSingle();

          existingQual = qualByCourseLink;
        }

        if (!existingQual) {
          const { data: qualByRequirement } = await supabase
            .from('user_qualifications')
            .select('*')
            .eq('user_id', member.user_id)
            .eq('requirement_id', fullRequirement.id)
            .maybeSingle();

          existingQual = qualByRequirement;
        }

        // Step 2: Create space-specific assignment in compliance_assignments (just tracking flag)
        const { data: assignmentData, error: assignmentError } = await supabase
          .from('compliance_assignments')
          .upsert(
            {
              project_id: projectId,
              requirement_id: fullRequirement.id,
              team_member_id: member.id,
              assigned: true
            },
            {
              onConflict: 'project_id,requirement_id,team_member_id'
            }
          )
          .select()
          .single();

        if (assignmentError) throw assignmentError;

        // Step 3: Create/update user_qualification if none exists
        // This ensures there's a qualification record for status tracking
        if (!existingQual) {
          const qualificationData = {
            user_id: member.user_id,
            requirement_id: fullRequirement.id,
            course_link_id: fullRequirement.course_link_id,
            course_link_type: fullRequirement.course_link_type,
            status: null, // NULL = missing (no evidence yet)
            assigned_date: new Date().toISOString(),
            assigned_by: member.user_id,
            due_date: new Date(
              Date.now() + 30 * 24 * 60 * 60 * 1000
            ).toISOString(),
            notes: 'Requirement assigned',
            updated_date: new Date().toISOString()
          };

          await supabase.from('user_qualifications').upsert(qualificationData, {
            onConflict: 'user_id,requirement_id'
          });
        }

        // Refresh matrix data to show correct status
        await fetchData();

        if (existingQual) {
          toast.success(
            `${requirement.name} assigned to ${member.name} (synced from existing qualification)`
          );
        } else {
          toast.success(`${requirement.name} assigned to ${member.name}`);
        }

        // Broadcast update
        broadcastUpdate({ action: 'assignment_created' });
      } catch (error) {
        console.error('Error assigning requirement:', error);
        toast.error('Failed to assign requirement');
        setAssignments(previousAssignments);
      } finally {
        removeAssigningCell(cellKey);
      }
      return; // Don't open dock for unassigned cells
    }

    // For existing assignments, open the radial dock
    // Stop propagation to prevent multiple clicks
    if (event) {
      event.stopPropagation();
    }

    // Get the exact position and size of the clicked tile for seamless expansion
    let tileData = null;
    if (event && event.currentTarget) {
      const cellElement = event.currentTarget;
      const rect = cellElement.getBoundingClientRect();

      // Store tile dimensions for animation
      tileData = {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
        centerX: rect.left + rect.width / 2,
        centerY: rect.top + rect.height / 2
      };
    } else {
      // Fallback: animate from center of viewport when no event is available
      tileData = {
        x: (window.innerWidth - 100) / 2,
        y: (window.innerHeight - 40) / 2,
        width: 100,
        height: 40,
        centerX: window.innerWidth / 2,
        centerY: window.innerHeight / 2
      };
    }

    const assignment = assignments.find(
      a =>
        a.team_member_id === member.id &&
        a.requirement_id === fullRequirement.id
    );

    // Set dock state with tile data for seamless animation
    // Use openDock action instead of individual setters
    openDock(tileData, {
      member,
      requirement: fullRequirement,
      status,
      assignment: normalizeAssignmentForDock(assignment),
      tileData
    });
  };

  // Handle cell right-click for context menu
  const handleCellRightClick = (member, requirement, status, event) => {
    event.preventDefault(); // Prevent default context menu
    event.stopPropagation();

    // Close header context menu if open
    setHeaderContextMenuOpen(false);

    // If context menu is already open, close it first
    if (contextMenuOpen) {
      setContextMenuOpen(false);
      return;
    }

    // Look up the full requirement object (the parameter only has limited fields)
    const fullRequirement =
      filteredRequirements.find(req => req.id === requirement.id) ||
      requirement;

    const assignment = assignments.find(
      a =>
        a.team_member_id === member.id &&
        a.requirement_id === fullRequirement.id
    );

    // Use openContextMenu action instead of individual setters
    openContextMenu(
      { x: event.clientX, y: event.clientY },
      {
        member,
        requirement: fullRequirement,
        status,
        assignment
      }
    );
  };

  // Handle header right-click for qualification column context menu
  const handleHeaderRightClick = (qual, qualStats, event) => {
    event.preventDefault();
    event.stopPropagation();

    // Close any existing context menus
    if (headerContextMenuOpen) {
      closeHeaderContextMenu();
      return;
    }
    closeContextMenu();

    // Use openHeaderContextMenu action instead of individual setters
    openHeaderContextMenu(
      { x: event.clientX, y: event.clientY },
      { qual, qualStats }
    );
  };

  // Handle "Assign All" - assigns qualification to all team members who don't have it assigned
  const handleAssignAllToQualification = async qual => {
    if (!qual || !projectId) return;

    try {
      // Get all existing assignment records for this qualification
      const { data: existingDbAssignments, error: fetchError } = await supabase
        .from('compliance_assignments')
        .select('id, team_member_id, assigned')
        .eq('requirement_id', qual.id)
        .eq('project_id', projectId);

      if (fetchError) throw fetchError;

      // Find records that exist but are NOT marked as assigned (assigned = false or null)
      const unassignedRecords = (existingDbAssignments || []).filter(
        a => !a.assigned
      );
      const assignedMemberIds = new Set(
        (existingDbAssignments || [])
          .filter(a => a.assigned === true)
          .map(a => a.team_member_id)
      );

      // Find team members with no record at all
      const existingMemberIds = new Set(
        (existingDbAssignments || []).map(a => a.team_member_id)
      );
      const membersWithNoRecord = filteredMatrixData.teamMembers.filter(
        member => !existingMemberIds.has(member.id)
      );

      // Members to update (have record but assigned=false)
      const recordsToUpdate = unassignedRecords.map(r => r.id);

      // Members to insert (no record at all)
      const membersToInsert = membersWithNoRecord;

      const totalToProcess = recordsToUpdate.length + membersToInsert.length;

      if (totalToProcess === 0) {
        toast.info('All team members already have this qualification assigned');
        setHeaderContextMenuOpen(false);
        return;
      }

      // Update existing unassigned records to assigned=true
      if (recordsToUpdate.length > 0) {
        const { error: updateError } = await supabase
          .from('compliance_assignments')
          .update({ assigned: true })
          .in('id', recordsToUpdate);

        if (updateError) throw updateError;
      }

      // Insert new records for members with no record
      if (membersToInsert.length > 0) {
        const newAssignments = membersToInsert.map(member => ({
          requirement_id: qual.id,
          team_member_id: member.id,
          project_id: projectId,
          assigned: true
        }));

        const { error: insertError } = await supabase
          .from('compliance_assignments')
          .insert(newAssignments);

        if (insertError) throw insertError;
      }

      // Refresh data and notify other clients
      await fetchData();
      broadcastUpdate({ action: 'assign_all' });

      const messages = [];
      if (recordsToUpdate.length > 0) {
        messages.push(`Updated ${recordsToUpdate.length} existing`);
      }
      if (membersToInsert.length > 0) {
        messages.push(`Added ${membersToInsert.length} new`);
      }
      toast.success(
        `Assigned "${qual.name}" to ${totalToProcess} team member${totalToProcess > 1 ? 's' : ''}`
      );
    } catch (error) {
      console.error('Error assigning qualification to all:', error);
      toast.error('Failed to assign qualification to all team members');
    } finally {
      setHeaderContextMenuOpen(false);
    }
  };

  // Handle context menu actions
  const handleContextMenuAction = async action => {
    if (!contextMenuCell) return;

    setContextMenuOpen(false);

    // Only open the cell dialog for actions that need it (book, upload, remove, assign-and-upload)
    // Skip for 'details' (uses dock), 'assign', 'oneclick-quote', 'profile' (no dialog needed)
    if (
      action !== 'details' &&
      action !== 'assign' &&
      action !== 'oneclick-quote' &&
      action !== 'profile' &&
      action !== 'mark-booked'
    ) {
      setSelectedCell(contextMenuCell);
    }

    switch (action) {
      case 'assign':
        // In stack view, show space selector dialog
        if (isStackView && stackProjects && stackProjects.length > 0) {
          // Use openSpaceSelector action instead of individual setters
          openSpaceSelector({
            member: contextMenuCell.member,
            requirement: contextMenuCell.requirement
          });
        } else {
          // Single space view - assign directly
          const previousAssignments = assignments;
          try {
            // Optimistic: add temp assignment
            const newAssignment = {
              id: `temp-${Date.now()}`,
              team_member_id: contextMenuCell.member.id,
              requirement_id: contextMenuCell.requirement.id,
              project_id: projectId,
              assigned: true,
              isAssigned: true,
              effectiveStatus: 'missing',
              hasQualification: false
            };

            setAssignments(prev => [...prev, newAssignment]);

            const { error } = await supabase
              .from('compliance_assignments')
              .insert({
                team_member_id: contextMenuCell.member.id,
                requirement_id: contextMenuCell.requirement.id,
                project_id: projectId,
                assigned: true
              });

            if (error) throw error;

            toast.success(
              `${contextMenuCell.requirement.name} assigned to ${contextMenuCell.member.name}`
            );

            // Reconcile with server and notify other clients
            await fetchData();
            broadcastUpdate({ action: 'assignment_created' });
          } catch (error) {
            console.error('Error assigning requirement:', error);
            toast.error('Failed to assign requirement');
            // Rollback optimistic update
            setAssignments(previousAssignments);
          }
        }
        break;

      case 'assign-and-upload':
        // Assign and then open upload modal
        {
          const previousAssignments = assignments;
          try {
            // Optimistic: add temp assignment
            const newAssignment = {
              id: `temp-${Date.now()}`,
              team_member_id: contextMenuCell.member.id,
              requirement_id: contextMenuCell.requirement.id,
              project_id: projectId,
              assigned: true,
              isAssigned: true,
              effectiveStatus: 'missing',
              hasQualification: false
            };

            setAssignments(prev => [...prev, newAssignment]);

            const { data, error } = await supabase
              .from('compliance_assignments')
              .insert({
                team_member_id: contextMenuCell.member.id,
                requirement_id: contextMenuCell.requirement.id,
                project_id: projectId,
                assigned: true
              })
              .select()
              .single();

            if (error) throw error;

            // Update selected cell with the new assignment
            setSelectedCell({
              member: contextMenuCell.member,
              requirement: contextMenuCell.requirement,
              status: 'missing',
              assignment: data
            });

            // Open upload modal
            setUploadModalOpen(true);

            // Non-blocking reconciliation (upload modal is about to open)
            fetchData();
            broadcastUpdate({ action: 'assignment_created' });
          } catch (error) {
            console.error('Error assigning requirement:', error);
            toast.error('Failed to assign requirement');
            // Rollback optimistic update
            setAssignments(previousAssignments);
          }
        }
        break;

      case 'book':
        setQuoteModalOpen(true);
        break;

      case 'upload':
        setUploadModalOpen(true);
        break;

      case 'details':
        // Open the dock dialog with context menu position as fallback
        // Use context menu position as tile position for animation
        const fallbackTileData = {
          x: contextMenuPosition.x - 60,
          y: contextMenuPosition.y - 32,
          width: 120,
          height: 64,
          centerX: contextMenuPosition.x,
          centerY: contextMenuPosition.y
        };

        // Pass context menu cell data with normalized assignment for dock display
        openDock(fallbackTileData, {
          member: contextMenuCell.member,
          requirement: contextMenuCell.requirement,
          status: contextMenuCell.status,
          assignment: normalizeAssignmentForDock(contextMenuCell.assignment),
          tileData: fallbackTileData
        });
        break;

      case 'remove':
        setRemoveModalOpen(true);
        break;

      case 'oneclick-quote':
        // Pass contextMenuCell directly to avoid race condition with state updates
        handleOneClickQuote(contextMenuCell);
        break;

      case 'profile':
        navigate(`/profile/${contextMenuCell.member.id}`);
        break;

      case 'mark-booked':
        setMarkBookedCell(contextMenuCell);
        setMarkBookedConfirmOpen(true);
        break;

      default:
        break;
    }
  };

  // Handle confirming "Mark as Booked" from the confirmation dialog
  const handleConfirmMarkBooked = async () => {
    if (!markBookedCell) return;
    const member = markBookedCell.member;
    const requirement = markBookedCell.requirement;
    const assignment = markBookedCell.assignment;

    if (!assignment?.id) {
      toast.error('No assignment found for this cell');
      return;
    }

    try {
      await supabase
        .from('compliance_assignments')
        .update({
          status: 'booked',
          updated_date: new Date().toISOString()
        })
        .eq('id', assignment.id);

      toast.success(`Marked as booked for ${member.name}`);
      await fetchData();
      broadcastUpdate({ action: 'mark_booked' });
    } catch (error) {
      console.error('Error marking as booked:', error);
      toast.error('Failed to mark as booked');
    } finally {
      setMarkBookedConfirmOpen(false);
      setMarkBookedCell(null);
    }
  };

  // Handle radial dock actions
  const handleDockAction = async action => {
    if (!dockCell) return;

    setSelectedCell(dockCell);
    setDockOpen(false);

    switch (action) {
      case 'book':
        // Open quote modal instead of booking modal
        setQuoteModalOpen(true);
        break;

      case 'upload':
        // Open upload modal in normal mode
        setUploadModalInitialOverride(false);
        setUploadModalOpen(true);
        break;

      case 'override':
        // Open upload modal in override mode
        setUploadModalInitialOverride(true);
        setUploadModalOpen(true);
        break;

      case 'details':
        // Open details modal
        setDetailsModalOpen(true);
        break;

      case 'remove':
        // Open remove modal
        setRemoveModalOpen(true);
        break;

      case 'oneclick-quote':
        // Handle 1-click quote - opens quick quote popup with context menu cell
        handleOneClickQuote(contextMenuCell);
        break;

      default:
        break;
    }
  };

  // Handle one-click quote - opens the Quick Quote popup
  const handleOneClickQuote = (cell = null) => {
    // Use parameter first (for direct calls from context menu), then fall back to state
    const targetCell = cell || selectedCell || dockCell;
    if (!targetCell) return;

    // Store cell data and open popup
    setQuickQuoteCellData(targetCell);
    setQuickQuotePopupOpen(true);

    // Close other panels/dialogs
    setDialogOpen(false);
    setDockOpen(false);
    setContextMenuOpen(false);
  };

  // Handle Quick Quote popup submission - creates the quote and returns the ID
  const handleQuickQuoteSubmit = async quoteData => {
    try {
      // Call the same booking handler that processes quotes
      // But we need to return the created quote ID
      const result = await handleModalBookingWithReturn(quoteData);

      // Close the dock after action if open
      setDockOpen(false);
      setTimeout(() => closeDock(), 400);

      return result;
    } catch (error) {
      console.error('Error creating quick quote:', error);
      toast.error('Failed to create quote');
      throw error;
    }
  };

  // Modified version of handleModalBooking that returns the quote ID
  const handleModalBookingWithReturn = async data => {
    try {
      // Determine the course name - use provided one or fall back to requirement
      const courseName =
        data.courseName ||
        quickQuoteCellData?.requirement?.name ||
        'Training Course';

      // Get delegate names if provided, otherwise use selected member
      const delegatesList =
        data.delegates && data.delegates.length > 0
          ? data.delegates.join(', ')
          : quickQuoteCellData?.member?.name || 'Unknown';

      // Format date preferences properly
      let formattedDatePrefs = '';
      if (data.datePreference === 'specific' && data.preferredDate) {
        formattedDatePrefs = new Date(data.preferredDate).toLocaleDateString(
          'en-US',
          {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          }
        );
      } else if (data.preferredMonth) {
        const [year, month] = data.preferredMonth.split('-');
        const monthNames = [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December'
        ];
        formattedDatePrefs = `${monthNames[parseInt(month) - 1]} ${year}`;
      }

      // Build webhook payload
      const webhookPayload = {
        requested_date: new Date().toISOString(),
        course_name: courseName,
        delegates: parseInt(data.numberOfAttendees),
        date_prefs: formattedDatePrefs,
        locations: data.location || 'Not specified',
        additional_details: `<p><strong>Requested by:</strong> ${user?.user_metadata?.first_name || ''} ${user?.user_metadata?.last_name || ''} (${user?.email || 'Unknown'})</p>
                      <p><strong>Delegates:</strong> ${delegatesList} (${data.numberOfAttendees} total)</p>
                      <p><strong>Project:</strong> ${space?.name || 'Unknown Project'}</p>
                      ${quickQuoteCellData?.requirement?.name ? `<p><strong>Requirements:</strong></p><p>${quickQuoteCellData.requirement.name}</p>` : ''}
                      ${data.notes ? `<p><strong>Additional notes:</strong></p><p>${data.notes}</p>` : ''}`
      };

      // Get the user_profile ID
      let userProfileId = null;
      if (user?.id) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('auth_id', user.id)
          .single();
        userProfileId = profile?.id;
      }

      // Get delegate names
      let delegateNames = [];
      if (data.delegates && Array.isArray(data.delegates)) {
        delegateNames = data.delegates;
      } else if (quickQuoteCellData?.member?.name) {
        delegateNames = [quickQuoteCellData.member.name];
      }

      // Get company_id from the project
      let companyId = null;
      if (projectId) {
        const { data: projectData } = await supabase
          .from('projects')
          .select('company_id')
          .eq('id', projectId)
          .single();
        companyId = projectData?.company_id;
      }

      // Fetch global trainers to pre-select on the draft quote
      let defaultTrainers = [];
      try {
        const { data: globalTrainers } = await supabase
          .from('trainers')
          .select('id, trainer_name, booking_email')
          .is('company_id', null)
          .order('display_order', { nullsFirst: false })
          .order('trainer_name');

        if (globalTrainers?.length > 0) {
          defaultTrainers = globalTrainers.map(t => ({
            id: t.id,
            name: t.trainer_name,
            email: t.booking_email,
            type: 'global',
            wasNotified: false
          }));
        }
      } catch (err) {
        console.error('Failed to fetch global trainers:', err);
      }

      // Create the quote
      const quoteInsertData = {
        project_id: projectId,
        company_id: companyId,
        requested_by: userProfileId,
        requested_date: new Date().toISOString(),
        course_name: courseName,
        status: 'draft',
        delegates: data.numberOfAttendees,
        delegate_names: delegateNames,
        date_prefs: formattedDatePrefs,
        locations: data.location,
        further_info: data.notes,
        webhook_payload: webhookPayload,
        delivery_methods: data.deliveryMethods || ['onsite'],
        selected_trainers: defaultTrainers,
        requirement_id: data.requirementId || null,
        course_id: quickQuoteCellData?.requirement?.course_link_id || null
      };

      const { data: quoteData, error: quoteError } = await supabase
        .from('training_quotes')
        .insert(quoteInsertData)
        .select()
        .single();

      if (quoteError) {
        console.error('Failed to store quote:', quoteError);
        throw quoteError;
      }

      const createdQuoteId = quoteData?.id;

      // Link quote to matrix: create compliance_assignments (tracking) and user_qualifications (status)
      if (data.requirementId) {
        const requirement = quickQuoteCellData?.requirement;
        const allDelegateIds =
          data.selectedDelegateIds?.length > 0
            ? data.selectedDelegateIds
            : data.memberId
              ? [data.memberId]
              : [];

        // Create/update compliance_assignments for all delegates (tracking + quote_id for navigation)
        if (allDelegateIds.length > 0) {
          const assignmentsToUpsert = allDelegateIds.map(delegateId => ({
            project_id: projectId,
            requirement_id: data.requirementId,
            team_member_id: delegateId,
            assigned: true,
            status: 'quoting',
            quote_id: createdQuoteId
          }));

          await supabase
            .from('compliance_assignments')
            .upsert(assignmentsToUpsert, {
              onConflict: 'project_id,requirement_id,team_member_id'
            });
        }

        // Update user_qualifications renewal_status for all delegates
        for (const delegateId of allDelegateIds) {
          const teamMember = teamMembers.find(m => m.id === delegateId);
          const userId = teamMember?.user_id || delegateId;

          if (userId) {
            const { data: existingQual } = await supabase
              .from('user_qualifications')
              .select('id')
              .eq('user_id', userId)
              .eq('requirement_id', data.requirementId)
              .maybeSingle();

            if (existingQual) {
              await supabase
                .from('user_qualifications')
                .update({
                  renewal_status: 'quoting',
                  updated_date: new Date().toISOString()
                })
                .eq('id', existingQual.id);
            } else {
              // Create new user_qualification with course_link fields for cross-space matching
              await supabase.from('user_qualifications').insert({
                user_id: userId,
                requirement_id: data.requirementId,
                course_link_id: requirement?.course_link_id || null,
                course_link_type: requirement?.course_link_type || null,
                status: null,
                renewal_status: 'quoting',
                created_date: new Date().toISOString(),
                updated_date: new Date().toISOString()
              });
            }
          }
        }
      }

      toast.success('Quote created successfully');

      // Trigger matrix refresh and notify other clients
      if (refetchMatrixData) {
        refetchMatrixData();
      }
      broadcastUpdate({ action: 'quote_created' });

      return { quoteId: createdQuoteId };
    } catch (error) {
      console.error('Error in handleModalBookingWithReturn:', error);
      throw error;
    }
  };

  // Handle space selection confirmation for stack view assignments
  const handleSpaceSelectionConfirm = async selectedSpaceIds => {
    if (!pendingAssignment) return;

    try {
      const { member, requirement } = pendingAssignment;
      let successCount = 0;
      let errorCount = 0;

      // Assign to each selected space
      for (const spaceId of selectedSpaceIds) {
        try {
          const { error } = await supabase
            .from('compliance_assignments')
            .insert({
              team_member_id: member.id,
              requirement_id: requirement.id,
              project_id: spaceId,
              assigned: true
            });

          if (error) {
            // Check if it's a duplicate (already assigned)
            if (error.code === '23505') {
              // Silently skip duplicates
              continue;
            }
            throw error;
          }

          successCount++;
        } catch (error) {
          console.error(`Error assigning to space ${spaceId}:`, error);
          errorCount++;
        }
      }

      // Show success message
      if (successCount > 0) {
        const spaceName = successCount === 1 ? 'space' : 'spaces';
        toast.success(
          `${requirement.name} assigned to ${member.name} in ${successCount} ${spaceName}`
        );

        // Reload data to reflect changes
        await reloadAssignments();
      }

      if (errorCount > 0) {
        toast.warning(`Failed to assign in ${errorCount} space(s)`);
      }

      // Close dialog and reset
      closeSpaceSelector();
    } catch (error) {
      console.error('Error in space selection:', error);
      toast.error('Failed to assign qualification');
    }
  };

  // Helper function to reload assignments
  const reloadAssignments = async () => {
    // HYBRID ARCHITECTURE (transition period):
    // 1. team_member_requirements: Space-specific requirements (new way)
    // 2. compliance_assignments: Old space-specific assignments (legacy, still contains data)
    // 3. user_qualifications: User-level completions (new way)
    // We need to merge all three sources during transition

    // Fetch space-specific requirements (new way)
    const { data: teamMemberReqs } = await supabase
      .from('team_member_requirements')
      .select('*');

    // Fetch old compliance_assignments (legacy - still has data)
    const { data: complianceAssigns } = await supabase
      .from('compliance_assignments')
      .select('*')
      .eq('project_id', projectId);

    // Filter to only include members from this project
    const projectMemberIds = new Set(teamMembers.map(m => m.id));
    const filteredTeamReqs = (teamMemberReqs || []).filter(req =>
      projectMemberIds.has(req.team_member_id)
    );

    // Fetch user-level qualifications for all team members in this project
    const userIds = teamMembers.map(m => m.user_id).filter(Boolean);
    const { data: userQuals } = await supabase
      .from('user_qualifications')
      .select(
        `
        *,
        compliance_requirements!inner(id, name)
      `
      )
      .in('user_id', userIds);

    // Fetch requirement names for all requirements in this project
    const allReqIds = [
      ...new Set([
        ...(filteredTeamReqs || []).map(r => r.requirement_id),
        ...(complianceAssigns || []).map(r => r.requirement_id)
      ])
    ];

    const { data: requirements } = await supabase
      .from('compliance_requirements')
      .select('id, name, calculated_required, minimum_personnel')
      .in('id', allReqIds);

    const reqIdToName = new Map(requirements?.map(r => [r.id, r.name]) || []);

    // Create a map of user_id + requirement_NAME to qualification
    // This handles duplicate requirements with different IDs but same name
    const qualsMap = new Map();
    (userQuals || []).forEach(qual => {
      const reqName = qual.compliance_requirements?.name;
      if (reqName) {
        const key = `${qual.user_id}_${reqName}`;
        // If multiple qualifications for same user+name, keep the best one
        const existing = qualsMap.get(key);
        if (!existing || qual.status === 'completed') {
          qualsMap.set(key, qual);
        }
      }
    });

    // Start with team_member_requirements and compliance_assignments
    const allRequirements = [...filteredTeamReqs, ...(complianceAssigns || [])];

    // Merge: For each requirement, check if user has completed it globally (by NAME)
    const mergedAssignments = allRequirements.map(req => {
      const teamMember = teamMembers.find(m => m.id === req.team_member_id);
      const userId = teamMember?.user_id;

      if (userId) {
        const reqName = reqIdToName.get(req.requirement_id);
        if (reqName) {
          const key = `${userId}_${reqName}`;
          const userQual = qualsMap.get(key);

          // If user has completed this globally, use that data; otherwise use local requirement
          if (userQual) {
            return {
              ...req,
              ...userQual,
              team_member_id: req.team_member_id, // Keep team_member_id for matrix lookups
              project_id: projectId // Keep project_id for context
            };
          }
        }
      }

      return req;
    });

    setAssignments(mergedAssignments);
  };

  // Handle modal actions
  const handleModalUpload = async data => {
    try {
      // Get the team member's user_id
      const teamMember = teamMembers.find(m => m.id === data.memberId);
      if (!teamMember?.user_id) {
        throw new Error(
          'Cannot save qualification: team member not linked to user profile'
        );
      }

      // Check if assignment exists in compliance_assignments
      const { data: existingAssignment } = await supabase
        .from('compliance_assignments')
        .select('id')
        .eq('project_id', projectId)
        .eq('team_member_id', teamMember.user_id)
        .eq('requirement_id', data.requirementId)
        .single();

      // If no assignment exists, create it (just tracking flag)
      if (!existingAssignment) {
        const { error: assignError } = await supabase
          .from('compliance_assignments')
          .insert({
            project_id: projectId,
            team_member_id: teamMember.id,
            requirement_id: data.requirementId,
            assigned: true
          });

        if (assignError) {
          console.error('Error creating assignment:', assignError);
          throw new Error('Failed to create assignment in matrix');
        }
      }

      if (data.isOverride) {
        // Override path - no file, manual status set
        const result = await userQualificationsService.upsertUserQualification({
          userId: teamMember.user_id,
          requirementId: data.requirementId,
          status: 'completed',
          completedDate: new Date().toISOString(),
          expiryDate: data.expiryDate,
          evidenceUrl: null,
          certificateNumber: data.certificateNumber || null,
          notes: data.overrideNotes || null,
          assignedBy: userProfile?.id || teamMember.user_id,
          overrideReason: data.overrideStatus,
          overrideBy: userProfile?.id || null,
          overrideDate: new Date().toISOString()
        });

        if (!result.success) {
          throw new Error(result.error);
        }

        toast.success('Status override saved and synced across all spaces');
      } else {
        // Normal upload path - file is already uploaded by the modal
        const filePath = data.uploadedFilePath;

        // Stamp uploaded_by onto evidence files if provided
        const evidenceFiles = data.evidenceFiles?.map(f => ({
          ...f,
          uploaded_by: userProfile?.id || teamMember.user_id
        }));

        const result = await userQualificationsService.upsertUserQualification({
          userId: teamMember.user_id,
          requirementId: data.requirementId,
          status: 'completed',
          completedDate: new Date().toISOString(),
          expiryDate: data.expiryDate,
          evidenceUrl: filePath,
          certificateNumber: data.certificateNumber || null,
          notes: data.notes || null,
          assignedBy: userProfile?.id || teamMember.user_id,
          overrideReason: null,
          overrideBy: null,
          overrideDate: null,
          evidenceFiles
        });

        if (!result.success) {
          throw new Error(result.error);
        }

        toast.success('Certificate uploaded and synced across all spaces');
      }

      await fetchData();
      setDockOpen(false);
      setUploadModalOpen(false);

      // Broadcast matrix update to all connected clients
      broadcastUpdate({ action: 'certificate_uploaded' });
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save certificate');
      throw error;
    }
  };

  const handleBookFromQuote = quoteDetails => {
    // Open booking modal with pre-filled data from approved quote
    setSelectedCell({
      ...selectedCell,
      status: 'required',
      quoteDetails: quoteDetails
    });
    setIsBookingModalOpen(true);
  };

  const handleModalBooking = async data => {
    try {
      // Determine the course name - use provided one or fall back to requirement
      const courseName =
        data.courseName || selectedCell?.requirement?.name || 'Training Course';

      // Get delegate names if provided, otherwise use selected member
      const delegatesList =
        data.delegates && data.delegates.length > 0
          ? data.delegates.join(', ')
          : selectedCell?.member?.name || 'Unknown';

      // Format date preferences properly
      let formattedDatePrefs = '';
      if (data.datePreference === 'specific' && data.preferredDate) {
        formattedDatePrefs = new Date(data.preferredDate).toLocaleDateString(
          'en-US',
          {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          }
        );
      } else if (data.preferredMonth) {
        // Convert YYYY-MM to readable format like "January to March 2025"
        const [year, month] = data.preferredMonth.split('-');
        const monthNames = [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December'
        ];
        formattedDatePrefs = `${monthNames[parseInt(month) - 1]} ${year}`;
      }

      // Send ONLY the fields the webhook expects
      const webhookPayload = {
        requested_date: new Date().toISOString(),
        course_name: courseName,
        delegates: parseInt(data.numberOfAttendees),
        date_prefs: formattedDatePrefs,
        locations: data.location || 'Not specified',
        additional_details: `<p><strong>Requested by:</strong> ${user?.user_metadata?.first_name || ''} ${user?.user_metadata?.last_name || ''} (${user?.email || 'Unknown'})</p>
                      <p><strong>Delegates:</strong> ${delegatesList} (${data.numberOfAttendees} total)</p>
                      <p><strong>Project:</strong> ${space?.name || 'Unknown Project'}</p>
                      ${selectedCell?.requirement?.name ? `<p><strong>Requirements:</strong></p><p>${selectedCell.requirement.name}</p>` : ''}
                      ${data.notes ? `<p><strong>Additional notes:</strong></p><p>${data.notes}</p>` : ''}`
      };

      // No longer call webhook immediately - save as draft for later sending
      // The user will click "Send to CRM" in the bookings tool
      let responseData = null; // Keep null - no webhook response yet

      // Create booking request - store details in notes field
      const bookingDetails = {
        datePreference: data.datePreference,
        preferredDate: data.preferredDate,
        preferredMonth: data.preferredMonth,
        location: data.location,
        numberOfAttendees: data.numberOfAttendees,
        additionalNotes: data.notes
      };

      // Convert month to proper date format (first day of the month)
      let dueDate = null;
      if (data.preferredDate) {
        dueDate = data.preferredDate;
      } else if (data.preferredMonth) {
        // Check if it's already in YYYY-MM format
        if (data.preferredMonth.match(/^\d{4}-\d{2}$/)) {
          dueDate = `${data.preferredMonth}-01`;
        } else {
          // If it's in a different format, try to parse it
          dueDate = null;
        }
      }

      // Get the user_profile ID from auth user - needed for quote creation
      let userProfileId = null;
      if (user?.id) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('auth_id', user.id)
          .single();
        userProfileId = profile?.id;
      }

      // Get delegate names if available
      let delegateNames = [];
      if (data.delegates && Array.isArray(data.delegates)) {
        // If delegates are passed as an array of names from QuoteModalV2
        delegateNames = data.delegates;
      } else if (selectedCell?.member?.name) {
        // If from matrix cell, use the selected member's name
        delegateNames = [selectedCell.member.name];
      } else if (
        data.selectedDelegates &&
        Array.isArray(data.selectedDelegates)
      ) {
        // If selectedDelegates contains IDs, try to map to names
        delegateNames = data.selectedDelegates
          .map(id => {
            const member = teamMembers.find(m => m.id === id);
            return member?.name || 'Unknown';
          })
          .filter(name => name !== 'Unknown');
      }

      // Get company_id from the project
      let companyId = null;
      if (projectId) {
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('company_id')
          .eq('id', projectId)
          .single();
        companyId = projectData?.company_id;
      } else {
      }

      // FIRST: Create the quote in training_quotes table
      const quoteInsertData = {
        project_id: projectId,
        company_id: companyId,
        requested_by: userProfileId,
        requested_date: new Date().toISOString(),
        course_name: courseName,
        status: 'draft', // Mark as draft - not sent to CRM yet
        delegates: data.numberOfAttendees,
        delegate_names: delegateNames,
        date_prefs:
          data.datePreference === 'specific' && data.preferredDate
            ? new Date(data.preferredDate).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })
            : data.preferredMonth || '',
        locations: data.location,
        further_info: data.notes,
        webhook_payload: webhookPayload, // Store for later sending
        requirement_id: data.requirementId || null,
        course_id: selectedCell?.requirement?.course_link_id || null
      };

      const { data: quoteData, error: quoteError } = await supabase
        .from('training_quotes')
        .insert(quoteInsertData)
        .select()
        .single();

      if (quoteError) {
        console.error('Failed to store quote:', quoteError);
        console.error('Quote insert data was:', quoteInsertData);
        throw quoteError;
      }

      // Store the created quote ID to link it to the compliance assignment
      const createdQuoteId = quoteData?.id;

      // Link quote to matrix: create compliance_assignments (tracking) and user_qualifications (status)
      if (data.requirementId) {
        const requirement = selectedCell?.requirement;
        const allDelegateIds =
          data.selectedDelegateIds?.length > 0
            ? data.selectedDelegateIds
            : data.memberId
              ? [data.memberId]
              : [];

        // Create/update compliance_assignments for all delegates (tracking + quote_id for navigation)
        if (allDelegateIds.length > 0) {
          const assignmentsToUpsert = allDelegateIds.map(delegateId => ({
            project_id: projectId,
            requirement_id: data.requirementId,
            team_member_id: delegateId,
            assigned: true,
            status: 'quoting',
            quote_id: createdQuoteId
          }));

          await supabase
            .from('compliance_assignments')
            .upsert(assignmentsToUpsert, {
              onConflict: 'project_id,requirement_id,team_member_id'
            });
        }

        // Update user_qualifications renewal_status for all delegates
        for (const delegateId of allDelegateIds) {
          const teamMember = teamMembers.find(m => m.id === delegateId);
          const userId = teamMember?.user_id || delegateId;

          if (userId) {
            const { data: existingQual } = await supabase
              .from('user_qualifications')
              .select('id')
              .eq('user_id', userId)
              .eq('requirement_id', data.requirementId)
              .maybeSingle();

            if (existingQual) {
              await supabase
                .from('user_qualifications')
                .update({
                  renewal_status: 'quoting',
                  updated_date: new Date().toISOString()
                })
                .eq('id', existingQual.id);
            } else {
              // Create new user_qualification with course_link fields for cross-space matching
              await supabase.from('user_qualifications').insert({
                user_id: userId,
                requirement_id: data.requirementId,
                course_link_id: requirement?.course_link_id || null,
                course_link_type: requirement?.course_link_type || null,
                status: null,
                renewal_status: 'quoting',
                created_date: new Date().toISOString(),
                updated_date: new Date().toISOString()
              });
            }
          }
        }
      }

      toast.success(
        'Quote draft created successfully. Go to Bookings to send it to CRM.'
      );
      await fetchData();
      setDockOpen(false);
      setBookingModalOpen(false);

      // Broadcast matrix update to all connected clients
      broadcastUpdate({ action: 'booking_created' });
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to request booking');
      throw error;
    }
  };

  const handleModalRemove = async data => {
    const targetProjectIds = data.projectIds || [projectId];

    // Optimistic: mark assignments as unassigned immediately
    const previousAssignments = assignments;
    setAssignments(prev =>
      prev.map(a =>
        a.team_member_id === data.memberId &&
        a.requirement_id === data.requirementId &&
        targetProjectIds.includes(a.project_id)
          ? { ...a, assigned: false, isAssigned: false }
          : a
      )
    );
    setDockOpen(false);

    try {
      for (const pid of targetProjectIds) {
        const { error } = await supabase
          .from('compliance_assignments')
          .update({ assigned: false })
          .eq('requirement_id', data.requirementId)
          .eq('team_member_id', data.memberId)
          .eq('project_id', pid);

        if (error) throw error;
      }

      toast.success(targetProjectIds.length > 1
        ? `Assignment removed from ${targetProjectIds.length} spaces`
        : 'Assignment removed'
      );

      // Reconcile and notify other clients
      await fetchData();
      broadcastUpdate({ action: 'assignment_removed' });
    } catch (error) {
      console.error('Remove error:', error);
      toast.error('Failed to remove assignment');
      // Rollback optimistic update
      setAssignments(previousAssignments);
      throw error;
    }
  };

  // Row view action handlers
  const handleBookTraining = (member, requirement) => {
    // Look up the full requirement object
    const fullRequirement =
      filteredRequirements.find(req => req.id === requirement.id) ||
      requirement;
    setSelectedCell({ member, requirement: fullRequirement });
    setDockOpen(false);
    setBookingModalOpen(true);
  };

  const handleUploadCertificateRow = (member, requirement) => {
    // Look up the full requirement object
    const fullRequirement =
      filteredRequirements.find(req => req.id === requirement.id) ||
      requirement;
    setSelectedCell({ member, requirement: fullRequirement });
    setDockOpen(false);
    setUploadModalOpen(true);
  };

  const handleViewDetails = (member, requirement) => {
    // Look up the full requirement object
    const fullRequirement =
      filteredRequirements.find(req => req.id === requirement.id) ||
      requirement;
    setSelectedCell({ member, requirement: fullRequirement });
    setDockOpen(false);
    setDetailsModalOpen(true);
  };

  const handleEditAssignment = (member, requirement) => {
    // Look up the full requirement object
    const fullRequirement =
      filteredRequirements.find(req => req.id === requirement.id) ||
      requirement;
    setSelectedCell({ member, requirement: fullRequirement });
    setDockOpen(false);
    setUploadModalOpen(true);
  };

  const handleAddRequirement = async (member, requirement) => {
    try {
      // Look up the full requirement object
      const fullRequirement =
        filteredRequirements.find(req => req.id === requirement.id) ||
        requirement;

      const { error } = await supabase.from('compliance_assignments').insert({
        project_id: projectId,
        requirement_id: fullRequirement.id,
        team_member_id: member.id,
        assigned: true
      });

      if (error) throw error;

      toast.success('Requirement added successfully');
      await fetchData();
      broadcastUpdate({ action: 'requirement_added' });
    } catch (error) {
      console.error('Error adding requirement:', error);
      toast.error('Failed to add requirement');
    }
  };

  // Handle booking
  const handleBooking = async () => {
    if (!selectedCell) return;

    // Validate required fields
    if (!trainingLocation) {
      toast.error('Please enter a training location');
      return;
    }

    if (datePreference === 'specific' && !preferredDate) {
      toast.error('Please select a preferred date');
      return;
    }

    if (datePreference === 'month' && !preferredMonth) {
      toast.error('Please select a preferred month');
      return;
    }

    try {
      // Prepare webhook payload
      const webhookPayload = {
        requested_date: preferredDate || new Date().toISOString(),
        course_name: selectedCell.requirement.name,
        delegates: delegatesCount,
        date_prefs:
          datePreference === 'specific'
            ? new Date(preferredDate).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })
            : preferredMonth,
        locations: trainingLocation,
        additional_details: `
          <p><strong>Requested by:</strong> ${user?.user_metadata?.first_name || ''} ${user?.user_metadata?.last_name || ''} ${user?.user_metadata?.first_name || user?.user_metadata?.last_name ? `(${user?.email})` : user?.email || 'Unknown'}</p>
          <p><strong>Delegates:</strong> ${selectedCell.member.name} (${delegatesCount} total)</p>
          <p><strong>Project:</strong> ${space?.name || 'Unknown Project'}</p>
          ${bookingNotes ? `<p><strong>Additional notes:</strong></p><p>${bookingNotes.replace(/\n/g, '<br />')}</p>` : ''}
        `.trim()
      };

      // No longer call webhook - save as draft for later sending
      let responseData = null;

      // Always save as draft without calling CRM
      // Get company ID from project
      const { data: projectData } = await supabase
        .from('projects')
        .select('company_id')
        .eq('id', projectId)
        .single();

      // Get user profile ID
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('auth_id', user?.id)
        .single();

      // Store the quote in Supabase as a draft
      const { data: insertedQuote, error: quoteError } = await supabase
        .from('training_quotes')
        .insert({
          company_id: projectData?.company_id,
          project_id: projectId,
          requested_by: userProfile?.id,
          requested_date: new Date().toISOString(),
          course_name: selectedCell.requirement.name,
          status: 'draft', // Mark as draft
          delegates: delegatesCount,
          date_prefs:
            datePreference === 'specific'
              ? new Date(preferredDate).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })
              : preferredMonth,
          locations: trainingLocation,
          further_info: bookingNotes,
          webhook_payload: webhookPayload // Store for later sending
        })
        .select()
        .single();

      if (quoteError) {
        console.error('Error storing quote:', quoteError);
        throw quoteError;
      } else {
        toast.success(
          'Quote draft created successfully! Go to Bookings to send it to CRM.'
        );
      }

      // Update assignment flag to assigned (just tracking)
      if (insertedQuote) {
        const { error: assignmentError } = await supabase
          .from('compliance_assignments')
          .upsert(
            {
              project_id: projectId,
              requirement_id: selectedCell.requirement.id,
              team_member_id: selectedCell.member.id,
              assigned: true
            },
            {
              onConflict: 'project_id,requirement_id,team_member_id'
            }
          );

        if (assignmentError) throw assignmentError;

        // SYNC RENEWAL STATUS TO USER_QUALIFICATIONS: Update renewal_status for this qualification
        const { data: existingQual } = await supabase
          .from('user_qualifications')
          .select('*')
          .eq('user_id', selectedCell.member.user_id)
          .eq('requirement_id', selectedCell.requirement.id)
          .single();

        if (existingQual) {
          // Update existing qualification with renewal status
          await supabase
            .from('user_qualifications')
            .update({
              renewal_status: 'quoting',
              updated_date: new Date().toISOString()
            })
            .eq('id', existingQual.id);
        } else {
          // Create new pending qualification with renewal status
          await supabase.from('user_qualifications').insert({
            user_id: selectedCell.member.user_id,
            requirement_id: selectedCell.requirement.id,
            status: null, // NULL = missing (no evidence yet)
            renewal_status: 'quoting',
            created_date: new Date().toISOString(),
            updated_date: new Date().toISOString()
          });
        }
      }

      // Refresh data
      await fetchData();
      setDialogOpen(false);

      // Reset booking form
      setPreferredDate('');
      setPreferredMonth('');
      setDelegatesCount(1);
      setTrainingLocation('');
      setBookingNotes('');
    } catch (error) {
      console.error('Error booking training:', error);
      toast.error('Failed to book training');
    }
  };

  // Validate certificate with AI before upload
  const validateCertificateWithAI = async (file, requirement) => {
    setValidatingUpload(true);
    const loadingToast = toast.loading('AI is validating certificate...');

    try {
      // Upload file to GCS temp storage
      let valUploadResult;
      try {
        valUploadResult = await gcsUploadFile(file, {
          folderPath: 'certificates/temp',
          fileName: `${Date.now()}_${file.name}`,
          contentType: file.type
        });
      } catch (uploadError) {
        console.error('Upload error:', uploadError);
        toast.error('Failed to upload file for validation', {
          id: loadingToast
        });
        setValidatingUpload(false);
        return { valid: false };
      }

      const tempFileName = valUploadResult.filePath;

      // Get signed URL for doc parser
      let valSignedUrl;
      try {
        valSignedUrl = await gcsGetSignedReadUrl(tempFileName);
      } catch {
        toast.error('Failed to process file', { id: loadingToast });
        setValidatingUpload(false);
        return { valid: false };
      }

      // Call doc parser webhook
      const response = await fetch(
        'https://addai-flows-backend-29522465016.europe-west2.run.app/webhook/e2a909a7-6f57-461d-83b8-b1506cf3a79a/process-certificate',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            documentUrl: valSignedUrl,
            fileName: file.name,
            uploadedAt: new Date().toISOString()
          })
        }
      );

      if (!response.ok) {
        toast.error('Failed to parse certificate', { id: loadingToast });
        setValidatingUpload(false);
        // Delete temp file
        await gcsDeleteFile(tempFileName);
        return { valid: false };
      }

      const responseData = await response.json();
      const parsed = parseAiFlowResponse(responseData);

      if (!parsed.success || !parsed.extractedData) {
        toast.warning(
          'Could not extract information from certificate. Proceeding with manual upload.',
          { id: loadingToast }
        );
        // Delete temp file
        await gcsDeleteFile(tempFileName);
        setValidatingUpload(false);
        return { valid: true, skipValidation: true };
      }

      const extractedData = parsed.extractedData;

      // Extract course titles
      const courseTitles = Array.isArray(extractedData.course_title)
        ? extractedData.course_title
        : [extractedData.course_title];

      if (!courseTitles.length) {
        toast.warning(
          'Could not find course title in certificate. Proceeding with manual upload.',
          { id: loadingToast }
        );
        // Delete temp file
        await gcsDeleteFile(tempFileName);
        setValidatingUpload(false);
        return { valid: true, skipValidation: true };
      }

      // Try to match the requirement name with extracted course titles
      const normalizeText = text =>
        text
          .toLowerCase()
          .replace(/\b(training|course|certification|certificate)\b/gi, '')
          .replace(/\s+/g, ' ')
          .trim();

      const requirementName = requirement.name.toLowerCase();
      const normalizedRequirement = normalizeText(requirementName);

      let isMatch = false;
      for (const courseTitle of courseTitles) {
        const courseLower = courseTitle.toLowerCase();
        const normalizedCourse = normalizeText(courseLower);

        // Try exact match
        if (requirementName === courseLower) {
          isMatch = true;
          break;
        }

        // Try contains match (either direction)
        if (
          requirementName.includes(courseLower) ||
          courseLower.includes(requirementName)
        ) {
          isMatch = true;
          break;
        }

        // Try normalized match
        if (
          normalizedRequirement.includes(normalizedCourse) ||
          normalizedCourse.includes(normalizedRequirement)
        ) {
          isMatch = true;
          break;
        }
      }

      // Delete temp file
      await gcsDeleteFile(tempFileName);

      if (!isMatch) {
        // Show warning - certificate doesn't match
        toast.dismiss(loadingToast);
        setValidationWarning({
          extractedTitle: courseTitles[0],
          expectedTitle: requirement.name
        });
        setValidatingUpload(false);
        return { valid: false, mismatch: true };
      }

      // Certificate matches!
      toast.success('Certificate validated successfully!', {
        id: loadingToast
      });
      setValidatingUpload(false);
      return { valid: true };
    } catch (error) {
      console.error('Validation error:', error);
      toast.error('Failed to validate certificate', { id: loadingToast });
      setValidatingUpload(false);
      return { valid: false };
    }
  };

  // Handle file upload
  const handleUploadCertificate = async (bypassValidation = false) => {
    if (!selectedCell || !uploadFile) return;

    // Validate expiry date
    if (!expiryDate) {
      toast.error('Please select an expiry date for the certificate');
      return;
    }

    // Run AI validation unless bypassed
    if (!bypassValidation) {
      const validationResult = await validateCertificateWithAI(
        uploadFile,
        selectedCell.requirement
      );

      if (!validationResult.valid && validationResult.mismatch) {
        // Validation warning is already set, user needs to confirm
        return;
      }

      if (!validationResult.valid && !validationResult.mismatch) {
        // Validation failed for technical reasons, don't proceed
        return;
      }

      // If valid or skipValidation, proceed with upload
    }

    try {
      // Upload file to GCS
      const folderPath = `certificates/${selectedCell.member.id}/${selectedCell.requirement.id}`;
      const directUploadResult = await gcsUploadFile(uploadFile, {
        folderPath,
        fileName: `${Date.now()}_${uploadFile.name}`,
        contentType: uploadFile.type
      });

      const publicUrl = directUploadResult.publicUrl;

      // Get the team member's user_id
      if (!selectedCell.member?.user_id) {
        throw new Error(
          'Cannot save qualification: team member not linked to user profile'
        );
      }

      // Use the new userQualificationsService to save qualification
      // This automatically syncs across all spaces via course_links
      const result = await userQualificationsService.upsertUserQualification({
        userId: selectedCell.member.user_id,
        requirementId: selectedCell.requirement.id,
        status: 'completed',
        completedDate: new Date().toISOString(),
        expiryDate: new Date(expiryDate).toISOString(),
        evidenceUrl: fileName, // Store the path, not publicUrl
        certificateNumber: certificateNumber || null,
        notes: `Certificate uploaded: ${uploadFile.name}`,
        assignedBy: userProfile?.id || selectedCell.member.user_id
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success(
        `Certificate uploaded for ${selectedCell.member.name} and synced across all spaces`
      );

      // Refresh data and notify other clients
      await fetchData();
      broadcastUpdate({ action: 'certificate_uploaded' });
      setDialogOpen(false);
      setUploadFile(null);
      setCertificateNumber('');
      setExpiryDate('');
    } catch (error) {
      console.error('Error uploading certificate:', error);
      toast.error('Failed to upload certificate');
    }
  };

  // Filter data by selected space (for stack view)
  const filteredRequirements = useMemo(() => {
    if (!isStackView || selectedSpaceFilter === 'all') {
      return requirements;
    }
    return requirements.filter(r =>
      r.spaces
        ? r.spaces.some(s => s.id === selectedSpaceFilter)
        : r.project_id === selectedSpaceFilter
    );
  }, [requirements, isStackView, selectedSpaceFilter]);

  const filteredTeamMembers = useMemo(() => {
    if (!isStackView || selectedSpaceFilter === 'all') {
      return teamMembers;
    }
    return teamMembers.filter(m => m.project_id === selectedSpaceFilter);
  }, [teamMembers, isStackView, selectedSpaceFilter]);

  const filteredAssignments = useMemo(() => {
    if (!isStackView || selectedSpaceFilter === 'all') {
      return assignments;
    }
    return assignments.filter(a => a.project_id === selectedSpaceFilter);
  }, [assignments, isStackView, selectedSpaceFilter]);

  // Calculate real statistics
  const calculateStats = () => {
    const totalPossible =
      filteredTeamMembers.length * filteredRequirements.length;
    const now = new Date();
    const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

    // Valid certificates
    const compliant = filteredAssignments.filter(a => {
      if (a.status === 'completed' && a.expiry_date) {
        const expiryDate = new Date(a.expiry_date);
        return expiryDate > thirtyDaysFromNow;
      }
      return a.status === 'completed' && !a.expiry_date;
    }).length;

    // Expiring certificates (within 30 days)
    const expiring = filteredAssignments.filter(a => {
      if (a.status === 'completed' && a.expiry_date) {
        const expiryDate = new Date(a.expiry_date);
        return expiryDate > now && expiryDate <= thirtyDaysFromNow;
      }
      return false;
    }).length;

    // Expired certificates
    const expired = filteredAssignments.filter(a => {
      if (a.status === 'completed' && a.expiry_date) {
        const expiryDate = new Date(a.expiry_date);
        return expiryDate < now;
      }
      return false;
    }).length;

    // Booked training
    const booked = filteredAssignments.filter(
      a => a.status === 'booked'
    ).length;

    // Quotes - assignments with 'quoting' status
    const quotes = filteredAssignments.filter(
      a => a.status === 'quoting'
    ).length;

    // Missing - assigned requirements that have never been certified (excluding unassigned)
    const missing = filteredAssignments.filter(a => {
      const isFreshAssignment =
        !a.completed_date &&
        !a.expiry_date &&
        !a.certificate_number &&
        a.status !== 'completed' &&
        a.status !== 'booked' &&
        a.status !== 'quoting'; // Don't count booked or quoting as missing
      return isFreshAssignment;
    }).length;

    const failed = filteredAssignments.filter(
      a => a.status === 'failed'
    ).length;

    return {
      totalEmployees: filteredTeamMembers.length,
      totalRequirements: filteredRequirements.length,
      compliant,
      expiring,
      expired,
      missing,
      booked,
      failed,
      quotes,
      result: 0, // Awaiting results
      cert: compliant, // Certificates uploaded
      notRenewing: 0 // Would need a specific flag for this
    };
  };

  // toggleFilter is now provided by useMatrixFilters hook

  // Handle bulk add team members to qualification
  const handleAddMembersToQualification = async () => {
    if (!selectedQualificationForAdd || selectedMembersToAdd.length === 0) {
      return;
    }

    try {
      // Filter out members who already have this assignment (double check to prevent duplicates)
      const existingAssignmentIds = new Set(
        assignments
          .filter(a => a.requirement_id === selectedQualificationForAdd.id)
          .map(a => a.team_member_id)
      );

      const membersToAdd = selectedMembersToAdd.filter(
        memberId => !existingAssignmentIds.has(memberId)
      );

      if (membersToAdd.length === 0) {
        toast.info(
          'All selected members already have this qualification assigned'
        );
        setAddMembersDialogOpen(false);
        setSelectedQualificationForAdd(null);
        setSelectedMembersToAdd([]);
        return;
      }

      // Create assignments for each selected member (just tracking flags)
      const newAssignments = membersToAdd.map(memberId => ({
        requirement_id: selectedQualificationForAdd.id,
        team_member_id: memberId,
        project_id: projectId,
        assigned: true
      }));

      const { error } = await supabase
        .from('compliance_assignments')
        .insert(newAssignments);

      if (error) throw error;

      // Refresh assignments and notify other clients
      await fetchData();
      broadcastUpdate({ action: 'members_assigned' });

      // Close dialog and reset
      setAddMembersDialogOpen(false);
      setSelectedQualificationForAdd(null);
      setSelectedMembersToAdd([]);

      toast.success(
        `Added ${membersToAdd.length} team member(s) to ${selectedQualificationForAdd.name}`
      );
    } catch (error) {
      console.error('Error adding team members:', error);

      // More specific error messages
      if (error.code === '23505') {
        toast.error('Some members already have this qualification assigned');
      } else {
        toast.error('Failed to add team members');
      }
    }
  };

  // Export matrix data to CSV
  const handleExportToCSV = () => {
    try {
      // Prepare CSV data
      const csvRows = [];

      // Header row
      const headers = [
        'Team Member',
        ...filteredMatrixData.qualifications.map(q => q.name)
      ];
      csvRows.push(headers.join(','));

      // Data rows
      filteredMatrixData.teamMembers.forEach(member => {
        const row = [member.name];

        member.requirements.forEach(req => {
          let cellValue = '';

          switch (req.status) {
            case 'valid':
              cellValue = `Valid${req.date ? ` (${req.date})` : ''}`;
              break;
            case 'expired':
              cellValue = `Expired${req.date ? ` (${req.date})` : ''}`;
              break;
            case 'soon':
              cellValue = `Expiring${req.date ? ` (${req.date})` : ''}`;
              break;
            case 'missing':
              cellValue = 'Missing';
              break;
            case 'booked':
              cellValue = `Booked${req.date ? ` (${req.date})` : ''}`;
              break;
            case 'quoting':
              cellValue = 'Quote Requested';
              break;
            case 'add':
              cellValue = 'Not Assigned';
              break;
            default:
              cellValue = req.status || 'Unknown';
          }

          // Escape commas in cell values
          row.push(`"${cellValue}"`);
        });

        csvRows.push(row.join(','));
      });

      // Create blob and download
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `compliance-matrix-${space?.name || 'export'}-${new Date().toISOString().split('T')[0]}.csv`
      );
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Matrix exported successfully');
    } catch (error) {
      console.error('Error exporting matrix:', error);
      toast.error('Failed to export matrix');
    }
  };

  // Transform real data to match the component structure - memoized to prevent recalculation
  const matrixData = useMemo(
    () => ({
      name: space?.name || 'Compliance Matrix',
      description: space?.description || '',
      stats: calculateStats(),
      qualifications: filteredRequirements.map((req, index) => ({
        id: req.id || index + 1,
        name: req.name,
        description: req.description || 'No description provided',
        // Include course_link fields for cross-space qualification matching
        course_link_id: req.course_link_id,
        course_link_type: req.course_link_type,
        project_id: req.project_id,
        requirement_type: req.requirement_type,
        // Spaces array for deduped requirements in stack/global view
        spaces: req.spaces
      })),
      teamMembers: filteredTeamMembers.map(member => {
        // For each team member, create their requirements status
        const memberRequirements = filteredRequirements.map(req => {
          // Find if this member has an assignment for this requirement (enriched).
          // requirement_id is already canonical (remapped by mergeStackData in stack view),
          // so team_member_id + requirement_id is sufficient to identify the cell.
          // A project_id guard here hid Oak House assignments in stack view when the
          // canonical requirement came from a different space (see Head Office stack bug).
          const assignment = filteredAssignments.find(
            a =>
              a.team_member_id === member.id &&
              a.requirement_id === req.id
          );

          if (!assignment) {
            // No assignment cell - but check if user has a qualification (cross-space)
            // This handles case where user is qualified from another space
            const qualificationMaps = buildQualificationMaps(qualifications);

            // Create a mock assignment object to use with findQualification
            // NOTE: findQualification uses user_id not team_member_id for lookup
            const mockAssignment = {
              team_member_id: member.user_id, // Use user_id for qualification lookup
              requirement_id: req.id
            };

            const qualification = findQualification(
              mockAssignment,
              req,
              qualificationMaps
            );

            if (qualification) {
              // User has qualification from another space!
              const effectiveStatus = calculateEffectiveStatus(qualification);

              // ONLY show cross-space qualifications if they are valid or expiring
              // Don't show expired, missing (null status), or pending qualifications
              if (
                effectiveStatus !== 'valid' &&
                effectiveStatus !== 'expired'
              ) {
                // Missing, pending, or any other status - treat as unassigned
                return {
                  id: req.id,
                  status: 'unassigned',
                  icon: Plus,
                  isAssigned: false,
                  qualifiedFromOtherSpace: false,
                  renewalStatus: null,
                  date: ''
                };
              }

              let status = 'unassigned'; // Still unassigned in this space
              let icon = Plus;
              let date = '';

              // Check if valid with expiry date
              if (effectiveStatus === 'valid' && qualification.expiry_date) {
                const expiryDate = new Date(qualification.expiry_date);
                const now = new Date();
                const thirtyDaysFromNow = new Date(
                  Date.now() + 30 * 24 * 60 * 60 * 1000
                );

                if (expiryDate < now) {
                  // Expired - don't show as cross-space qualification
                  return {
                    id: req.id,
                    status: 'unassigned',
                    icon: Plus,
                    isAssigned: false,
                    qualifiedFromOtherSpace: false,
                    renewalStatus: null,
                    date: ''
                  };
                } else if (expiryDate <= thirtyDaysFromNow) {
                  // Expiring soon - show it
                  status = 'soon';
                  icon = AlertTriangle;
                  date = expiryDate.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit'
                  });
                } else {
                  // Valid - show it
                  status = 'valid';
                  icon = CheckCircle;
                  date = expiryDate.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit'
                  });
                }
              } else if (effectiveStatus === 'valid') {
                // Valid without expiry date - show it
                status = 'valid';
                icon = CheckCircle;
                date = '';
              } else {
                // Expired - don't show as cross-space qualification
                return {
                  id: req.id,
                  status: 'unassigned',
                  icon: Plus,
                  isAssigned: false,
                  qualifiedFromOtherSpace: false,
                  renewalStatus: null,
                  date: ''
                };
              }

              // Only return cross-space qualification if status is valid or soon
              return {
                id: req.id,
                status,
                date,
                icon,
                isAssigned: false, // Not assigned in this space
                qualifiedFromOtherSpace: true, // Cross-space qualification
                renewalStatus: qualification.renewal_status || null
              };
            }

            // No assignment AND no qualification - truly unassigned
            return {
              id: req.id,
              status: 'unassigned',
              icon: Plus,
              isAssigned: false,
              qualifiedFromOtherSpace: false,
              renewalStatus: null,
              date: ''
            };
          }

          // Determine status using enriched assignment data
          let status = 'missing';
          let icon = XCircle;
          let date = '';
          let renewalStatus = null;

          // Get qualification if exists (single source of truth)
          const qual = assignment.qualification;

          // Renewal status comes from compliance_assignments.status (exposed as renewal_status by v4 RPC)
          renewalStatus =
            assignment.renewalStatus || assignment.renewal_status || null;

          if (
            assignment.hasQualification &&
            assignment.effectiveStatus !== 'missing'
          ) {
            // Has a COMPLETED qualification - determine current status from qualification data
            // Note: effectiveStatus='missing' means qualification exists but isn't completed (status=null)
            // Check expiry status if qualification is completed and has expiry date
            if (assignment.effectiveStatus === 'valid' && qual.expiry_date) {
              const expiryDate = new Date(qual.expiry_date);
              const now = new Date();
              const thirtyDaysFromNow = new Date(
                Date.now() + 30 * 24 * 60 * 60 * 1000
              );

              if (expiryDate < now) {
                status = 'expired';
                icon = AlertTriangle;
                date = expiryDate.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: '2-digit'
                });
              } else if (expiryDate <= thirtyDaysFromNow) {
                status = 'soon';
                icon = AlertTriangle;
                date = expiryDate.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: '2-digit'
                });
              } else {
                status = 'valid';
                icon = CheckCircle;
                date = expiryDate.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: '2-digit'
                });
              }
            } else if (assignment.effectiveStatus === 'valid') {
              // Valid but no expiry date
              status = 'valid';
              icon = CheckCircle;
              date = '';
            } else if (assignment.effectiveStatus === 'expiring') {
              // v4 returns 'expiring' directly for items within 30 days
              status = 'soon';
              icon = AlertTriangle;
              date = qual?.expiry_date
                ? new Date(qual.expiry_date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit'
                  })
                : '';
            } else if (assignment.effectiveStatus === 'expired') {
              status = 'expired';
              icon = AlertTriangle;
              date = qual?.expiry_date
                ? new Date(qual.expiry_date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit'
                  })
                : '';
            } else if (
              assignment.effectiveStatus === 'pending' ||
              assignment.effectiveStatus === 'missing'
            ) {
              status = 'missing';
              icon = Clock;
              date = '';
            }
          } else {
            // No qualification in current space
            // Check if there's a qualification from another space (cross-space)
            if (assignment.qualifiedFromOtherSpace && qual) {
              // Has cross-space qualification - check if it's valid or expiring
              const crossSpaceStatus = assignment.effectiveStatus;

              if (
                crossSpaceStatus === 'valid' ||
                crossSpaceStatus === 'expiring' ||
                crossSpaceStatus === 'expired'
              ) {
                // Check expiry to determine if we should show it
                if (qual.expiry_date) {
                  const expiryDate = new Date(qual.expiry_date);
                  const now = new Date();
                  const thirtyDaysFromNow = new Date(
                    Date.now() + 30 * 24 * 60 * 60 * 1000
                  );

                  if (expiryDate < now) {
                    // Expired - don't show cross-space qual, treat as unassigned or missing
                    if (assignment.isAssigned) {
                      status = 'missing';
                      icon = XCircle;
                    } else {
                      status = 'unassigned';
                      icon = Plus;
                    }
                    date = '';
                    // Don't preserve qualifiedFromOtherSpace
                    return {
                      id: req.id,
                      status,
                      date,
                      icon,
                      isAssigned: assignment.isAssigned,
                      qualifiedFromOtherSpace: false,
                      renewalStatus
                    };
                  } else if (expiryDate <= thirtyDaysFromNow) {
                    // Expiring soon - show it!
                    status = 'soon';
                    icon = AlertTriangle;
                    date = expiryDate.toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit'
                    });
                  } else {
                    // Valid - show it!
                    status = 'valid';
                    icon = CheckCircle;
                    date = expiryDate.toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit'
                    });
                  }
                } else if (crossSpaceStatus === 'valid') {
                  // Valid with no expiry - show it
                  status = 'valid';
                  icon = CheckCircle;
                  date = '';
                } else {
                  // Expired - don't show
                  if (assignment.isAssigned) {
                    status = 'missing';
                    icon = XCircle;
                  } else {
                    status = 'unassigned';
                    icon = Plus;
                  }
                  date = '';
                  return {
                    id: req.id,
                    status,
                    date,
                    icon,
                    isAssigned: assignment.isAssigned,
                    qualifiedFromOtherSpace: false,
                    renewalStatus
                  };
                }
              } else {
                // Not valid or expired (missing, pending, etc) - don't show cross-space
                if (assignment.isAssigned) {
                  status = 'missing';
                  icon = XCircle;
                } else {
                  status = 'unassigned';
                  icon = Plus;
                }
                date = '';
                return {
                  id: req.id,
                  status,
                  date,
                  icon,
                  isAssigned: assignment.isAssigned,
                  qualifiedFromOtherSpace: false,
                  renewalStatus
                };
              }
            } else {
              // No qualification at all
              // If unassigned, show as 'unassigned' (Plus icon, "Click to assign")
              // Only show 'missing' if it's assigned (required)
              if (assignment.isAssigned) {
                status = 'missing';
                icon = XCircle;
              } else {
                status = 'unassigned';
                icon = Plus;
              }
              date = '';
            }
          }

          return {
            id: req.id,
            status,
            date,
            icon,
            isAssigned: assignment.isAssigned,
            // Show cross-space indicator for both assigned and unassigned cells when qualification is from another space
            qualifiedFromOtherSpace:
              assignment.qualifiedFromOtherSpace &&
              (status === 'valid' || status === 'soon'),
            renewalStatus,
            isOverridden: !!(qual?.override_reason),
            overrideInfo: qual?.override_reason ? {
              reason: qual.override_reason,
              by: qual.override_by_name || null,
              date: qual.override_date
            } : null,
            assignment: assignment // Include full assignment object for access to assignment.status, quote_id, etc
          };
        });

        return {
          ...member, // Preserve all member fields including user_id
          requirements: memberRequirements
        };
      })
    }),
    [
      space,
      filteredRequirements,
      filteredTeamMembers,
      filteredAssignments,
      qualifications
    ]
  );

  // Sync column and row order when data changes
  // - First load: initialize from data
  // - Subsequent loads: append new items, remove stale ones, preserve user ordering
  useEffect(() => {
    if (matrixData?.qualifications) {
      const dataIds = matrixData.qualifications.map(q => q.id);
      if (columnOrder.length === 0) {
        setColumnOrder(dataIds);
      } else {
        const dataIdSet = new Set(dataIds);
        const existingIdSet = new Set(columnOrder);
        const newIds = dataIds.filter(id => !existingIdSet.has(id));
        const staleRemoved = columnOrder.filter(id => dataIdSet.has(id));
        if (newIds.length > 0 || staleRemoved.length !== columnOrder.length) {
          setColumnOrder([...staleRemoved, ...newIds]);
        }
      }
    }
    if (matrixData?.teamMembers) {
      const dataIds = matrixData.teamMembers.map(m => m.id);
      if (rowOrder.length === 0) {
        setRowOrder(dataIds);
      } else {
        const dataIdSet = new Set(dataIds);
        const existingIdSet = new Set(rowOrder);
        const newIds = dataIds.filter(id => !existingIdSet.has(id));
        const staleRemoved = rowOrder.filter(id => dataIdSet.has(id));
        if (newIds.length > 0 || staleRemoved.length !== rowOrder.length) {
          setRowOrder([...staleRemoved, ...newIds]);
        }
      }
    }
  }, [matrixData]);

  // Check if tab scroll arrows should be shown
  useEffect(() => {
    const checkTabScroll = () => {
      if (tabScrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = tabScrollRef.current;
        setShowTabScrollLeft(scrollLeft > 5);
        setShowTabScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
      }
    };

    checkTabScroll();
    const handleResize = () => checkTabScroll();
    window.addEventListener('resize', handleResize);

    // Check after short delays to ensure layout is complete
    const timeout1 = setTimeout(checkTabScroll, 100);
    const timeout2 = setTimeout(checkTabScroll, 300);
    const timeout3 = setTimeout(checkTabScroll, 500);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
    };
  }, [views]);

  // Filter matrix data based on active filters - memoized
  const filteredMatrixData = useMemo(() => {
    let filteredMembers = matrixData.teamMembers;
    let filteredQualifications = matrixData.qualifications;

    // Apply view-based filtering by requirement_type
    // Site view: show only documentation and internal_checklist
    // Other views (grid, qualifications, employees): show only courses (null or 'course')
    if (viewMode === 'site') {
      filteredQualifications = filteredQualifications.filter(
        q =>
          q.requirement_type === 'documentation' ||
          q.requirement_type === 'internal_checklist'
      );
    } else {
      // Grid, qualifications, employees views - show only actual qualifications/courses
      filteredQualifications = filteredQualifications.filter(
        q =>
          q.requirement_type !== 'documentation' &&
          q.requirement_type !== 'internal_checklist'
      );
    }

    // Apply employee & qualification search filter (smart token-based)
    if (employeeSearchTerm.trim()) {
      const searchTokens = employeeSearchTerm.toLowerCase().trim().split(/\s+/);

      // Check which tokens match member names
      const memberMatchingTokens = searchTokens.filter(token => {
        return matrixData.teamMembers.some(m =>
          m.name.toLowerCase().includes(token)
        );
      });

      // Check which tokens match qualification names
      const qualMatchingTokens = searchTokens.filter(token => {
        return matrixData.qualifications.some(
          q =>
            q.name.toLowerCase().includes(token) ||
            (q.description || '').toLowerCase().includes(token)
        );
      });

      // Filter members by member-matching tokens only
      if (memberMatchingTokens.length > 0) {
        filteredMembers = filteredMembers.filter(member => {
          const memberNameLower = member.name.toLowerCase();
          return memberMatchingTokens.some(token =>
            memberNameLower.includes(token)
          );
        });
      }

      // Content-aware filtering logic:
      // If ONLY employee names matched: show all their assigned qualifications
      // If ONLY qualification names matched: show those qualifications for all employees
      // If BOTH matched: show intersection (employee's quals that match the qual name)

      if (memberMatchingTokens.length > 0) {
        // Employee names were searched - collect their assigned qualifications
        const assignedQualIds = new Set();
        filteredMembers.forEach(member => {
          member.requirements.forEach(req => {
            if (req.status !== 'add') {
              assignedQualIds.add(req.id);
            }
          });
        });

        // If qualification names were also searched, intersect both filters
        if (qualMatchingTokens.length > 0) {
          // Show qualifications that: (1) match qual name AND (2) are assigned to employee
          filteredQualifications = filteredQualifications.filter(qual => {
            const qualNameLower = qual.name.toLowerCase();
            const qualDescLower = (qual.description || '').toLowerCase();
            const matchesQualName = qualMatchingTokens.some(
              token =>
                qualNameLower.includes(token) || qualDescLower.includes(token)
            );
            return matchesQualName && assignedQualIds.has(qual.id);
          });
        } else {
          // Only employee names searched - show all their assigned qualifications
          filteredQualifications = filteredQualifications.filter(qual =>
            assignedQualIds.has(qual.id)
          );
        }
      } else if (qualMatchingTokens.length > 0) {
        // Only qualification names searched (no employees) - filter by qual name only
        filteredQualifications = filteredQualifications.filter(qual => {
          const qualNameLower = qual.name.toLowerCase();
          const qualDescLower = (qual.description || '').toLowerCase();
          return qualMatchingTokens.some(
            token =>
              qualNameLower.includes(token) || qualDescLower.includes(token)
          );
        });
      }
    }

    // Apply status filters if any
    if (activeFilters.size === 0) {
      // Even without status filters, we need to filter member requirements based on search
      const filteredQualIds = new Set(filteredQualifications.map(q => q.id));
      const updatedMembers = filteredMembers.map(member => ({
        ...member,
        requirements: member.requirements.filter(req =>
          filteredQualIds.has(req.id)
        )
      }));

      return {
        ...matrixData,
        teamMembers: updatedMembers,
        qualifications: filteredQualifications
      };
    }

    // Separate status filters from assignment filters
    const statusFilters = Array.from(activeFilters).filter(
      f => !['assigned', 'unassigned'].includes(f)
    );
    const assignmentFilters = Array.from(activeFilters).filter(f =>
      ['assigned', 'unassigned'].includes(f)
    );

    // Map filter types to status values
    const filterToStatus = {
      compliant: 'valid',
      valid: 'valid',
      expiring: 'soon',
      soon: 'soon',
      expired: 'expired',
      missing: 'missing',
      booked: 'booked',
      quotes: 'quoting'
    };

    // Get the statuses we want to show
    const statusesToShow = statusFilters.map(filter => filterToStatus[filter]);

    // Filter team members - only show those who have at least one requirement matching the filters
    filteredMembers = filteredMembers.filter(member => {
      return member.requirements.some(req => {
        // Check status filter (if any)
        const matchesStatus =
          statusesToShow.length === 0 || statusesToShow.includes(req.status);

        // Check assignment filter (if any)
        let matchesAssignment = true;
        if (assignmentFilters.length > 0) {
          matchesAssignment = assignmentFilters.some(filter => {
            if (filter === 'assigned') return req.isAssigned === true;
            if (filter === 'unassigned') return req.isAssigned === false;
            return false;
          });
        }

        return matchesStatus && matchesAssignment;
      });
    });

    // Filter qualifications by status and assignment - only show those that have at least one member matching the filter
    const qualificationIdsToShow = new Set();
    filteredMembers.forEach(member => {
      member.requirements.forEach(req => {
        const matchesStatus =
          statusesToShow.length === 0 || statusesToShow.includes(req.status);

        let matchesAssignment = true;
        if (assignmentFilters.length > 0) {
          matchesAssignment = assignmentFilters.some(filter => {
            if (filter === 'assigned') return req.isAssigned === true;
            if (filter === 'unassigned') return req.isAssigned === false;
            return false;
          });
        }

        if (matchesStatus && matchesAssignment) {
          qualificationIdsToShow.add(req.id);
        }
      });
    });

    const statusFilteredQualifications = filteredQualifications.filter(qual =>
      qualificationIdsToShow.has(qual.id)
    );

    // Create a set of filtered qualification IDs for easy lookup
    const filteredQualIds = new Set(
      statusFilteredQualifications.map(q => q.id)
    );

    // Update member requirements to only show filtered qualifications
    const updatedMembers = filteredMembers.map(member => ({
      ...member,
      requirements: member.requirements.filter(req =>
        filteredQualIds.has(req.id)
      )
    }));

    // Filter out hidden columns and rows
    const visibleQualifications = statusFilteredQualifications.filter(
      qual => !hiddenColumns.has(qual.id)
    );

    const visibleQualIds = new Set(visibleQualifications.map(q => q.id));

    // Filter out hidden team members
    const visibleMembers = updatedMembers.filter(
      member => !hiddenRows.has(member.id)
    );

    // Update members to only include visible qualifications
    const finalMembers = visibleMembers.map(member => ({
      ...member,
      requirements: member.requirements.filter(req =>
        visibleQualIds.has(req.id)
      )
    }));

    // Apply column ordering (preserving any new items not yet in the saved order)
    let orderedQualifications = visibleQualifications;
    if (columnOrder.length > 0) {
      const ordered = columnOrder
        .map(id => visibleQualifications.find(q => q.id === id))
        .filter(Boolean);
      const orderedIds = new Set(columnOrder);
      const unordered = visibleQualifications.filter(q => !orderedIds.has(q.id));
      orderedQualifications = [...ordered, ...unordered];
    }

    // Apply row ordering (preserving any new items not yet in the saved order)
    let orderedMembers = finalMembers;
    if (rowOrder.length > 0) {
      const ordered = rowOrder
        .map(id => finalMembers.find(m => m.id === id))
        .filter(Boolean);
      const orderedIds = new Set(rowOrder);
      const unordered = finalMembers.filter(m => !orderedIds.has(m.id));
      orderedMembers = [...ordered, ...unordered];
    }

    return {
      ...matrixData,
      qualifications: orderedQualifications,
      teamMembers: orderedMembers
    };
  }, [
    matrixData,
    activeFilters,
    employeeSearchTerm,
    hiddenColumns,
    hiddenRows,
    columnOrder,
    rowOrder,
    viewMode
  ]);

  // Complete ordered ID lists for the hide fields popover
  // Merges columnOrder/rowOrder with any new items not yet in the saved order
  const fullColumnOrder = useMemo(() => {
    if (!matrixData?.qualifications) return columnOrder;
    const allIds = matrixData.qualifications.map(q => q.id);
    if (columnOrder.length === 0) return allIds;
    const orderedSet = new Set(columnOrder);
    const newIds = allIds.filter(id => !orderedSet.has(id));
    return [...columnOrder, ...newIds];
  }, [columnOrder, matrixData?.qualifications]);

  const fullRowOrder = useMemo(() => {
    if (!matrixData?.teamMembers) return rowOrder;
    const allIds = matrixData.teamMembers.map(m => m.id);
    if (rowOrder.length === 0) return allIds;
    const orderedSet = new Set(rowOrder);
    const newIds = allIds.filter(id => !orderedSet.has(id));
    return [...rowOrder, ...newIds];
  }, [rowOrder, matrixData?.teamMembers]);

  // Check if current view has unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    // If we're on the default "All" view, check if any filters or hidden fields are applied
    if (activeViewId === 'all') {
      return (
        activeFilters.size > 0 || hiddenColumns.size > 0 || hiddenRows.size > 0
      );
    }

    // If we're on a view that we don't own (public view created by someone else),
    // show "Save as new view" button if filters have changed
    const currentView = views.find(v => v.id === activeViewId);
    if (currentView && currentView.userId !== userProfile?.id) {
      // Check if current filters differ from the saved view's filters
      const savedFilters = currentView.filters || {};
      const filtersChanged =
        JSON.stringify(Array.from(activeFilters).sort()) !==
          JSON.stringify((savedFilters.activeFilters || []).sort()) ||
        JSON.stringify(Array.from(hiddenColumns).sort()) !==
          JSON.stringify((savedFilters.hiddenColumns || []).sort()) ||
        JSON.stringify(Array.from(hiddenRows).sort()) !==
          JSON.stringify((savedFilters.hiddenRows || []).sort());
      return filtersChanged;
    }

    return false;
  }, [
    activeViewId,
    activeFilters,
    hiddenColumns,
    hiddenRows,
    views,
    userProfile?.id
  ]);

  // Memoize employee stats to prevent recalculation on every render
  // IMPORTANT: Use original matrixData (not filteredMatrixData) so stats always show overall totals
  // regardless of hidden columns or status filters
  const employeeStatsMap = useMemo(() => {
    const statsMap = new Map();

    matrixData.teamMembers.forEach(member => {
      let totalAssigned = 0;
      let valid = 0;
      let expiring = 0;
      let expired = 0;
      let missing = 0;
      let booked = 0;
      let quoting = 0;

      member.requirements.forEach(req => {
        // Only count if actually assigned to this employee
        if (req.isAssigned === true) {
          totalAssigned++;
          if (req.status === 'valid') valid++;
          else if (req.status === 'soon') expiring++;
          else if (req.status === 'expired') expired++;
          else if (req.status === 'missing') missing++;
          else if (req.status === 'booked') booked++;
          else if (req.status === 'quoting') quoting++;
        }
      });

      const compliancePercentage =
        totalAssigned > 0
          ? Math.min(
              100,
              Math.round(((valid + expiring) / totalAssigned) * 100)
            )
          : 0;

      statsMap.set(member.id, {
        totalAssigned,
        valid,
        expiring,
        expired,
        missing,
        booked,
        quoting,
        compliancePercentage
      });
    });

    return statsMap;
  }, [matrixData]);

  // SINGLE SOURCE OF TRUTH: Use RPC data for qualification stats
  // This ensures consistency with Space cards, ToolPreview, and all other compliance displays
  const qualStatsMap = useMemo(() => {
    const statsMap = new Map();

    // If we have RPC data, use it as the source of truth
    if (complianceStatsData?.by_requirement) {
      complianceStatsData.by_requirement.forEach(reqStats => {
        statsMap.set(reqStats.requirement_id, {
          totalAssigned: reqStats.assigned_count || 0,
          valid: reqStats.valid || 0,
          validCrossSpace: reqStats.valid_cross_space || 0,
          expiring: reqStats.expiring || 0,
          expiringCrossSpace: reqStats.expiring_cross_space || 0,
          expired: reqStats.expired || 0,
          missing: reqStats.missing || 0,
          booked: reqStats.booked || 0,
          quoting: reqStats.quoting || 0,
          compliancePercentage:
            reqStats.compliance_percentage !== null
              ? Math.round(reqStats.compliance_percentage)
              : 0,
          required: reqStats.required || 0,
          isDocumentRequirement: reqStats.is_document_requirement || false
        });
      });
    }

    return statsMap;
  }, [complianceStatsData]);

  // SINGLE SOURCE OF TRUTH: Use RPC data for sidebar stats
  const sidebarStats = useMemo(() => {
    // Use RPC data as source of truth
    if (complianceStatsData?.overall) {
      const overall = complianceStatsData.overall;
      return {
        compliant: overall.valid || 0,
        compliantCrossSpace: overall.valid_cross_space || 0,
        expiring: overall.expiring || 0,
        expiringCrossSpace: overall.expiring_cross_space || 0,
        expired: overall.expired || 0,
        missing: overall.missing || 0,
        booked: overall.booked || 0,
        quotes: overall.quoting || 0,
        totalRequired:
          complianceStatsData.by_requirement?.reduce(
            (sum, r) => sum + (r.required || 0),
            0
          ) || 0,
        totalAssigned:
          complianceStatsData.by_requirement?.reduce(
            (sum, r) => sum + (r.assigned_count || 0),
            0
          ) || 0,
        totalRequirements: overall.total_requirements || 0,
        totalEmployees: overall.total_team_members || 0,
        overallCompliancePercentage:
          overall.compliance_percentage !== null
            ? Math.round(overall.compliance_percentage)
            : 0
      };
    }

    // Fallback to empty stats if no RPC data
    return {
      compliant: 0,
      compliantCrossSpace: 0,
      expiring: 0,
      expiringCrossSpace: 0,
      expired: 0,
      missing: 0,
      booked: 0,
      quotes: 0,
      totalRequired: 0,
      totalAssigned: 0,
      totalRequirements: matrixData.qualifications.length,
      totalEmployees: matrixData.teamMembers.length,
      overallCompliancePercentage: 0
    };
  }, [complianceStatsData, matrixData]);

  // SINGLE SOURCE OF TRUTH: Use RPC data for qualifications with stats (for rows view)
  const qualificationsWithStats = useMemo(() => {
    return filteredMatrixData.qualifications.map(qualification => {
      // Get stats from RPC data (single source of truth)
      const rpcStats = complianceStatsData?.by_requirement?.find(
        r => r.requirement_id === qualification.id
      );
      const requirementData = filteredRequirements.find(
        r => r.id === qualification.id
      );

      // Build qualStats from RPC data
      const qualStats = {
        required: rpcStats?.required || 0,
        valid: rpcStats?.valid || 0,
        validCrossSpace: rpcStats?.valid_cross_space || 0,
        expiring: rpcStats?.expiring || 0,
        expiringCrossSpace: rpcStats?.expiring_cross_space || 0,
        expired: rpcStats?.expired || 0,
        missing: rpcStats?.missing || 0,
        booked: rpcStats?.booked || 0,
        quoting: rpcStats?.quoting || 0,
        gap: 0,
        assigned: rpcStats?.assigned_count || 0,
        isDocumentRequirement: rpcStats?.is_document_requirement || false
      };

      // Calculate gap from RPC data
      const totalValid = qualStats.valid + qualStats.validCrossSpace;
      qualStats.gap = qualStats.required - totalValid;

      // Get compliance percentage from RPC
      const compliancePercentage =
        rpcStats?.compliance_percentage != null
          ? Math.round(rpcStats.compliance_percentage)
          : 0;

      return {
        ...qualification,
        qualStats,
        compliancePercentage,
        requirementData
      };
    });
  }, [
    filteredMatrixData.qualifications,
    complianceStatsData,
    filteredRequirements
  ]);

  // SINGLE SOURCE OF TRUTH: Use RPC data for overall compliance rate
  const overallComplianceRate = useMemo(() => {
    if (
      complianceStatsData?.overall?.compliance_percentage !== null &&
      complianceStatsData?.overall?.compliance_percentage !== undefined
    ) {
      return Math.round(complianceStatsData.overall.compliance_percentage);
    }
    return 0;
  }, [complianceStatsData]);

  // Calculate projected compliance rate (includes booked training) using AVERAGE method
  const projectedComplianceRate = useMemo(() => {
    if (qualificationsWithStats.length === 0) return 0;

    let totalProjectedPercentage = 0;
    let qualificationCount = 0;

    qualificationsWithStats.forEach(qual => {
      // Skip document/checklist requirements from compliance calculation
      if (qual.qualStats.isDocumentRequirement) return;

      // Individual requirement projected compliance: (valid + expiring + booked) / required
      const totalValid = qual.qualStats.valid + qual.qualStats.validCrossSpace;
      const totalExpiring =
        qual.qualStats.expiring + qual.qualStats.expiringCrossSpace;
      const projectedCompliantCount =
        totalValid + totalExpiring + qual.qualStats.booked;

      let requirementProjected =
        qual.qualStats.required > 0
          ? Math.round(
              (projectedCompliantCount / qual.qualStats.required) * 100
            )
          : 0;

      // Cap at 100%
      requirementProjected = Math.min(requirementProjected, 100);

      totalProjectedPercentage += requirementProjected;
      qualificationCount++;
    });

    // Projected compliance = average of all requirement projected percentages
    return qualificationCount > 0
      ? Math.round(totalProjectedPercentage / qualificationCount)
      : 0;
  }, [qualificationsWithStats]);

  // Memoized filtered and sorted qualifications for rows view to prevent lag
  const filteredQualificationsForRows = useMemo(() => {
    let filtered = qualificationsWithStats;

    // Apply search filter for qualifications
    if (employeeSearchTerm.trim()) {
      // Split search term into tokens (e.g., "COSHH Ben" -> ["COSHH", "Ben"])
      const searchTokens = employeeSearchTerm.toLowerCase().trim().split(/\s+/);

      // Check which tokens match qualifications
      const qualMatchingTokens = searchTokens.filter(token => {
        return qualificationsWithStats.some(
          qual =>
            qual.name.toLowerCase().includes(token) ||
            (qual.description || '').toLowerCase().includes(token)
        );
      });

      // Only filter qualifications if at least one token matches a qualification
      if (qualMatchingTokens.length > 0) {
        filtered = filtered.filter(qualification => {
          const qualNameLower = qualification.name.toLowerCase();
          const qualDescLower = (qualification.description || '').toLowerCase();

          // Check if any matching token matches this qualification
          return qualMatchingTokens.some(
            token =>
              qualNameLower.includes(token) || qualDescLower.includes(token)
          );
        });
      }
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (qualificationsSortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'compliance-asc':
          return a.compliancePercentage - b.compliancePercentage;
        case 'compliance-desc':
          return b.compliancePercentage - a.compliancePercentage;
        case 'gap-asc':
          return a.qualStats.gap - b.qualStats.gap;
        case 'gap-desc':
          return b.qualStats.gap - a.qualStats.gap;
        case 'missing-asc':
          return a.qualStats.missing - b.qualStats.missing;
        case 'missing-desc':
          return b.qualStats.missing - a.qualStats.missing;
        default:
          return 0;
      }
    });

    return sorted;
  }, [qualificationsWithStats, employeeSearchTerm, qualificationsSortBy]);

  // Get color based on status
  const getStatusColor = status => {
    switch (status) {
      case 'valid':
        return 'bg-green-100 hover:bg-green-200';
      case 'expired':
        return 'bg-red-100 hover:bg-red-200';
      case 'soon':
        return 'bg-yellow-100 hover:bg-yellow-200';
      case 'missing':
        return 'bg-red-100 hover:bg-red-200';
      case 'failed':
        return 'bg-red-100 hover:bg-red-200';
      case 'booked':
        return 'bg-blue-100 hover:bg-blue-200';
      case 'quoting':
        return 'bg-purple-100 hover:bg-purple-200';
      case 'overdue':
        return 'bg-orange-100 hover:bg-orange-200';
      case 'add':
        return 'bg-gray-50 hover:bg-gray-100';
      default:
        return 'bg-gray-100';
    }
  };

  // Normalize assignment for dock display - flatten qualification data onto assignment
  const normalizeAssignmentForDock = assignment => {
    if (!assignment) return assignment;
    const qual = assignment.qualification;
    return {
      ...assignment,
      // Flatten qualification fields so dock code can access them directly
      evidence_url:
        qual?.evidence_url ||
        assignment.assignment_evidence_url ||
        assignment.assignmentEvidenceUrl ||
        assignment.evidence_url,
      certificate_number:
        qual?.certificate_number ||
        assignment.assignment_certificate_number ||
        assignment.assignmentCertificateNumber ||
        assignment.certificate_number,
      completed_date: qual?.completed_date || assignment.completed_date,
      expiry_date: qual?.expiry_date || assignment.expiry_date
    };
  };

  // Get status label
  const getStatusLabel = status => {
    switch (status) {
      case 'valid':
        return 'Valid';
      case 'expired':
        return 'Expired';
      case 'soon':
        return 'Expiring';
      case 'missing':
        return 'Missing';
      case 'unassigned':
        return 'Click to assign';
      case 'failed':
        return 'Failed';
      case 'booked':
        return 'Booked';
      case 'quoting':
        return 'Quoting';
      case 'overdue':
        return 'Overdue';
      default:
        return '';
    }
  };

  // Get status date text
  const getStatusDateText = (status, date) => {
    if (!date) return '';
    switch (status) {
      case 'valid':
        return `until ${date}`;
      case 'expired':
        return `on ${date}`;
      case 'soon':
        return `on ${date}`;
      case 'failed':
        return `on ${date}`;
      case 'booked':
        return `for ${date}`;
      case 'overdue':
        return `since ${date}`;
      default:
        return '';
    }
  };

  // Get status config (combines icon, label, color for rows view)
  const getStatusConfig = status => {
    switch (status) {
      case 'valid':
        return {
          icon: CheckCircle,
          label: 'Valid',
          dotColor: 'bg-green-500'
        };
      case 'expired':
        return {
          icon: X,
          label: 'Expired',
          dotColor: 'bg-red-500'
        };
      case 'soon':
        return {
          icon: Clock,
          label: 'Expiring Soon',
          dotColor: 'bg-yellow-500'
        };
      case 'missing':
        return {
          icon: AlertCircle,
          label: 'Missing',
          dotColor: 'bg-red-500'
        };
      case 'unassigned':
        return {
          icon: Plus,
          label: 'Click to assign',
          dotColor: 'bg-gray-400'
        };
      case 'booked':
        return {
          icon: Calendar,
          label: 'Booked',
          dotColor: 'bg-primary'
        };
      case 'failed':
        return {
          icon: X,
          label: 'Failed',
          dotColor: 'bg-red-500'
        };
      case 'quoting':
        return {
          icon: FileText,
          label: 'Quoting',
          dotColor: 'bg-primary'
        };
      default:
        return {
          icon: Plus,
          label: 'Add',
          dotColor: 'bg-gray-400'
        };
    }
  };

  // Get smart context actions based on status
  const getContextActions = status => {
    switch (status) {
      case 'missing':
        return [
          {
            label: 'Request Quote',
            icon: FileText,
            variant: 'default',
            action: 'request_quote'
          }
        ];
      case 'expired':
      case 'soon':
        return [
          {
            label: 'Upload Certificate',
            icon: Upload,
            variant: 'default',
            action: 'upload_certificate'
          },
          {
            label: 'Book Renewal',
            icon: Calendar,
            variant: 'outline',
            action: 'book_renewal'
          }
        ];
      case 'valid':
        return [
          {
            label: 'View Certificate',
            icon: FileText,
            variant: 'ghost',
            action: 'view_certificate'
          }
        ];
      case 'booked':
        return [
          {
            label: 'View Booking',
            icon: Calendar,
            variant: 'outline',
            action: 'view_booking'
          }
        ];
      case 'quoting':
        return [
          {
            label: 'View Quote',
            icon: FileText,
            variant: 'outline',
            action: 'view_quote'
          }
        ];
      case 'failed':
        return [
          {
            label: 'Retry',
            icon: RotateCcw,
            variant: 'default',
            action: 'retry'
          }
        ];
      default:
        return [
          { label: 'Update', icon: Plus, variant: 'ghost', action: 'update' }
        ];
    }
  };

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <LoadingSpinner />
      </div>
    );
  }

  const matrixContent = (
    <>
      {/* Main Matrix Content - Full Screen */}
      <div
        className='bg-white w-full h-full overflow-x-auto overflow-y-auto relative'
        style={{
          overflowX: hideFieldsOpen ? 'hidden' : 'auto',
          overflowY: hideFieldsOpen ? 'hidden' : 'auto'
        }}
      >
        {/* Highlight Controls Bar - Fixed at Bottom */}
        {(highlightedRows.size > 0 || highlightedColumns.size > 0) && (
          <div className='fixed bottom-0 left-0 right-0 z-50 bg-blue-50 border-t border-blue-200 px-4 py-2 flex items-center justify-between shadow-lg'>
            <div className='flex items-center gap-3 text-sm'>
              <div className='flex items-center gap-2'>
                <div className='w-2 h-2 rounded-full bg-blue-500'></div>
                <span className='text-gray-700 font-medium'>
                  {highlightedRows.size + highlightedColumns.size} selected
                </span>
              </div>
              {highlightedRows.size > 0 && (
                <span className='text-gray-500'>
                  {highlightedRows.size} row
                  {highlightedRows.size !== 1 ? 's' : ''}
                </span>
              )}
              {highlightedColumns.size > 0 && (
                <span className='text-gray-500'>
                  {highlightedColumns.size} column
                  {highlightedColumns.size !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className='flex items-center gap-2'>
              <span className='text-xs text-gray-500'>Press ESC to clear</span>
              <Button
                size='sm'
                variant='outline'
                onClick={clearAllHighlights}
                className='h-7 text-xs'
              >
                <X className='h-3 w-3 mr-1' />
                Clear All
              </Button>
            </div>
          </div>
        )}

        {/* View Tabs - Filing Cabinet Style */}
        <div className='sticky top-0 left-0 z-40 bg-gray-100 pt-1.5 px-4 pb-0'>
          <div className='flex items-end gap-4 relative h-[40px]'>
            {/* Border line - positioned absolutely below tabs */}
            <div className='absolute bottom-0 left-0 right-0 h-[1px] bg-gray-200 z-0' />
            {/* Left: Folder Tabs - Scrollable Container */}
            <div className='flex-1 min-w-0 flex items-end gap-1'>
              {/* Tab Scroll Left Arrow */}
              <button
                onClick={() => {
                  if (tabScrollRef.current && showTabScrollLeft) {
                    tabScrollRef.current.scrollBy({
                      left: -200,
                      behavior: 'smooth'
                    });
                  }
                }}
                disabled={!showTabScrollLeft}
                className={`flex-shrink-0 w-7 h-7 mb-0.5 flex items-center justify-center rounded transition-all ${
                  showTabScrollLeft
                    ? 'opacity-100 hover:bg-gray-200/60 cursor-pointer'
                    : 'opacity-0 pointer-events-none'
                }`}
                title={showTabScrollLeft ? 'Scroll left' : ''}
              >
                <ChevronRight className='h-4 w-4 text-gray-600 rotate-180' />
              </button>

              {/* Tabs Scrollable Area */}
              <div
                ref={tabScrollRef}
                className='flex-1 min-w-0 overflow-x-auto overflow-y-hidden hide-scrollbar'
                onScroll={e => {
                  const { scrollLeft, scrollWidth, clientWidth } =
                    e.currentTarget;
                  setShowTabScrollLeft(scrollLeft > 5);
                  setShowTabScrollRight(
                    scrollLeft < scrollWidth - clientWidth - 5
                  );
                }}
              >
                <div className='min-w-max'>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={({ active }) => setActiveTabId(active.id)}
                    onDragEnd={handleTabDragEnd}
                  >
                    <SortableContext
                      items={views.filter(v => !v.isDefault).map(v => v.id)}
                      strategy={horizontalListSortingStrategy}
                    >
                      <div className='flex items-end gap-0.5'>
                        {/* View Tabs */}
                        {views.map(view => (
                          <SortableTab
                            key={view.id}
                            view={view}
                            isActive={activeViewId === view.id}
                            viewContextMenu={viewContextMenu}
                            setActiveViewId={setActiveViewId}
                            setViewContextMenu={setViewContextMenu}
                            editingTabId={editingTabId}
                            setEditingTabId={setEditingTabId}
                            onRename={handleRenameView}
                            userProfile={userProfile}
                            onToggleMenu={e => {
                              if (viewContextMenu?.viewId === view.id) {
                                setViewContextMenu(null);
                              } else {
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                setViewContextMenu({
                                  viewId: view.id,
                                  x: rect.left,
                                  y: rect.bottom + 4
                                });
                              }
                            }}
                          />
                        ))}

                        {/* Add View Button - folder style */}
                        <button
                          onClick={async () => {
                            const newViewId = await createView(
                              `View ${views.length}`,
                              {},
                              false
                            );
                            if (newViewId) {
                              // Immediately open for editing
                              setTimeout(() => {
                                setEditingTabId(newViewId);
                              }, 100);
                            }
                          }}
                          className='ml-1 w-8 h-8 mb-0.5 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200/60 rounded-md transition-all duration-200'
                          title='Add new view'
                        >
                          <Plus className='h-4 w-4' />
                        </button>
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              </div>

              {/* Tab Scroll Right Arrow */}
              <button
                onClick={() => {
                  if (tabScrollRef.current && showTabScrollRight) {
                    tabScrollRef.current.scrollBy({
                      left: 200,
                      behavior: 'smooth'
                    });
                  }
                }}
                disabled={!showTabScrollRight}
                className={`flex-shrink-0 w-7 h-7 mb-0.5 flex items-center justify-center rounded transition-all ${
                  showTabScrollRight
                    ? 'opacity-100 hover:bg-gray-200/60 cursor-pointer'
                    : 'opacity-0 pointer-events-none'
                }`}
                title={showTabScrollRight ? 'Scroll right' : ''}
              >
                <ChevronRight className='h-4 w-4 text-gray-600' />
              </button>
            </div>

            {/* Right: Toolbar Buttons */}
            <div className='flex items-center gap-2 flex-shrink-0 self-center'>
              {/* Save as New View Button */}
              {hasUnsavedChanges && (
                <button
                  onClick={async () => {
                    const currentFilters = getCurrentFilters();
                    const newViewId = await createView(
                      `View ${views.length}`,
                      currentFilters,
                      false
                    );
                    if (newViewId) {
                      // Immediately open for editing
                      setTimeout(() => {
                        setEditingTabId(newViewId);
                      }, 100);
                    }
                  }}
                  className='h-8 px-3 flex items-center gap-2 text-[13px] font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-all duration-200 border border-transparent shadow-sm'
                >
                  <Plus className='h-3.5 w-3.5' />
                  <span>Save as new view</span>
                </button>
              )}

              {/* Saving Status Indicator */}
              {activeViewId !== 'all' &&
                (() => {
                  const currentView = views.find(v => v.id === activeViewId);
                  const isOwner = currentView?.userId === userProfile?.id;

                  // Only show saving status if user owns this view
                  if (!isOwner) return null;

                  return (
                    <div className='flex items-center gap-1.5 text-[13px] text-gray-500 px-2'>
                      {savingStatus === 'saving' ? (
                        <>
                          <div className='w-3.5 h-3.5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin' />
                          <span className='font-medium'>Saving...</span>
                        </>
                      ) : (
                        <>
                          <svg
                            className='w-3.5 h-3.5 text-gray-500'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M5 13l4 4L19 7'
                            />
                          </svg>
                          <span className='font-medium'>Saved</span>
                        </>
                      )}
                    </div>
                  );
                })()}

              {/* Hide Fields Button */}
              <div className='relative'>
                <button
                  onClick={() => setHideFieldsOpen(!hideFieldsOpen)}
                  className={`h-8 px-3 flex items-center gap-2 text-[13px] font-medium rounded-md transition-all duration-200 ${
                    hideFieldsOpen
                      ? 'bg-gray-100 text-gray-900 border border-gray-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/60 border border-transparent hover:border-gray-200'
                  }`}
                >
                  <EyeOff className='h-3.5 w-3.5' />
                  <span>Hide fields</span>
                  <ChevronDown
                    className={`h-3.5 w-3.5 transition-transform duration-200 ${hideFieldsOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Hide Fields Dropdown */}
                {hideFieldsOpen && (
                  <>
                    <div
                      className='fixed inset-0 z-40'
                      onClick={() => setHideFieldsOpen(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{
                        opacity: { duration: 0.1 },
                        scale: { duration: 0.1 }
                      }}
                      className='absolute top-full right-0 mt-1 w-80 bg-white rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.15)] border border-gray-200 z-50 flex flex-col overflow-hidden'
                      style={{ maxHeight: 'min(500px, calc(100vh - 150px))' }}
                    >
                      {/* Tab Switcher */}
                      <div className='flex border-b border-gray-200 bg-white flex-shrink-0 rounded-t-xl overflow-hidden relative'>
                        <button
                          onClick={() => setHideFieldsTab('qualifications')}
                          className='flex-1 px-4 py-2 text-xs font-medium relative z-10'
                        >
                          <span
                            className={`transition-colors duration-200 ${
                              hideFieldsTab === 'qualifications'
                                ? 'text-blue-600'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            Qualifications
                          </span>
                        </button>
                        <button
                          onClick={() => setHideFieldsTab('members')}
                          className='flex-1 px-4 py-2 text-xs font-medium relative z-10'
                        >
                          <span
                            className={`transition-colors duration-200 ${
                              hideFieldsTab === 'members'
                                ? 'text-blue-600'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            Team Members
                          </span>
                        </button>
                        {/* Animated underline */}
                        <motion.div
                          className='absolute bottom-0 h-0.5 bg-blue-600 z-0'
                          initial={false}
                          animate={{
                            x:
                              hideFieldsTab === 'qualifications'
                                ? '0%'
                                : '100%',
                            width: '50%'
                          }}
                          transition={{
                            type: 'spring',
                            stiffness: 300,
                            damping: 30
                          }}
                        />
                      </div>

                      {/* Search */}
                      <div className='p-3 bg-white flex-shrink-0'>
                        <div className='relative'>
                          <Search className='absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400' />
                          <input
                            type='text'
                            placeholder='Find a field'
                            value={hideFieldsSearch}
                            onChange={e => setHideFieldsSearch(e.target.value)}
                            className='w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-gray-400 bg-white'
                          />
                          {hideFieldsSearch && (
                            <button
                              className='absolute right-2 top-1/2 transform -translate-y-1/2'
                              onClick={() => setHideFieldsSearch('')}
                            >
                              <X className='h-3.5 w-3.5 text-gray-400 hover:text-gray-600' />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Field List */}
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={({ active }) =>
                          setActiveFieldDragId(active.id)
                        }
                        onDragEnd={event => {
                          const { active, over } = event;
                          setActiveFieldDragId(null);

                          if (active.id !== over?.id) {
                            if (hideFieldsTab === 'qualifications') {
                              const oldIndex = fullColumnOrder.findIndex(
                                id => id === active.id
                              );
                              const newIndex = fullColumnOrder.findIndex(
                                id => id === over.id
                              );
                              setColumnOrder(
                                arrayMove(fullColumnOrder, oldIndex, newIndex)
                              );
                            } else {
                              const oldIndex = fullRowOrder.findIndex(
                                id => id === active.id
                              );
                              const newIndex = fullRowOrder.findIndex(
                                id => id === over.id
                              );
                              setRowOrder(
                                arrayMove(fullRowOrder, oldIndex, newIndex)
                              );
                            }
                          }
                        }}
                      >
                        <SortableContext
                          items={
                            hideFieldsTab === 'qualifications'
                              ? fullColumnOrder
                              : fullRowOrder
                          }
                          strategy={verticalListSortingStrategy}
                        >
                          <div className='overflow-y-auto overflow-x-hidden flex-1 min-h-0 relative'>
                            <AnimatePresence mode='wait' initial={false}>
                              {hideFieldsTab === 'qualifications' ? (
                                <motion.div
                                  key='qualifications'
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  {(() => {
                                    return fullColumnOrder
                                      .map(qualId => {
                                        const qual =
                                          matrixData.qualifications.find(
                                            q => q.id === qualId
                                          );
                                        return qual;
                                      })
                                      .filter(qual => {
                                        if (!qual) return false;
                                        // Filter by requirement_type to match grid view
                                        if (viewMode === 'site') {
                                          if (
                                            qual.requirement_type !==
                                              'documentation' &&
                                            qual.requirement_type !==
                                              'internal_checklist'
                                          )
                                            return false;
                                        } else {
                                          if (
                                            qual.requirement_type ===
                                              'documentation' ||
                                            qual.requirement_type ===
                                              'internal_checklist'
                                          )
                                            return false;
                                        }
                                        if (!hideFieldsSearch) return true;
                                        return qual.name
                                          .toLowerCase()
                                          .includes(
                                            hideFieldsSearch.toLowerCase()
                                          );
                                      })
                                      .map(qual => {
                                        // A qualification is visible if it's NOT in hiddenColumns
                                        // Don't check filteredMatrixData because that also includes status filters
                                        const isVisible = !hiddenColumns.has(
                                          qual.id
                                        );
                                        return (
                                          <SortableFieldItem
                                            key={qual.id}
                                            item={qual}
                                            isVisible={isVisible}
                                            type='qualification'
                                            onToggle={() =>
                                              toggleColumn(qual.id)
                                            }
                                          />
                                        );
                                      });
                                  })()}
                                </motion.div>
                              ) : (
                                <motion.div
                                  key='members'
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ duration: 0.15 }}
                                >
                                  {fullRowOrder
                                    .map(memberId => {
                                      const member =
                                        matrixData.teamMembers.find(
                                          m => m.id === memberId
                                        );
                                      return member;
                                    })
                                    .filter(member => {
                                      if (!member) return false;
                                      if (!hideFieldsSearch) return true;
                                      return member.name
                                        .toLowerCase()
                                        .includes(
                                          hideFieldsSearch.toLowerCase()
                                        );
                                    })
                                    .map(member => {
                                      const isVisible = !hiddenRows.has(
                                        member.id
                                      );
                                      return (
                                        <SortableFieldItem
                                          key={member.id}
                                          item={member}
                                          isVisible={isVisible}
                                          type='member'
                                          onToggle={() => toggleRow(member.id)}
                                        />
                                      );
                                    })}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </SortableContext>
                      </DndContext>

                      {/* Footer */}
                      <div className='px-3 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between flex-shrink-0 rounded-b-xl'>
                        <button
                          onClick={() => {
                            if (hideFieldsTab === 'qualifications') {
                              showAllColumns();
                            } else {
                              showAllRows();
                            }
                          }}
                          className='text-xs text-gray-600 hover:text-gray-900 font-medium'
                        >
                          Show all
                        </button>
                        <button
                          onClick={() => {
                            if (hideFieldsTab === 'qualifications') {
                              hideAllColumns(
                                filteredMatrixData.qualifications.map(q => q.id)
                              );
                            } else {
                              hideAllRows(
                                filteredMatrixData.teamMembers.map(m => m.id)
                              );
                            }
                          }}
                          className='text-xs text-gray-600 hover:text-gray-900 font-medium'
                        >
                          Hide all
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </div>

              {/* Filters Button */}
              <div className='relative'>
                <button
                  onClick={() => setFilterOpen(!filterOpen)}
                  className={`h-8 px-3 flex items-center gap-2 text-[13px] font-medium rounded-md transition-all duration-200 ${
                    filterOpen
                      ? 'bg-gray-100 text-gray-900 border border-gray-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/60 border border-transparent hover:border-gray-200'
                  }`}
                >
                  <Filter className='h-3.5 w-3.5' />
                  <span>Filter</span>
                  {activeFilters.size > 0 && (
                    <span className='ml-0.5 px-1.5 py-0.5 bg-blue-500 text-white text-[10px] font-semibold rounded-full'>
                      {activeFilters.size}
                    </span>
                  )}
                </button>

                {/* Filter Dropdown Panel */}
                {filterOpen && (
                  <>
                    <div
                      className='fixed inset-0 z-40'
                      onClick={() => setFilterOpen(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{
                        opacity: { duration: 0.1 },
                        scale: { duration: 0.1 }
                      }}
                      className='absolute top-full right-0 mt-1 w-64 bg-white rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.15)] border border-gray-200 z-50 overflow-hidden'
                    >
                      {/* Header */}
                      <div className='px-3 py-2.5 border-b border-gray-200 bg-white flex items-center justify-between'>
                        <span className='text-xs font-semibold text-gray-700'>
                          Filter by Status
                        </span>
                        {activeFilters.size > 0 && (
                          <button
                            onClick={clearActiveFilters}
                            className='text-[11px] text-blue-600 hover:text-blue-700 font-medium'
                          >
                            Clear all
                          </button>
                        )}
                      </div>

                      {/* Filter Options */}
                      <div className='py-1'>
                        {/* Valid */}
                        <button
                          onClick={() => toggleFilter('valid')}
                          className='w-full px-3 py-2 hover:bg-gray-50 flex items-center justify-between group transition-colors'
                        >
                          <div className='flex items-center gap-2.5'>
                            <div
                              className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                                activeFilters.has('valid')
                                  ? 'bg-green-500 border-green-500'
                                  : 'border-gray-300 group-hover:border-gray-400'
                              }`}
                            >
                              {activeFilters.has('valid') && (
                                <CheckCircle className='h-3 w-3 text-white' />
                              )}
                            </div>
                            <span className='text-xs text-gray-700'>Valid</span>
                          </div>
                          <div className='w-2 h-2 rounded-full bg-green-500' />
                        </button>

                        {/* Expiring */}
                        <button
                          onClick={() => toggleFilter('soon')}
                          className='w-full px-3 py-2 hover:bg-gray-50 flex items-center justify-between group transition-colors'
                        >
                          <div className='flex items-center gap-2.5'>
                            <div
                              className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                                activeFilters.has('soon')
                                  ? 'bg-yellow-500 border-yellow-500'
                                  : 'border-gray-300 group-hover:border-gray-400'
                              }`}
                            >
                              {activeFilters.has('soon') && (
                                <CheckCircle className='h-3 w-3 text-white' />
                              )}
                            </div>
                            <span className='text-xs text-gray-700'>
                              Expiring
                            </span>
                          </div>
                          <div className='w-2 h-2 rounded-full bg-yellow-500' />
                        </button>

                        {/* Expired */}
                        <button
                          onClick={() => toggleFilter('expired')}
                          className='w-full px-3 py-2 hover:bg-gray-50 flex items-center justify-between group transition-colors'
                        >
                          <div className='flex items-center gap-2.5'>
                            <div
                              className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                                activeFilters.has('expired')
                                  ? 'bg-red-500 border-red-500'
                                  : 'border-gray-300 group-hover:border-gray-400'
                              }`}
                            >
                              {activeFilters.has('expired') && (
                                <CheckCircle className='h-3 w-3 text-white' />
                              )}
                            </div>
                            <span className='text-xs text-gray-700'>
                              Expired
                            </span>
                          </div>
                          <div className='w-2 h-2 rounded-full bg-red-500' />
                        </button>

                        {/* Missing */}
                        <button
                          onClick={() => toggleFilter('missing')}
                          className='w-full px-3 py-2 hover:bg-gray-50 flex items-center justify-between group transition-colors'
                        >
                          <div className='flex items-center gap-2.5'>
                            <div
                              className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                                activeFilters.has('missing')
                                  ? 'bg-gray-500 border-gray-500'
                                  : 'border-gray-300 group-hover:border-gray-400'
                              }`}
                            >
                              {activeFilters.has('missing') && (
                                <CheckCircle className='h-3 w-3 text-white' />
                              )}
                            </div>
                            <span className='text-xs text-gray-700'>
                              Missing
                            </span>
                          </div>
                          <div className='w-2 h-2 rounded-full bg-gray-400' />
                        </button>

                        {/* Divider */}
                        <div className='my-1 border-t border-gray-200' />

                        {/* Assigned */}
                        <button
                          onClick={() => toggleFilter('assigned')}
                          className='w-full px-3 py-2 hover:bg-gray-50 flex items-center justify-between group transition-colors'
                        >
                          <div className='flex items-center gap-2.5'>
                            <div
                              className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                                activeFilters.has('assigned')
                                  ? 'bg-blue-500 border-blue-500'
                                  : 'border-gray-300 group-hover:border-gray-400'
                              }`}
                            >
                              {activeFilters.has('assigned') && (
                                <CheckCircle className='h-3 w-3 text-white' />
                              )}
                            </div>
                            <span className='text-xs text-gray-700'>
                              Assigned
                            </span>
                          </div>
                        </button>

                        {/* Unassigned */}
                        <button
                          onClick={() => toggleFilter('unassigned')}
                          className='w-full px-3 py-2 hover:bg-gray-50 flex items-center justify-between group transition-colors'
                        >
                          <div className='flex items-center gap-2.5'>
                            <div
                              className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                                activeFilters.has('unassigned')
                                  ? 'bg-blue-500 border-blue-500'
                                  : 'border-gray-300 group-hover:border-gray-400'
                              }`}
                            >
                              {activeFilters.has('unassigned') && (
                                <CheckCircle className='h-3 w-3 text-white' />
                              )}
                            </div>
                            <span className='text-xs text-gray-700'>
                              Unassigned
                            </span>
                          </div>
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* View Context Menu */}
        {viewContextMenu &&
          (() => {
            const currentView = views.find(
              v => v.id === viewContextMenu.viewId
            );
            const isOwner = currentView?.userId === userProfile?.id;
            const isDefaultView =
              currentView?.isDefault || viewContextMenu.viewId === 'all';

            return (
              <>
                <div
                  className='fixed inset-0 z-40'
                  onClick={() => setViewContextMenu(null)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className='fixed z-50 bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-200/60 py-1.5 min-w-[180px] backdrop-blur-sm'
                  style={{
                    left: `${viewContextMenu.x}px`,
                    top: `${viewContextMenu.y}px`
                  }}
                >
                  {/* Author name for public views */}
                  {!isOwner && !isDefaultView && currentView?.authorName && (
                    <div className='px-4 py-2 border-b border-gray-100'>
                      <p className='text-[11px] text-gray-500'>Managed by</p>
                      <p className='text-[13px] font-medium text-gray-700'>
                        {currentView.authorName}
                      </p>
                    </div>
                  )}

                  {/* Rename - Only for owners and not default view */}
                  {isOwner && !isDefaultView && (
                    <button
                      className='w-full px-4 py-2.5 text-[13px] text-left hover:bg-blue-50/80 flex items-center gap-3 text-gray-700 hover:text-blue-700 transition-colors group'
                      onClick={() => {
                        setEditingTabId(viewContextMenu.viewId);
                        setViewContextMenu(null);
                      }}
                    >
                      <Edit2 className='h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors' />
                      <span className='font-medium'>Rename</span>
                    </button>
                  )}

                  {/* Export - Available to all */}
                  <button
                    className='w-full px-4 py-2.5 text-[13px] text-left hover:bg-blue-50/80 flex items-center gap-3 text-gray-700 hover:text-blue-700 transition-colors group'
                    onClick={() => {
                      try {
                        // Get the view name for the filename
                        const view = views.find(
                          v => v.id === viewContextMenu.viewId
                        );
                        const viewName = view?.name || 'All';

                        // Use filteredMatrixData to export only what's visible in the current view
                        const qualifications =
                          filteredMatrixData.qualifications;
                        const teamMembers = filteredMatrixData.teamMembers;

                        // Create CSV header row
                        const headers = [
                          'Employee',
                          ...qualifications.map(q => q.name)
                        ];

                        // Create CSV data rows
                        const rows = teamMembers.map(member => {
                          const row = [member.name];

                          // For each qualification, find the member's status
                          qualifications.forEach(qual => {
                            const req = member.requirements.find(
                              r => r.id === qual.id
                            );
                            if (req) {
                              // Format the status with date if available
                              let cellValue = req.status;
                              if (req.date) {
                                const dateStr = new Date(
                                  req.date
                                ).toLocaleDateString();
                                cellValue += ` (${dateStr})`;
                              }
                              row.push(cellValue);
                            } else {
                              row.push('');
                            }
                          });

                          return row;
                        });

                        // Combine headers and rows
                        const csvContent = [
                          headers.join(','),
                          ...rows.map(row =>
                            row
                              .map(cell => {
                                // Escape cells that contain commas or quotes
                                if (
                                  typeof cell === 'string' &&
                                  (cell.includes(',') || cell.includes('"'))
                                ) {
                                  return `"${cell.replace(/"/g, '""')}"`;
                                }
                                return cell;
                              })
                              .join(',')
                          )
                        ].join('\n');

                        // Create blob and download
                        const blob = new Blob([csvContent], {
                          type: 'text/csv;charset=utf-8;'
                        });
                        const link = document.createElement('a');
                        const url = URL.createObjectURL(blob);
                        link.setAttribute('href', url);
                        link.setAttribute(
                          'download',
                          `training-matrix-${viewName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`
                        );
                        link.style.visibility = 'hidden';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        toast.success('Matrix exported successfully');
                      } catch (error) {
                        console.error('Error exporting matrix:', error);
                        toast.error('Failed to export matrix');
                      }
                      setViewContextMenu(null);
                    }}
                  >
                    <Download className='h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors' />
                    <span className='font-medium'>Export</span>
                  </button>

                  {/* Public Toggle - Only for owners and not default view */}
                  {isOwner && !isDefaultView && (
                    <div
                      className='w-full px-4 py-2.5 text-[13px] hover:bg-blue-50/80 flex items-center justify-between text-gray-700 hover:text-blue-700 transition-colors group cursor-pointer'
                      onClick={async e => {
                        e.preventDefault();
                        e.stopPropagation();
                        await toggleViewPublic(viewContextMenu.viewId);
                      }}
                    >
                      <div className='flex items-center gap-3'>
                        <Eye className='h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors' />
                        <span className='font-medium'>Public</span>
                      </div>
                      <Switch
                        checked={
                          views.find(v => v.id === viewContextMenu.viewId)
                            ?.isPublic || false
                        }
                        className='pointer-events-none'
                      />
                    </div>
                  )}

                  {/* Duplicate - Available to all */}
                  <button
                    className='w-full px-4 py-2.5 text-[13px] text-left hover:bg-blue-50/80 flex items-center gap-3 text-gray-700 hover:text-blue-700 transition-colors group'
                    onClick={async () => {
                      const view = views.find(
                        v => v.id === viewContextMenu.viewId
                      );
                      // Use current active state for filters (captures current visible state)
                      const currentFilters = getCurrentFilters();
                      await createView(
                        `${view.name} (Copy)`,
                        currentFilters,
                        false
                      );
                      setViewContextMenu(null);
                    }}
                  >
                    <FileText className='h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors' />
                    <span className='font-medium'>Duplicate</span>
                  </button>

                  {/* Delete - Only for owners and not default view */}
                  {isOwner && !isDefaultView && (
                    <>
                      <div className='border-t border-gray-200/60 my-1.5 mx-2' />
                      <button
                        className='w-full px-4 py-2.5 text-[13px] text-left hover:bg-red-50/80 text-red-600 flex items-center gap-3 transition-colors group'
                        onClick={() => {
                          const view = views.find(
                            v => v.id === viewContextMenu.viewId
                          );
                          setViewToDelete(view);
                          setDeleteViewDialogOpen(true);
                          setViewContextMenu(null);
                        }}
                      >
                        <X className='h-4 w-4 text-red-400 group-hover:text-red-600 transition-colors' />
                        <span className='font-medium'>Delete view</span>
                      </button>
                    </>
                  )}
                </motion.div>
              </>
            );
          })()}

        {/* Matrix Views Container - Full Width */}
        <div className='relative'>
          <AnimatePresence mode='wait' initial={false}>
            {viewMode === 'grid' ? (
              /* Grid View - Using extracted MatrixGrid component */
              <MatrixGrid
                // Data
                filteredMatrixData={filteredMatrixData}
                qualStatsMap={qualStatsMap}
                employeeStatsMap={employeeStatsMap}
                sidebarStats={sidebarStats}
                filteredRequirements={filteredRequirements}
                // UI State
                nameColumnExpanded={nameColumnExpanded}
                hiddenColumns={hiddenColumns}
                hiddenRows={hiddenRows}
                columnOrder={columnOrder}
                rowOrder={rowOrder}
                highlightedColumns={highlightedColumns}
                highlightedRows={highlightedRows}
                // Handlers
                toggleColumnHighlight={toggleColumnHighlight}
                toggleRowHighlight={toggleRowHighlight}
                handleRemoveQualificationFromMatrix={
                  handleRemoveQualificationFromMatrix
                }
                handleOpenAddQualificationDialog={
                  handleOpenAddQualificationDialog
                }
                handleOpenAddMemberDialog={handleOpenAddMemberDialog}
                handleCellClick={handleCellClick}
                handleCellRightClick={handleCellRightClick}
                handleHeaderRightClick={handleHeaderRightClick}
                handleOneClickQuote={handleOneClickQuote}
                getCellDragProps={getCellDragProps}
                isCellDragOver={isCellDragOver}
                getStatusLabel={getStatusLabel}
                getStatusDateText={getStatusDateText}
                onColumnDragEnd={handleDragEnd}
                // Modal/Dock state
                openQualificationGapId={openQualificationGapId}
                setOpenQualificationGapId={setOpenQualificationGapId}
                dockOpen={dockOpen}
                dockCell={dockCell}
                setDockOpen={setDockOpen}
                setDockCell={setDockCell}
                setSelectedCell={setSelectedCell}
                setCrossSpaceModalOpen={setCrossSpaceModalOpen}
                // Navigation
                projectId={projectId}
                // Loading state for assigning cells
                assigningCells={assigningCells}
              />
            ) : viewMode === 'qualifications' ? (
              /* Qualifications View - Grouped by Qualification */
              <motion.div
                key='qualifications-view'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15, ease: 'easeInOut' }}
                style={{ willChange: 'opacity' }}
                className='overflow-auto max-w-7xl mx-auto'
              >
                <Accordion
                  type='single'
                  collapsible
                  className='w-full space-y-3 p-4'
                  value={expandedAccordionId}
                  onValueChange={setExpandedAccordionId}
                >
                  {/* Simplified: removed complex spring/3D animations for performance */}
                  <AnimatePresence mode='popLayout'>
                    {filteredQualificationsForRows.map(
                      (qualification, qualIndex) => {
                        // Use pre-calculated stats from useMemo
                        const {
                          qualStats,
                          compliancePercentage,
                          requirementData
                        } = qualification;

                        return (
                          <motion.div
                            key={qualification.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{
                              opacity: 1,
                              y: 0,
                              transition: {
                                duration: 0.2,
                                delay: qualIndex * 0.03,
                                ease: 'easeOut'
                              }
                            }}
                            exit={{
                              opacity: 0,
                              y: -10,
                              transition: {
                                duration: 0.15,
                                ease: 'easeIn'
                              }
                            }}
                            className='bg-background rounded-lg border'
                          >
                            <AccordionItem
                              value={`item-${qualification.id}`}
                              className='border-0'
                              data-qualification-id={qualification.id}
                            >
                              <AccordionTrigger className='hover:no-underline px-4 py-3'>
                                {/* Horizontal row design like ProjectListItem */}
                                <div className='group flex items-center gap-4 w-full justify-between'>
                                  {/* Left side: indicator, icon, and text */}
                                  <div className='flex items-center gap-4 flex-1 min-w-0'>
                                    {/* Compliance color indicator */}
                                    <div
                                      className='w-1 h-16 rounded-full flex-shrink-0'
                                      style={{
                                        backgroundColor:
                                          compliancePercentage >= 80
                                            ? '#22c55e'
                                            : compliancePercentage >= 50
                                              ? '#eab308'
                                              : '#ef4444'
                                      }}
                                    />

                                    {/* Icon/Badge */}
                                    <div className='flex-shrink-0'>
                                      <div
                                        className={`rounded-lg w-12 h-12 flex items-center justify-center text-sm font-semibold ${
                                          compliancePercentage >= 80
                                            ? 'bg-green-100 text-green-700'
                                            : compliancePercentage >= 50
                                              ? 'bg-yellow-100 text-yellow-700'
                                              : 'bg-red-100 text-red-700'
                                        }`}
                                      >
                                        {qualification.name
                                          .split(' ')
                                          .map(w => w[0])
                                          .join('')
                                          .toUpperCase()
                                          .slice(0, 2)}
                                      </div>
                                    </div>

                                    {/* Name and description */}
                                    <div className='flex-1 min-w-0 max-w-md'>
                                      <h3 className='font-semibold text-base truncate'>
                                        {qualification.name}
                                      </h3>
                                      {qualification.description && (
                                        <p className='text-sm text-muted-foreground truncate mt-0.5'>
                                          {qualification.description}
                                        </p>
                                      )}
                                    </div>

                                    {/* Edit button */}
                                    <Button
                                      variant='ghost'
                                      size='icon'
                                      className='h-8 w-8 flex-shrink-0 text-gray-400 hover:text-gray-700'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditQualSelected(qualification);
                                        setEditQualDialogOpen(true);
                                      }}
                                    >
                                      <Edit2 className='h-4 w-4' />
                                    </Button>
                                  </div>

                                  {/* Right side: Stats badges with gap analysis tooltip */}
                                  <TooltipProvider delayDuration={300}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className='flex items-center gap-2 flex-shrink-0 ml-auto cursor-pointer hover:opacity-80 transition-opacity'>
                                          <Badge
                                            variant='secondary'
                                            className='gap-1'
                                          >
                                            <Users className='w-3 h-3' />
                                            {qualStats.required}
                                          </Badge>
                                          <Badge
                                            variant='outline'
                                            className='gap-1 border-green-200 bg-green-50 text-green-700'
                                          >
                                            <CheckCircle className='w-3 h-3' />
                                            {qualStats.valid}
                                          </Badge>
                                          <Badge
                                            variant='outline'
                                            className='gap-1 border-yellow-200 bg-yellow-50 text-yellow-700'
                                          >
                                            <Clock className='w-3 h-3' />
                                            {qualStats.expiring}
                                          </Badge>
                                          <Badge
                                            variant='outline'
                                            className='gap-1 border-red-200 bg-red-50 text-red-700'
                                          >
                                            <AlertTriangle className='w-3 h-3' />
                                            {qualStats.expired}
                                          </Badge>
                                          <Badge
                                            variant='outline'
                                            className='gap-1 border-orange-200 bg-orange-50 text-orange-700'
                                          >
                                            <AlertCircle className='w-3 h-3' />
                                            {qualStats.missing}
                                          </Badge>
                                          <div className='flex flex-col items-center gap-0.5 w-20 border-l pl-3'>
                                            <span className='text-xs text-muted-foreground'>
                                              Compliance
                                            </span>
                                            <span className='text-xl font-bold'>
                                              {compliancePercentage}%
                                            </span>
                                          </div>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent
                                        side='bottom'
                                        className='max-w-xs bg-white border border-gray-200 shadow-lg p-3'
                                        sideOffset={5}
                                      >
                                        {/* Header with qualification name */}
                                        <div className='font-semibold text-sm text-gray-900 mb-3 pb-2 border-b border-gray-100'>
                                          {qualification.name}
                                        </div>

                                        {/* Compliance percentage - highlighted */}
                                        <div
                                          className={`rounded-lg p-3 mb-3 ${
                                            compliancePercentage >= 80
                                              ? 'bg-green-50 border border-green-200'
                                              : compliancePercentage >= 50
                                                ? 'bg-yellow-50 border border-yellow-200'
                                                : 'bg-red-50 border border-red-200'
                                          }`}
                                        >
                                          <div className='flex items-center justify-between gap-4'>
                                            <span className='text-xs font-medium text-gray-600'>
                                              Overall Compliance
                                            </span>
                                            <span
                                              className={`text-lg font-bold ${
                                                compliancePercentage >= 80
                                                  ? 'text-green-600'
                                                  : compliancePercentage >= 50
                                                    ? 'text-yellow-600'
                                                    : 'text-red-600'
                                              }`}
                                            >
                                              {compliancePercentage}%
                                            </span>
                                          </div>
                                        </div>

                                        {/* Stats Grid */}
                                        <div className='grid grid-cols-2 gap-2 text-xs'>
                                          <div className='bg-green-50 rounded-md px-2 py-1.5 border border-green-200'>
                                            <div className='flex flex-col gap-0.5'>
                                              <div className='flex items-center justify-between'>
                                                <span className='text-green-700 font-medium'>
                                                  Valid
                                                </span>
                                                <span className='font-bold text-green-600'>
                                                  {(qualStats.valid || 0) +
                                                    (qualStats.validCrossSpace ||
                                                      0)}
                                                </span>
                                              </div>
                                              <div className='flex items-center gap-2 text-[10px] text-green-600 opacity-70'>
                                                <span>
                                                  Assigned:{' '}
                                                  {qualStats.valid || 0}
                                                </span>
                                                <span>
                                                  Unassigned:{' '}
                                                  {qualStats.validCrossSpace ||
                                                    0}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className='bg-yellow-50 rounded-md px-2 py-1.5 border border-yellow-200'>
                                            <div className='flex flex-col gap-0.5'>
                                              <div className='flex items-center justify-between'>
                                                <span className='text-yellow-700 font-medium'>
                                                  Expiring
                                                </span>
                                                <span className='font-bold text-yellow-600'>
                                                  {(qualStats.expiring || 0) +
                                                    (qualStats.expiringCrossSpace ||
                                                      0)}
                                                </span>
                                              </div>
                                              <div className='flex items-center gap-2 text-[10px] text-yellow-600 opacity-70'>
                                                <span>
                                                  Assigned:{' '}
                                                  {qualStats.expiring || 0}
                                                </span>
                                                <span>
                                                  Unassigned:{' '}
                                                  {qualStats.expiringCrossSpace ||
                                                    0}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className='bg-red-50 rounded-md px-2 py-1.5 border border-red-200'>
                                            <div className='flex items-center justify-between'>
                                              <span className='text-red-700 font-medium'>
                                                Expired
                                              </span>
                                              <span className='font-bold text-red-600'>
                                                {qualStats.expired}
                                              </span>
                                            </div>
                                          </div>
                                          <div className='bg-orange-50 rounded-md px-2 py-1.5 border border-orange-200'>
                                            <div className='flex items-center justify-between'>
                                              <span className='text-orange-700 font-medium'>
                                                Missing
                                              </span>
                                              <span className='font-bold text-orange-600'>
                                                {qualStats.missing}
                                              </span>
                                            </div>
                                          </div>
                                          <div className='bg-blue-50 rounded-md px-2 py-1.5 border border-blue-200'>
                                            <div className='flex items-center justify-between'>
                                              <span className='text-blue-700 font-medium'>
                                                Booked
                                              </span>
                                              <span className='font-bold text-blue-600'>
                                                {qualStats.booked}
                                              </span>
                                            </div>
                                          </div>
                                          <div className='bg-purple-50 rounded-md px-2 py-1.5 border border-purple-200'>
                                            <div className='flex items-center justify-between'>
                                              <span className='text-purple-700 font-medium'>
                                                Quoting
                                              </span>
                                              <span className='font-bold text-purple-600'>
                                                {qualStats.quoting}
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Required vs Assigned */}
                                        <div className='mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-2 text-xs'>
                                          <div className='flex items-center justify-between'>
                                            <span className='text-gray-600'>
                                              Required:
                                            </span>
                                            <span className='font-bold text-gray-900'>
                                              {qualStats.required}
                                            </span>
                                          </div>
                                          <div className='flex items-center justify-between'>
                                            <span className='text-gray-600'>
                                              Assigned:
                                            </span>
                                            <span className='font-bold text-gray-900'>
                                              {qualStats.assigned}
                                            </span>
                                          </div>
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                              </AccordionTrigger>

                              <AccordionContent className='px-6 pb-4'>
                                <div className='space-y-4'>
                                  {/* Full description when expanded */}
                                  {qualification.description && (
                                    <div className='pb-3 border-b'>
                                      <p className='text-sm text-muted-foreground whitespace-normal break-words'>
                                        {qualification.description}
                                      </p>
                                    </div>
                                  )}

                                  {/* Two-column panel: Space members + Company members */}
                                  <QualificationMembersPanel
                                    qualification={qualification}
                                    spaceMembers={
                                      filteredMatrixData.teamMembers
                                    }
                                    companyId={company?.id}
                                    projectId={projectId}
                                    stackId={stackId}
                                    stackProjects={
                                      isStackView ? stackProjects : []
                                    }
                                    isStackView={isStackView}
                                    currentTeamMemberIds={filteredMatrixData.teamMembers.map(
                                      m => m.user_id
                                    )}
                                    onMemberClick={member => {
                                      const req = member.requirements?.find(
                                        r => r.id === qualification.id
                                      );
                                      if (req) {
                                        handleCellClick(
                                          member,
                                          qualification,
                                          req.status,
                                          null,
                                          req
                                        );
                                      }
                                    }}
                                    onAddToSpace={handleAddCompanyMemberToSpace}
                                  />

                                  <div className='mt-4 flex justify-center'>
                                    <div className='inline-flex flex-col items-center gap-2 p-4 bg-blue-50 rounded-lg border border-blue-100'>
                                      <span className='text-sm text-gray-600'>
                                        Add existing space members to this
                                        qualification
                                      </span>
                                      <Button
                                        size='sm'
                                        onClick={() => {
                                          setSelectedQualificationForAdd(
                                            qualification
                                          );
                                          setAddMembersDialogOpen(true);
                                        }}
                                        className='bg-blue-600 hover:bg-blue-700'
                                      >
                                        <Plus className='h-4 w-4 mr-2' />
                                        Add Team Member
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </motion.div>
                        );
                      }
                    )}
                  </AnimatePresence>
                </Accordion>
              </motion.div>
            ) : viewMode === 'employees' ? (
              /* Employees View - Grouped by Employee */
              <motion.div
                key='employees-view'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15, ease: 'easeInOut' }}
                style={{ willChange: 'opacity' }}
                className='overflow-auto max-w-7xl mx-auto'
              >
                <Accordion
                  type='single'
                  collapsible
                  className='w-full space-y-3 p-4'
                  value={expandedAccordionId}
                  onValueChange={setExpandedAccordionId}
                >
                  <AnimatePresence mode='popLayout'>
                    {filteredMatrixData.teamMembers.map(
                      (member, memberIndex) => {
                        // Calculate employee stats
                        const stats = employeeStatsMap.get(member.id);
                        if (!stats) return null;

                        const compliancePercentage =
                          stats.totalAssigned > 0
                            ? stats.compliancePercentage
                            : null;
                        const gap =
                          stats.totalAssigned - (stats.valid + stats.expiring);
                        const hasAssignments = stats.totalAssigned > 0;

                        return (
                          <motion.div
                            key={member.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{
                              opacity: 1,
                              y: 0,
                              transition: {
                                duration: 0.2,
                                delay: memberIndex * 0.03,
                                ease: 'easeOut'
                              }
                            }}
                            exit={{
                              opacity: 0,
                              y: -10,
                              transition: {
                                duration: 0.15,
                                ease: 'easeIn'
                              }
                            }}
                            className='bg-background rounded-lg border'
                          >
                            <AccordionItem
                              value={`item-${member.id}`}
                              className='border-0'
                              data-member-id={member.id}
                            >
                              <AccordionTrigger className='hover:no-underline px-4 py-3'>
                                <div className='group flex items-center gap-4 w-full justify-between'>
                                  {/* Left side: indicator, avatar, and text */}
                                  <div className='flex items-center gap-4 flex-1 min-w-0'>
                                    {/* Compliance color indicator */}
                                    <div
                                      className='w-1 h-16 rounded-full flex-shrink-0'
                                      style={{
                                        backgroundColor: !hasAssignments
                                          ? '#9ca3af'
                                          : compliancePercentage >= 80
                                            ? '#22c55e'
                                            : compliancePercentage >= 50
                                              ? '#eab308'
                                              : '#ef4444'
                                      }}
                                    />

                                    {/* Avatar */}
                                    <div className='flex-shrink-0'>
                                      {member.avatar_url ? (
                                        <img
                                          src={member.avatar_url}
                                          alt={member.name}
                                          className='w-12 h-12 rounded-lg object-cover'
                                        />
                                      ) : (
                                        <div
                                          className={`rounded-lg w-12 h-12 flex items-center justify-center text-sm font-semibold ${
                                            !hasAssignments
                                              ? 'bg-gray-100 text-gray-500'
                                              : compliancePercentage >= 80
                                                ? 'bg-green-100 text-green-700'
                                                : compliancePercentage >= 50
                                                  ? 'bg-yellow-100 text-yellow-700'
                                                  : 'bg-red-100 text-red-700'
                                          }`}
                                        >
                                          {member.name
                                            .split(' ')
                                            .map(n => n[0])
                                            .join('')
                                            .toUpperCase()}
                                        </div>
                                      )}
                                    </div>

                                    {/* Name and role */}
                                    <div className='flex-1 min-w-0 max-w-md'>
                                      <h3 className='font-semibold text-base truncate'>
                                        {member.name}
                                      </h3>
                                      {member.role && (
                                        <p className='text-sm text-muted-foreground truncate mt-0.5'>
                                          {member.role}
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  {/* Right side: Stats badges */}
                                  <div className='flex items-center gap-2 flex-shrink-0 ml-auto'>
                                    <Badge
                                      variant='secondary'
                                      className='gap-1'
                                    >
                                      <Award className='w-3 h-3' />
                                      {stats.totalAssigned}
                                    </Badge>
                                    <Badge
                                      variant='outline'
                                      className='gap-1 border-green-200 bg-green-50 text-green-700'
                                    >
                                      <CheckCircle className='w-3 h-3' />
                                      {stats.valid}
                                    </Badge>
                                    <Badge
                                      variant='outline'
                                      className='gap-1 border-yellow-200 bg-yellow-50 text-yellow-700'
                                    >
                                      <Clock className='w-3 h-3' />
                                      {stats.expiring}
                                    </Badge>
                                    <Badge
                                      variant='outline'
                                      className='gap-1 border-red-200 bg-red-50 text-red-700'
                                    >
                                      <AlertTriangle className='w-3 h-3' />
                                      {gap}
                                    </Badge>
                                    <div className='flex flex-col items-center gap-0.5 w-20 border-l pl-3'>
                                      <span className='text-xs text-muted-foreground'>
                                        Compliance
                                      </span>
                                      <span
                                        className={`text-xl font-bold ${!hasAssignments ? 'text-muted-foreground' : ''}`}
                                      >
                                        {hasAssignments
                                          ? `${compliancePercentage}%`
                                          : 'N/A'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </AccordionTrigger>

                              <AccordionContent className='px-6 pb-4'>
                                <div className='space-y-4'>
                                  {/* Email when expanded */}
                                  {member.email && (
                                    <div className='pb-3 border-b'>
                                      <p className='text-sm text-muted-foreground'>
                                        {member.email}
                                      </p>
                                    </div>
                                  )}

                                  <div className='flex items-center gap-4 mb-4'>
                                    <span className='text-sm text-gray-500'>
                                      Search qualifications
                                    </span>
                                    <div className='flex-1 relative'>
                                      <input
                                        type='text'
                                        placeholder='Search by name...'
                                        value={rowsSearchTerm}
                                        onChange={e =>
                                          setRowsSearchTerm(e.target.value)
                                        }
                                        className='w-full px-3 py-1.5 text-sm border rounded-md'
                                      />
                                    </div>
                                  </div>

                                  <div className='border rounded-lg'>
                                    <table className='w-full'>
                                      <tbody>
                                        {member.requirements
                                          .filter(req => req.isAssigned)
                                          .map(req => {
                                            // Get the actual qualification data
                                            const qualification =
                                              filteredMatrixData.qualifications.find(
                                                q => q.id === req.id
                                              );
                                            return { ...req, qualification };
                                          })
                                          .filter(({ qualification }) => {
                                            if (!qualification) return false;
                                            if (!rowsSearchTerm.trim())
                                              return true;
                                            const searchLower =
                                              rowsSearchTerm.toLowerCase();
                                            return qualification.name
                                              .toLowerCase()
                                              .includes(searchLower);
                                          })
                                          .map(
                                            (
                                              { qualification, ...req },
                                              idx
                                            ) => {
                                              if (!qualification) return null;
                                              const statusConfig =
                                                getStatusConfig(req.status);

                                              return (
                                                <tr
                                                  key={req.id}
                                                  className='border-b last:border-0 hover:bg-gray-50 transition-colors cursor-pointer'
                                                  onClick={e => {
                                                    handleCellClick(
                                                      member,
                                                      qualification,
                                                      req.status,
                                                      e,
                                                      req
                                                    );
                                                  }}
                                                >
                                                  <td className='p-3'>
                                                    <div className='flex items-center gap-3'>
                                                      <div className='w-8 h-8 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center text-gray-700 text-xs font-medium'>
                                                        {qualification.name
                                                          .split(' ')
                                                          .map(n => n[0])
                                                          .join('')
                                                          .toUpperCase()
                                                          .slice(0, 2)}
                                                      </div>
                                                      <div className='flex-1 min-w-0'>
                                                        <div className='text-sm font-medium text-gray-900'>
                                                          {qualification.name}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </td>
                                                  <td className='p-3'>
                                                    <div className='flex items-center gap-3'>
                                                      {/* Status indicator */}
                                                      <div className='flex items-center gap-2'>
                                                        <div
                                                          className={`w-2 h-2 rounded-full ${statusConfig.dotColor}`}
                                                        ></div>
                                                        <span className='text-sm font-medium'>
                                                          {statusConfig.label}
                                                        </span>
                                                      </div>

                                                      {/* Renewal Status Container */}
                                                      {req.renewalStatus &&
                                                        req.renewalStatus !==
                                                          'n/a' && (
                                                          <div className='px-2 py-1 bg-white/90 border border-gray-300 rounded-full flex items-center gap-1 shadow-sm'>
                                                            {req.renewalStatus ===
                                                            'quoting' ? (
                                                              <>
                                                                <FileText
                                                                  className='h-3 w-3 text-purple-600'
                                                                  strokeWidth={
                                                                    1.5
                                                                  }
                                                                />
                                                                <span className='text-[9px] font-medium text-purple-600'>
                                                                  Quoting
                                                                </span>
                                                              </>
                                                            ) : req.renewalStatus ===
                                                              'booked' ? (
                                                              <>
                                                                <Calendar
                                                                  className='h-3 w-3 text-blue-600'
                                                                  strokeWidth={
                                                                    1.5
                                                                  }
                                                                />
                                                                <span className='text-[9px] font-medium text-blue-600'>
                                                                  Booked
                                                                </span>
                                                              </>
                                                            ) : req.renewalStatus ===
                                                              'quoted' ? (
                                                              <>
                                                                <FileCheck
                                                                  className='h-3 w-3 text-indigo-600'
                                                                  strokeWidth={
                                                                    1.5
                                                                  }
                                                                />
                                                                <span className='text-[9px] font-medium text-indigo-600'>
                                                                  Quoted
                                                                </span>
                                                              </>
                                                            ) : null}
                                                          </div>
                                                        )}
                                                    </div>
                                                  </td>
                                                  <td className='p-3'>
                                                    {req.expiry_date && (
                                                      <div className='text-sm text-gray-600'>
                                                        <span className='text-gray-500'>
                                                          Expires:{' '}
                                                        </span>
                                                        {new Date(
                                                          req.expiry_date
                                                        ).toLocaleDateString()}
                                                      </div>
                                                    )}
                                                  </td>
                                                </tr>
                                              );
                                            }
                                          )}
                                        {member.requirements
                                          .filter(req => req.isAssigned)
                                          .map(req => {
                                            const qualification =
                                              filteredMatrixData.qualifications.find(
                                                q => q.id === req.id
                                              );
                                            return { ...req, qualification };
                                          })
                                          .filter(({ qualification }) => {
                                            if (!qualification) return false;
                                            if (!rowsSearchTerm.trim())
                                              return true;
                                            const searchLower =
                                              rowsSearchTerm.toLowerCase();
                                            return qualification.name
                                              .toLowerCase()
                                              .includes(searchLower);
                                          }).length === 0 && (
                                          <tr>
                                            <td
                                              colSpan='3'
                                              className='p-6 text-center text-gray-500 text-sm'
                                            >
                                              No qualifications found
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </motion.div>
                        );
                      }
                    )}
                  </AnimatePresence>
                </Accordion>
              </motion.div>
            ) : viewMode === 'site' ? (
              /* Site View - Site Compliance Requirements */
              <motion.div
                key='site-view'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15, ease: 'easeInOut' }}
                style={{ willChange: 'opacity' }}
                className='overflow-auto max-w-7xl mx-auto'
              >
                {siteRequirementsLoading ? (
                  <div className='flex items-center justify-center h-96'>
                    <LoadingSpinner />
                  </div>
                ) : siteRequirements.length === 0 ? (
                  <div className='flex items-center justify-center h-96'>
                    <div className='text-center'>
                      <MapPin className='h-16 w-16 text-gray-300 mx-auto mb-4' />
                      <h3 className='text-xl font-semibold text-gray-700 mb-2'>
                        No Site Compliance Items
                      </h3>
                      <p className='text-gray-500 mb-4'>
                        Set up site compliance requirements in the Compliance
                        tool
                      </p>
                      <Button
                        variant='outline'
                        onClick={() =>
                          navigate(`/space/${projectId}/compliance`)
                        }
                      >
                        <Shield className='w-4 h-4 mr-2' />
                        Go to Compliance
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Accordion
                    type='single'
                    collapsible
                    className='w-full space-y-3 p-4'
                    value={expandedAccordionId}
                    onValueChange={setExpandedAccordionId}
                  >
                    <AnimatePresence mode='popLayout'>
                      {(() => {
                        // Group site requirements by category
                        const groupedByCategory = siteRequirements.reduce(
                          (acc, req) => {
                            const category = req.category || 'Uncategorized';
                            if (!acc[category]) {
                              acc[category] = [];
                            }
                            acc[category].push(req);
                            return acc;
                          },
                          {}
                        );

                        // Calculate stats per category
                        const categoryStats = Object.entries(
                          groupedByCategory
                        ).map(([category, items]) => {
                          const total = items.length;
                          const compliant = items.filter(
                            i => i.status === 'compliant'
                          ).length;
                          const overdue = items.filter(i => {
                            if (!i.next_due_date) return false;
                            return (
                              new Date(i.next_due_date) < new Date() &&
                              i.status !== 'compliant'
                            );
                          }).length;
                          const dueSoon = items.filter(i => {
                            if (!i.next_due_date || i.status === 'compliant')
                              return false;
                            const dueDate = new Date(i.next_due_date);
                            const now = new Date();
                            const thirtyDaysFromNow = new Date(
                              now.getTime() + 30 * 24 * 60 * 60 * 1000
                            );
                            return (
                              dueDate >= now && dueDate <= thirtyDaysFromNow
                            );
                          }).length;
                          const pending = total - compliant - overdue - dueSoon;
                          const compliancePercentage =
                            total > 0
                              ? Math.round((compliant / total) * 100)
                              : 0;

                          return {
                            category,
                            items,
                            total,
                            compliant,
                            overdue,
                            dueSoon,
                            pending,
                            compliancePercentage
                          };
                        });

                        // Sort categories by compliance percentage (worst first)
                        categoryStats.sort(
                          (a, b) =>
                            a.compliancePercentage - b.compliancePercentage
                        );

                        return categoryStats.map(
                          (
                            {
                              category,
                              items,
                              total,
                              compliant,
                              overdue,
                              dueSoon,
                              compliancePercentage
                            },
                            catIndex
                          ) => (
                            <motion.div
                              key={category}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{
                                opacity: 1,
                                y: 0,
                                transition: {
                                  duration: 0.2,
                                  delay: catIndex * 0.03,
                                  ease: 'easeOut'
                                }
                              }}
                              exit={{
                                opacity: 0,
                                y: -10,
                                transition: {
                                  duration: 0.15,
                                  ease: 'easeIn'
                                }
                              }}
                              className='bg-background rounded-lg border'
                            >
                              <AccordionItem
                                value={`site-${category}`}
                                className='border-0'
                              >
                                <AccordionTrigger className='hover:no-underline px-4 py-3'>
                                  <div className='group flex items-center gap-4 w-full justify-between'>
                                    {/* Left side: indicator and category info */}
                                    <div className='flex items-center gap-4 flex-1 min-w-0'>
                                      {/* Compliance color indicator */}
                                      <div
                                        className='w-1 h-16 rounded-full flex-shrink-0'
                                        style={{
                                          backgroundColor:
                                            compliancePercentage >= 80
                                              ? '#22c55e'
                                              : compliancePercentage >= 50
                                                ? '#eab308'
                                                : '#ef4444'
                                        }}
                                      />

                                      {/* Category icon */}
                                      <div
                                        className={`rounded-lg w-12 h-12 flex items-center justify-center text-sm font-semibold ${
                                          compliancePercentage >= 80
                                            ? 'bg-green-100 text-green-700'
                                            : compliancePercentage >= 50
                                              ? 'bg-yellow-100 text-yellow-700'
                                              : 'bg-red-100 text-red-700'
                                        }`}
                                      >
                                        {category === 'Fire Safety'
                                          ? '🔥'
                                          : category === 'Electrical'
                                            ? '⚡'
                                            : category === 'Water Systems'
                                              ? '💧'
                                              : category === 'Structural'
                                                ? '🏢'
                                                : category ===
                                                    'Lifting Equipment'
                                                  ? '🏗️'
                                                  : category === 'Environmental'
                                                    ? '🌿'
                                                    : category === 'Insurance'
                                                      ? '📋'
                                                      : category === 'Gas'
                                                        ? '🔥'
                                                        : category === 'Access'
                                                          ? '♿'
                                                          : '📄'}
                                      </div>

                                      {/* Category name */}
                                      <div className='flex-1 min-w-0 max-w-md'>
                                        <h3 className='font-semibold text-base truncate'>
                                          {category}
                                        </h3>
                                        <p className='text-sm text-muted-foreground truncate mt-0.5'>
                                          {total} requirement
                                          {total !== 1 ? 's' : ''}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Right side: Stats badges */}
                                    <div className='flex items-center gap-2 flex-shrink-0 ml-auto'>
                                      <Badge
                                        variant='outline'
                                        className='gap-1 border-green-200 bg-green-50 text-green-700'
                                      >
                                        <CheckCircle className='w-3 h-3' />
                                        {compliant}
                                      </Badge>
                                      <Badge
                                        variant='outline'
                                        className='gap-1 border-yellow-200 bg-yellow-50 text-yellow-700'
                                      >
                                        <Clock className='w-3 h-3' />
                                        {dueSoon}
                                      </Badge>
                                      <Badge
                                        variant='outline'
                                        className='gap-1 border-red-200 bg-red-50 text-red-700'
                                      >
                                        <AlertTriangle className='w-3 h-3' />
                                        {overdue}
                                      </Badge>
                                      <div className='flex flex-col items-center gap-0.5 w-20 border-l pl-3'>
                                        <span className='text-xs text-muted-foreground'>
                                          Compliance
                                        </span>
                                        <span className='text-xl font-bold'>
                                          {compliancePercentage}%
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </AccordionTrigger>

                                <AccordionContent className='px-6 pb-4'>
                                  <div className='border rounded-lg'>
                                    <table className='w-full'>
                                      <tbody>
                                        {items.map((req, idx) => {
                                          // Determine status and colors
                                          const isOverdue =
                                            req.next_due_date &&
                                            new Date(req.next_due_date) <
                                              new Date() &&
                                            req.status !== 'compliant';
                                          const isDueSoon =
                                            req.next_due_date &&
                                            !isOverdue &&
                                            req.status !== 'compliant' &&
                                            (() => {
                                              const dueDate = new Date(
                                                req.next_due_date
                                              );
                                              const now = new Date();
                                              const thirtyDaysFromNow =
                                                new Date(
                                                  now.getTime() +
                                                    30 * 24 * 60 * 60 * 1000
                                                );
                                              return (
                                                dueDate >= now &&
                                                dueDate <= thirtyDaysFromNow
                                              );
                                            })();
                                          const isCompliant =
                                            req.status === 'compliant';

                                          const statusColor = isCompliant
                                            ? 'bg-green-500'
                                            : isOverdue
                                              ? 'bg-red-500'
                                              : isDueSoon
                                                ? 'bg-yellow-500'
                                                : 'bg-gray-400';
                                          const statusLabel = isCompliant
                                            ? 'Compliant'
                                            : isOverdue
                                              ? 'Overdue'
                                              : isDueSoon
                                                ? 'Due Soon'
                                                : 'Pending';

                                          return (
                                            <tr
                                              key={req.id}
                                              className='border-b last:border-0 hover:bg-gray-50 transition-colors cursor-pointer'
                                              onClick={() =>
                                                navigate(
                                                  `/space/${projectId}/compliance`
                                                )
                                              }
                                            >
                                              <td className='p-3'>
                                                <div className='flex items-center gap-3'>
                                                  <div
                                                    className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-medium ${
                                                      isCompliant
                                                        ? 'bg-green-500'
                                                        : isOverdue
                                                          ? 'bg-red-500'
                                                          : isDueSoon
                                                            ? 'bg-yellow-500'
                                                            : 'bg-gray-400'
                                                    }`}
                                                  >
                                                    {isCompliant ? (
                                                      <CheckCircle className='w-4 h-4' />
                                                    ) : isOverdue ? (
                                                      <AlertTriangle className='w-4 h-4' />
                                                    ) : isDueSoon ? (
                                                      <Clock className='w-4 h-4' />
                                                    ) : (
                                                      <FileText className='w-4 h-4' />
                                                    )}
                                                  </div>
                                                  <div className='flex-1 min-w-0'>
                                                    <div className='text-sm font-medium text-gray-900'>
                                                      {req.name}
                                                    </div>
                                                    {req.description && (
                                                      <div className='text-xs text-gray-500 truncate max-w-md'>
                                                        {req.description}
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              </td>
                                              <td className='p-3'>
                                                <div className='flex items-center gap-2'>
                                                  <div
                                                    className={`w-2 h-2 rounded-full ${statusColor}`}
                                                  ></div>
                                                  <span className='text-sm font-medium'>
                                                    {statusLabel}
                                                  </span>
                                                </div>
                                              </td>
                                              <td className='p-3'>
                                                {req.next_due_date && (
                                                  <div className='text-sm text-gray-600'>
                                                    <span className='text-gray-500'>
                                                      Due:{' '}
                                                    </span>
                                                    {new Date(
                                                      req.next_due_date
                                                    ).toLocaleDateString()}
                                                  </div>
                                                )}
                                              </td>
                                              <td className='p-3'>
                                                {req.inspection_frequency && (
                                                  <Badge
                                                    variant='secondary'
                                                    className='text-xs'
                                                  >
                                                    {req.inspection_frequency.replace(
                                                      /_/g,
                                                      ' '
                                                    )}
                                                  </Badge>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            </motion.div>
                          )
                        );
                      })()}
                    </AnimatePresence>
                  </Accordion>
                )}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </>
  );

  return (
    <PageTransition>
      <div className='bg-muted/40 h-screen flex flex-col'>
        <div className='relative'>
          <SharedNavbar
            isGlobalToolsOpen={globalToolsOpen}
            onGlobalToolsToggle={handleGlobalToolsToggle}
          />
          <GlobalToolsExpandingPanel
            isOpen={globalToolsOpen}
            onClose={() => setGlobalToolsOpen(false)}
            currentTool={null}
          />

          {/* Space Navigation Bar - TRULY Full width */}
          <div className='w-full bg-white border-b border-gray-200'>
            <div className='flex items-center justify-between py-3 px-6'>
              {/* Left side - Tool switcher + Breadcrumb + Space Filter (for stacks) */}
              <div className='flex items-center gap-3'>
                {/* Space Filter Dropdown - Only show for stack view */}
                {isStackView && stackProjects && stackProjects.length > 0 && (
                  <Select
                    value={selectedSpaceFilter}
                    onValueChange={setSelectedSpaceFilter}
                  >
                    <SelectTrigger className='w-[200px] h-8 text-sm'>
                      <SelectValue placeholder='Filter by space' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='all'>
                        All Spaces ({stackProjects.length})
                      </SelectItem>
                      {stackProjects.map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {/* Tools Dropdown */}
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant='outline'
                      size='sm'
                      className='h-8 px-2 flex items-center gap-1 border border-gray-200 bg-background hover:bg-muted data-[state=open]:bg-muted data-[state=open]:border-gray-300 rounded-md'
                    >
                      <LayoutGrid className='h-4 w-4 text-gray-600' />
                      <ChevronDown className='h-3 w-3 text-gray-500 data-[state=open]:rotate-180 transition-transform' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align='start'
                    sideOffset={8}
                    className='w-[400px] p-4 rounded-lg border border-gray-200 shadow-lg bg-white'
                    onCloseAutoFocus={e => e.preventDefault()}
                  >
                    <div className='grid grid-cols-3 gap-3'>
                      {spaceTools.map(tool => {
                        const Icon = tool.icon;
                        const isActive = tool.tool_type === 'matrix';

                        // Skip tools without valid icons
                        // Accept both function components and React.forwardRef components
                        if (!Icon) {
                          return null;
                        }

                        // Validate it's a renderable React component (function or forwardRef)
                        const isValidComponent =
                          typeof Icon === 'function' ||
                          (typeof Icon === 'object' && Icon.$$typeof);
                        if (!isValidComponent) {
                          return null;
                        }

                        return (
                          <div
                            key={tool.id}
                            className={`relative flex flex-col items-center p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                              isActive
                                ? 'border-blue-300 bg-blue-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                            onClick={e => {
                              // Navigate to different tools based on type
                              if (tool.tool_type === 'matrix') {
                                // Already on matrix page, do nothing
                                return;
                              }

                              // Map tool types to their routes
                              const toolRoutes = {
                                team: `/space/${projectId}/team`,
                                compliance: `/space/${projectId}/compliance`,
                                bookings: `/space/${projectId}/bookings`,
                                qualifications: `/space/${projectId}/qualifications`,
                                chat: `/chat?space=${projectId}`,
                                message_board: `/space/${projectId}/message-board`,
                                check_ins: `/space/${projectId}/checkins`,
                                checkins: `/space/${projectId}/checkins`,
                                calendar: `/space/${projectId}/schedule`,
                                checklists: `/space/${projectId}/checklists`,
                                docs: `/space/${projectId}/docs`
                              };

                              const route = toolRoutes[tool.tool_type];
                              if (route) {
                                // Check if ctrl/cmd + click for new tab
                                if (e.ctrlKey || e.metaKey) {
                                  window.open(route, '_blank');
                                } else {
                                  navigate(route);
                                }
                              } else {
                                // Fallback to space overview
                                if (e.ctrlKey || e.metaKey) {
                                  window.open(`/space/${projectId}`, '_blank');
                                } else {
                                  navigate(`/space/${projectId}`);
                                }
                              }
                            }}
                            onAuxClick={e => {
                              // Handle middle click (mouse wheel click)
                              if (e.button === 1) {
                                e.preventDefault();

                                if (tool.tool_type === 'matrix') {
                                  return;
                                }

                                const toolRoutes = {
                                  team: `/space/${projectId}/team`,
                                  compliance: `/space/${projectId}/compliance`,
                                  bookings: `/space/${projectId}/bookings`,
                                  qualifications: `/space/${projectId}/qualifications`,
                                  chat: `/chat?space=${projectId}`,
                                  message_board: `/space/${projectId}/message-board`,
                                  check_ins: `/space/${projectId}/checkins`,
                                  checkins: `/space/${projectId}/checkins`,
                                  calendar: `/space/${projectId}/schedule`,
                                  checklists: `/space/${projectId}/checklists`,
                                  docs: `/space/${projectId}/docs`
                                };

                                const route = toolRoutes[tool.tool_type];
                                if (route) {
                                  window.open(route, '_blank');
                                } else {
                                  window.open(`/space/${projectId}`, '_blank');
                                }
                              }
                            }}
                          >
                            <div
                              className='w-12 h-12 rounded-lg flex items-center justify-center mb-2 flex-shrink-0'
                              style={{ backgroundColor: tool.color + '20' }}
                            >
                              {Icon && (
                                <Icon
                                  className='h-6 w-6'
                                  style={{ color: tool.color }}
                                />
                              )}
                            </div>
                            <div className='h-8 flex items-center justify-center'>
                              <span className='text-xs font-medium text-center line-clamp-2 w-full leading-tight'>
                                {tool.name}
                              </span>
                            </div>
                            {isActive && (
                              <div className='absolute top-2 right-2'>
                                <CheckCircle className='h-4 w-4 text-blue-600' />
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {spaceTools.length === 0 && (
                        <div className='col-span-3 text-sm text-gray-500 text-center py-4'>
                          No tools configured
                        </div>
                      )}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Breadcrumb with editable Matrix name */}
                <EditableMatrixName
                  initialName={matrixName}
                  spaceName={space?.name || 'Space'}
                  projectId={projectId}
                  onNameChange={setMatrixName}
                  isStackView={isStackView}
                  stackId={stackId}
                  stackName={stackName}
                />
              </div>

              {/* Right side - Import, Search, Export & View Toggle */}
              <div className='flex items-center gap-3'>
                {/* Import Button - Only show for single space view */}
                {/* {!isStackView && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/space/${projectId}/import-matrix`)}
                className="h-8 gap-1.5 text-xs"
              >
                <FileUp className="h-3.5 w-3.5" />
                Import
              </Button>
            )} */}

                {/* Search Bar */}
                <div className='relative'>
                  <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                  <Input
                    type='text'
                    placeholder='Search employees & qualifications...'
                    value={employeeSearchTerm}
                    onChange={e => setEmployeeSearchTerm(e.target.value)}
                    className='w-80 h-8 pl-9 pr-4 text-sm bg-white'
                  />
                  {employeeSearchTerm && (
                    <button
                      onClick={() => setEmployeeSearchTerm('')}
                      className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600'
                    >
                      <X className='h-3 w-3' />
                    </button>
                  )}
                </div>

                {/* Sort Dropdown - Only show in qualifications/employees view */}
                {(viewMode === 'qualifications' ||
                  viewMode === 'employees') && (
                  <Select
                    value={qualificationsSortBy}
                    onValueChange={setQualificationsSortBy}
                  >
                    <SelectTrigger className='w-[200px] h-8'>
                      <div className='flex items-center gap-2'>
                        <ArrowUpDown className='h-3.5 w-3.5' />
                        <span className='text-sm'>
                          {qualificationsSortBy === 'name-asc' && 'Name (A-Z)'}
                          {qualificationsSortBy === 'name-desc' && 'Name (Z-A)'}
                          {qualificationsSortBy === 'compliance-asc' &&
                            'Compliance ↑'}
                          {qualificationsSortBy === 'compliance-desc' &&
                            'Compliance ↓'}
                          {qualificationsSortBy === 'gap-desc' && 'Gap (Most)'}
                          {qualificationsSortBy === 'gap-asc' && 'Gap (Least)'}
                          {qualificationsSortBy === 'missing-desc' &&
                            'Missing (Most)'}
                          {qualificationsSortBy === 'missing-asc' &&
                            'Missing (Least)'}
                        </span>
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='name-asc'>
                        <div className='flex items-center gap-2'>
                          <ArrowUp className='h-3 w-3' />
                          Name (A-Z)
                        </div>
                      </SelectItem>
                      <SelectItem value='name-desc'>
                        <div className='flex items-center gap-2'>
                          <ArrowDown className='h-3 w-3' />
                          Name (Z-A)
                        </div>
                      </SelectItem>
                      <SelectItem value='compliance-asc'>
                        <div className='flex items-center gap-2'>
                          <ArrowUp className='h-3 w-3' />
                          Compliance (Low-High)
                        </div>
                      </SelectItem>
                      <SelectItem value='compliance-desc'>
                        <div className='flex items-center gap-2'>
                          <ArrowDown className='h-3 w-3' />
                          Compliance (High-Low)
                        </div>
                      </SelectItem>
                      <SelectItem value='gap-asc'>
                        <div className='flex items-center gap-2'>
                          <ArrowUp className='h-3 w-3' />
                          Gap (Least first)
                        </div>
                      </SelectItem>
                      <SelectItem value='gap-desc'>
                        <div className='flex items-center gap-2'>
                          <ArrowDown className='h-3 w-3' />
                          Gap (Most first)
                        </div>
                      </SelectItem>
                      <SelectItem value='missing-asc'>
                        <div className='flex items-center gap-2'>
                          <ArrowUp className='h-3 w-3' />
                          Missing (Least first)
                        </div>
                      </SelectItem>
                      <SelectItem value='missing-desc'>
                        <div className='flex items-center gap-2'>
                          <ArrowDown className='h-3 w-3' />
                          Missing (Most first)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}

                {/* View Toggle - Animated sliding background */}
                <div className='relative inline-flex items-center p-1 bg-gray-100 rounded-lg'>
                  {/* Sliding background indicator */}
                  <motion.div
                    className='absolute h-[32px] bg-white shadow-sm rounded-md pointer-events-none'
                    initial={false}
                    animate={{
                      x:
                        viewMode === 'grid'
                          ? 0
                          : viewMode === 'qualifications'
                            ? 84
                            : viewMode === 'employees'
                              ? 220
                              : 330,
                      width:
                        viewMode === 'grid'
                          ? 84
                          : viewMode === 'qualifications'
                            ? 136
                            : viewMode === 'employees'
                              ? 110
                              : 70
                    }}
                    transition={{
                      type: 'spring',
                      stiffness: 400,
                      damping: 30,
                      mass: 0.8
                    }}
                    style={{ left: '4px' }}
                  />

                  {/* Matrix button */}
                  <button
                    className={`relative flex items-center justify-center px-3 py-1.5 transition-colors duration-200 flex-shrink-0 ${
                      viewMode === 'grid'
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => setViewMode('grid')}
                    style={{ width: '84px' }}
                  >
                    <Grid3X3 className='h-4 w-4 mr-2' />
                    <span className='text-xs font-medium'>Matrix</span>
                  </button>

                  {/* Qualifications button */}
                  <button
                    className={`relative flex items-center justify-center px-3 py-1.5 transition-colors duration-200 flex-shrink-0 ${
                      viewMode === 'qualifications'
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => setViewMode('qualifications')}
                    style={{ width: '136px' }}
                  >
                    <Award className='h-4 w-4 mr-2' />
                    <span className='text-xs font-medium'>Qualifications</span>
                  </button>

                  {/* Employees button */}
                  <button
                    className={`relative flex items-center justify-center px-3 py-1.5 transition-colors duration-200 flex-shrink-0 ${
                      viewMode === 'employees'
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => setViewMode('employees')}
                    style={{ width: '110px' }}
                  >
                    <Users className='h-4 w-4 mr-2' />
                    <span className='text-xs font-medium'>Employees</span>
                  </button>

                  {/* Site button */}
                  <button
                    className={`relative flex items-center justify-center px-3 py-1.5 transition-colors duration-200 flex-shrink-0 ${
                      viewMode === 'site'
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => setViewMode('site')}
                    style={{ width: '70px' }}
                  >
                    <MapPin className='h-4 w-4 mr-2' />
                    <span className='text-xs font-medium'>Site</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* End relative wrapper for nav drop zone */}

        {/* Main Content with Sidebar */}
        <div className='flex w-full relative flex-1 overflow-hidden'>
          {/* Main Matrix Content */}
          <div className='flex-1 h-full relative overflow-hidden'>
            {matrixContent}
          </div>

          {/* Right Sidebar - Gap Analysis Panel - DISABLED */}
          {false && (
            <>
              <div
                className={`bg-gray-50 border-l border-gray-200 flex flex-col flex-shrink-0 ${
                  sidebarExpanded ? 'w-72' : 'w-16'
                }`}
                style={{
                  transition: 'width 0.5s ease-in-out'
                }}
              >
                {/* Sidebar Header */}
                <div
                  className={`border-b border-gray-200 bg-white flex items-center flex-shrink-0 ${sidebarExpanded ? 'justify-between' : 'justify-center'}`}
                  style={{
                    height: '64px !important',
                    minHeight: '64px',
                    maxHeight: '64px',
                    paddingLeft: sidebarExpanded ? '12px' : '4px',
                    paddingRight: sidebarExpanded ? '12px' : '4px',
                    paddingTop: '0',
                    paddingBottom: '0'
                  }}
                >
                  <button
                    onClick={() => setSidebarExpanded(!sidebarExpanded)}
                    className={`flex items-center hover:bg-blue-50 rounded-lg ${!sidebarExpanded ? 'flex-col gap-0.5 p-1.5' : 'gap-1 p-2'}`}
                    title={
                      sidebarExpanded
                        ? 'Collapse Gap Analysis'
                        : 'Expand Gap Analysis'
                    }
                  >
                    <BarChart3
                      className={`text-blue-600 flex-shrink-0 ${
                        sidebarExpanded ? 'h-5 w-5' : 'h-7 w-7'
                      }`}
                      style={{ transition: 'none' }}
                    />
                    <ChevronRight
                      className={`text-blue-600 flex-shrink-0 ${
                        sidebarExpanded ? 'h-4 w-4' : 'h-5 w-5'
                      }`}
                      style={{
                        transform: sidebarExpanded
                          ? 'rotate(0deg)'
                          : 'rotate(180deg)',
                        transition: 'transform 0.5s ease-in-out'
                      }}
                    />
                  </button>

                  {/* Title and Clear button */}
                  {sidebarExpanded && (
                    <div className='flex items-center justify-between flex-1'>
                      <h3 className='text-sm font-semibold text-gray-900 flex-1 text-center whitespace-nowrap'>
                        Gap Analysis
                      </h3>
                      <button
                        onClick={clearActiveFilters}
                        className='text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 whitespace-nowrap'
                        title='Clear all filters'
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>

                {/* Sidebar Content */}
                <div
                  className='flex-1 overflow-y-auto'
                  onWheel={e => {
                    // Prevent scroll from propagating to parent (matrix)
                    e.stopPropagation();
                  }}
                >
                  <AnimatePresence mode='wait'>
                    {sidebarExpanded ? (
                      <motion.div
                        key='expanded'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className='p-3'
                      >
                        {/* Quick Stats Summary */}
                        <div className='mb-4 p-3 bg-white rounded-lg border border-gray-200'>
                          <div className='grid grid-cols-2 gap-2 text-xs'>
                            <div className='text-gray-600'>Total Team:</div>
                            <div className='font-semibold text-right'>
                              {sidebarStats.totalEmployees || 0}
                            </div>
                            <div className='text-gray-600'>Requirements:</div>
                            <div className='font-semibold text-right'>
                              {sidebarStats.totalRequirements || 0}
                            </div>
                            <div className='text-gray-600 col-span-2 pt-1 mt-1 border-t border-gray-200'></div>
                            <div className='text-gray-600'>
                              Compliance Rate:
                            </div>
                            <div
                              className={`font-semibold text-right ${overallComplianceRate >= 80 ? 'text-green-600' : overallComplianceRate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}
                            >
                              {overallComplianceRate}%
                            </div>
                            <div className='text-gray-600 flex items-center gap-1'>
                              Projected:
                              <TooltipProvider>
                                <Tooltip delayDuration={200}>
                                  <TooltipTrigger asChild>
                                    <Info className='h-3 w-3 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors' />
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side='top'
                                    className='text-xs'
                                  >
                                    <p>Includes booked training</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <div
                              className={`font-semibold text-right ${projectedComplianceRate >= 80 ? 'text-blue-600' : projectedComplianceRate >= 50 ? 'text-yellow-600' : 'text-orange-600'}`}
                            >
                              {projectedComplianceRate}%
                            </div>
                          </div>
                        </div>

                        {/* Status Filter Grid */}
                        <div className='grid grid-cols-3 gap-2'>
                          {/* Valid */}
                          <button
                            onClick={() => toggleFilter('compliant')}
                            className={`relative group flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                              activeFilters.has('compliant')
                                ? 'bg-green-500 text-white'
                                : 'bg-white border border-gray-200 hover:border-green-300'
                            }`}
                            title='Valid'
                          >
                            <CheckCircle className='h-5 w-5 mb-1' />
                            {sidebarStats.compliant > 0 && (
                              <span
                                className={`absolute -top-1 -right-1 bg-green-600 text-white text-[9px] rounded-full h-4 flex items-center justify-center font-bold ${
                                  sidebarStats.compliant > 99
                                    ? 'px-1 min-w-[20px]'
                                    : 'w-4'
                                }`}
                              >
                                {sidebarStats.compliant > 99
                                  ? '99+'
                                  : sidebarStats.compliant}
                              </span>
                            )}
                            <span className='text-[10px] font-medium mt-0.5'>
                              Valid
                            </span>
                          </button>

                          {/* Expiring */}
                          <button
                            onClick={() => toggleFilter('expiring')}
                            className={`relative group flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                              activeFilters.has('expiring')
                                ? 'bg-yellow-500 text-white'
                                : 'bg-white border border-gray-200 hover:border-yellow-300'
                            }`}
                            title='Expiring Soon'
                          >
                            <Clock className='h-5 w-5 mb-1' />
                            {sidebarStats.expiring > 0 && (
                              <span
                                className={`absolute -top-1 -right-1 bg-yellow-600 text-white text-[9px] rounded-full h-4 flex items-center justify-center font-bold ${
                                  sidebarStats.expiring > 99
                                    ? 'px-1 min-w-[20px]'
                                    : 'w-4'
                                }`}
                              >
                                {sidebarStats.expiring > 99
                                  ? '99+'
                                  : sidebarStats.expiring}
                              </span>
                            )}
                            <span className='text-[10px] font-medium mt-0.5'>
                              Expiring
                            </span>
                          </button>

                          {/* Expired */}
                          <button
                            onClick={() => toggleFilter('expired')}
                            className={`relative group flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                              activeFilters.has('expired')
                                ? 'bg-red-500 text-white'
                                : 'bg-white border border-gray-200 hover:border-red-300'
                            }`}
                            title='Expired'
                          >
                            <AlertTriangle className='h-5 w-5 mb-1' />
                            {sidebarStats.expired > 0 && (
                              <span
                                className={`absolute -top-1 -right-1 bg-red-600 text-white text-[9px] rounded-full h-4 flex items-center justify-center font-bold ${
                                  sidebarStats.expired > 99
                                    ? 'px-1 min-w-[20px]'
                                    : 'w-4'
                                }`}
                              >
                                {sidebarStats.expired > 99
                                  ? '99+'
                                  : sidebarStats.expired}
                              </span>
                            )}
                            <span className='text-[10px] font-medium mt-0.5'>
                              Expired
                            </span>
                          </button>

                          {/* Missing */}
                          <button
                            onClick={() => toggleFilter('missing')}
                            className={`relative group flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                              activeFilters.has('missing')
                                ? 'bg-red-500 text-white'
                                : 'bg-white border border-gray-200 hover:border-red-300'
                            }`}
                            title='Missing'
                          >
                            <XCircle className='h-5 w-5 mb-1' />
                            {sidebarStats.missing > 0 && (
                              <span
                                className={`absolute -top-1 -right-1 bg-red-600 text-white text-[9px] rounded-full h-4 flex items-center justify-center font-bold ${
                                  sidebarStats.missing > 99
                                    ? 'px-1 min-w-[20px]'
                                    : 'w-4'
                                }`}
                              >
                                {sidebarStats.missing > 99
                                  ? '99+'
                                  : sidebarStats.missing}
                              </span>
                            )}
                            <span className='text-[10px] font-medium mt-0.5'>
                              Missing
                            </span>
                          </button>

                          {/* Quotes */}
                          <button
                            onClick={() => toggleFilter('quotes')}
                            className={`relative group flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                              activeFilters.has('quotes')
                                ? 'bg-purple-500 text-white'
                                : 'bg-white border border-gray-200 hover:border-purple-300'
                            }`}
                            title='Quotes Requested'
                          >
                            <FileText className='h-5 w-5 mb-1' />
                            {sidebarStats.quotes > 0 && (
                              <span
                                className={`absolute -top-1 -right-1 bg-purple-600 text-white text-[9px] rounded-full h-4 flex items-center justify-center font-bold ${
                                  sidebarStats.quotes > 99
                                    ? 'px-1 min-w-[20px]'
                                    : 'w-4'
                                }`}
                              >
                                {sidebarStats.quotes > 99
                                  ? '99+'
                                  : sidebarStats.quotes}
                              </span>
                            )}
                            <span className='text-[10px] font-medium mt-0.5'>
                              Quotes
                            </span>
                          </button>

                          {/* Booked */}
                          <button
                            onClick={() => toggleFilter('booked')}
                            className={`relative group flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                              activeFilters.has('booked')
                                ? 'bg-blue-500 text-white'
                                : 'bg-white border border-gray-200 hover:border-blue-300'
                            }`}
                            title='Training Booked'
                          >
                            <Calendar className='h-5 w-5 mb-1' />
                            {sidebarStats.booked > 0 && (
                              <span
                                className={`absolute -top-1 -right-1 bg-blue-600 text-white text-[9px] rounded-full h-4 flex items-center justify-center font-bold ${
                                  sidebarStats.booked > 99
                                    ? 'px-1 min-w-[20px]'
                                    : 'w-4'
                                }`}
                              >
                                {sidebarStats.booked > 99
                                  ? '99+'
                                  : sidebarStats.booked}
                              </span>
                            )}
                            <span className='text-[10px] font-medium mt-0.5'>
                              Booked
                            </span>
                          </button>
                        </div>

                        {/* Divider */}
                        <div className='my-3 border-t border-gray-200'></div>

                        {/* Assignment Status Section */}
                        <div className='mb-2'>
                          <h4 className='text-[10px] font-semibold text-gray-600 uppercase tracking-wide px-1 mb-2'>
                            Assignment Status
                          </h4>
                          <div className='grid grid-cols-2 gap-2'>
                            {/* Assigned */}
                            <button
                              onClick={() => toggleFilter('assigned')}
                              className={`relative group flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                                activeFilters.has('assigned')
                                  ? 'bg-indigo-500 text-white'
                                  : 'bg-white border border-gray-200 hover:border-indigo-300'
                              }`}
                              title='Assigned to Track'
                            >
                              <CheckSquare className='h-5 w-5 mb-1' />
                              <span className='text-[10px] font-medium mt-0.5'>
                                Assigned
                              </span>
                            </button>

                            {/* Unassigned */}
                            <button
                              onClick={() => toggleFilter('unassigned')}
                              className={`relative group flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                                activeFilters.has('unassigned')
                                  ? 'bg-gray-500 text-white'
                                  : 'bg-white border border-gray-200 hover:border-gray-300'
                              }`}
                              title='Not Assigned'
                            >
                              <Eye className='h-5 w-5 mb-1' />
                              <span className='text-[10px] font-medium mt-0.5'>
                                Unassigned
                              </span>
                            </button>
                          </div>
                        </div>

                        {/* Divider */}
                        <div className='my-3 border-t border-gray-200'></div>

                        {/* Coverage Perspective Toggle */}
                        <div className='relative flex items-center p-1 bg-gray-100 rounded-lg mb-2'>
                          {/* Sliding background indicator */}
                          <motion.div
                            className='absolute h-[28px] bg-white shadow-sm rounded-md left-1'
                            initial={false}
                            animate={{
                              x:
                                coveragePerspective === 'requirement'
                                  ? 0
                                  : 'calc(100% + 4px)'
                            }}
                            transition={{
                              type: 'spring',
                              stiffness: 400,
                              damping: 30,
                              mass: 0.8
                            }}
                            style={{ width: 'calc(50% - 8px)' }}
                          />

                          {/* Requirement button */}
                          <button
                            className={`relative flex-1 flex items-center justify-center px-2 py-1 transition-colors duration-200 ${
                              coveragePerspective === 'requirement'
                                ? 'text-foreground'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                            onClick={() =>
                              setCoveragePerspective('requirement')
                            }
                          >
                            <ClipboardCheck className='h-3.5 w-3.5 mr-1.5' />
                            <span className='text-xs font-medium'>
                              Requirements
                            </span>
                          </button>

                          {/* Employee button */}
                          <button
                            className={`relative flex-1 flex items-center justify-center px-2 py-1 transition-colors duration-200 ${
                              coveragePerspective === 'employee'
                                ? 'text-foreground'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                            onClick={() => setCoveragePerspective('employee')}
                          >
                            <Users className='h-3.5 w-3.5 mr-1.5' />
                            <span className='text-xs font-medium'>
                              Employees
                            </span>
                          </button>
                        </div>

                        {/* Coverage Section */}
                        <div className='space-y-2'>
                          {/* Requirement Perspective */}
                          {coveragePerspective === 'requirement' && (
                            <Accordion
                              type='single'
                              collapsible
                              className='space-y-1.5'
                            >
                              {requirements.map(req => {
                                // Get stats from qualStatsMap (uses transformed statuses)
                                const stats = qualStatsMap.get(req.id) || {
                                  totalAssigned: 0,
                                  valid: 0,
                                  expiring: 0,
                                  expired: 0,
                                  missing: 0,
                                  booked: 0,
                                  quoting: 0,
                                  required: 0,
                                  compliancePercentage: 0
                                };

                                const neededCount = stats.required;
                                const statusBreakdown = {
                                  valid: stats.valid,
                                  expiring: stats.expiring,
                                  expired: stats.expired,
                                  booked: stats.booked,
                                  quoting: stats.quoting,
                                  missing: stats.missing
                                };

                                // Compliance includes valid + expiring + cross-space qualifications
                                const compliantCount =
                                  stats.valid +
                                  stats.validCrossSpace +
                                  stats.expiring +
                                  stats.expiringCrossSpace;
                                const percentage = stats.compliancePercentage;
                                const isCompliant =
                                  compliantCount >= neededCount;

                                // Calculate percentage widths for multi-colored progress bar
                                const validPercentage =
                                  neededCount > 0
                                    ? (stats.valid / neededCount) * 100
                                    : 0;
                                const validCrossSpacePercentage =
                                  neededCount > 0
                                    ? (stats.validCrossSpace / neededCount) *
                                      100
                                    : 0;
                                const expiringPercentage =
                                  neededCount > 0
                                    ? (stats.expiring / neededCount) * 100
                                    : 0;
                                const expiringCrossSpacePercentage =
                                  neededCount > 0
                                    ? (stats.expiringCrossSpace / neededCount) *
                                      100
                                    : 0;
                                const expiredPercentage =
                                  neededCount > 0
                                    ? (stats.expired / neededCount) * 100
                                    : 0;
                                const missingPercentage =
                                  neededCount > 0
                                    ? (stats.missing / neededCount) * 100
                                    : 0;

                                return (
                                  <AccordionItem
                                    key={req.id}
                                    value={req.id}
                                    className='bg-white rounded-lg border border-gray-200'
                                  >
                                    <AccordionTrigger className='p-2.5 hover:no-underline'>
                                      <div className='w-full'>
                                        <div className='flex items-start justify-between mb-1.5 gap-2'>
                                          <span className='text-xs font-medium text-gray-700 flex-1 text-left break-words'>
                                            {req.name}
                                          </span>
                                          <span
                                            className={`text-xs font-semibold whitespace-nowrap ${
                                              isCompliant
                                                ? 'text-green-600'
                                                : 'text-amber-600'
                                            }`}
                                          >
                                            {compliantCount}/{neededCount}
                                          </span>
                                        </div>
                                        {/* Multi-colored progress bar */}
                                        <div className='w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mr-6 flex'>
                                          {/* Valid (Green) */}
                                          {validPercentage > 0 && (
                                            <div
                                              className='h-full bg-green-500 transition-all duration-300'
                                              style={{
                                                width: `${Math.min(100, validPercentage)}%`
                                              }}
                                            />
                                          )}
                                          {/* Valid Cross-Space (Light Green) */}
                                          {validCrossSpacePercentage > 0 && (
                                            <div
                                              className='h-full bg-green-300 transition-all duration-300'
                                              style={{
                                                width: `${Math.min(100 - validPercentage, validCrossSpacePercentage)}%`
                                              }}
                                            />
                                          )}
                                          {/* Expiring (Yellow) */}
                                          {expiringPercentage > 0 && (
                                            <div
                                              className='h-full bg-yellow-500 transition-all duration-300'
                                              style={{
                                                width: `${Math.min(100 - validPercentage - validCrossSpacePercentage, expiringPercentage)}%`
                                              }}
                                            />
                                          )}
                                          {/* Expiring Cross-Space (Light Yellow) */}
                                          {expiringCrossSpacePercentage > 0 && (
                                            <div
                                              className='h-full bg-yellow-300 transition-all duration-300'
                                              style={{
                                                width: `${Math.min(100 - validPercentage - validCrossSpacePercentage - expiringPercentage, expiringCrossSpacePercentage)}%`
                                              }}
                                            />
                                          )}
                                          {/* Expired (Red) */}
                                          {expiredPercentage > 0 && (
                                            <div
                                              className='h-full bg-red-500 transition-all duration-300'
                                              style={{
                                                width: `${Math.min(100 - validPercentage - validCrossSpacePercentage - expiringPercentage - expiringCrossSpacePercentage, expiredPercentage)}%`
                                              }}
                                            />
                                          )}
                                          {/* Missing (Red) */}
                                          {missingPercentage > 0 && (
                                            <div
                                              className='h-full bg-red-500 transition-all duration-300'
                                              style={{
                                                width: `${Math.min(100 - validPercentage - validCrossSpacePercentage - expiringPercentage - expiringCrossSpacePercentage - expiredPercentage, missingPercentage)}%`
                                              }}
                                            />
                                          )}
                                        </div>
                                      </div>
                                    </AccordionTrigger>
                                    <AccordionContent className='px-2.5 pb-2.5'>
                                      <div className='space-y-1.5 pt-2 border-t border-gray-100'>
                                        {/* Status breakdown - always show all statuses */}
                                        <div className='space-y-1'>
                                          <div className='bg-green-50 rounded px-2 py-1'>
                                            <div className='flex items-center justify-between'>
                                              <span className='text-xs text-green-700'>
                                                Valid
                                              </span>
                                              <span className='text-xs font-semibold text-green-800'>
                                                {stats.valid +
                                                  stats.validCrossSpace}
                                              </span>
                                            </div>
                                            <div className='flex items-center gap-2 text-[10px] text-green-600 opacity-70 mt-0.5'>
                                              <span>
                                                Assigned: {stats.valid}
                                              </span>
                                              <span>
                                                Unassigned:{' '}
                                                {stats.validCrossSpace}
                                              </span>
                                            </div>
                                          </div>
                                          <div className='bg-yellow-50 rounded px-2 py-1'>
                                            <div className='flex items-center justify-between'>
                                              <span className='text-xs text-yellow-700'>
                                                Expiring Soon
                                              </span>
                                              <span className='text-xs font-semibold text-yellow-800'>
                                                {stats.expiring +
                                                  stats.expiringCrossSpace}
                                              </span>
                                            </div>
                                            <div className='flex items-center gap-2 text-[10px] text-yellow-600 opacity-70 mt-0.5'>
                                              <span>
                                                Assigned: {stats.expiring}
                                              </span>
                                              <span>
                                                Unassigned:{' '}
                                                {stats.expiringCrossSpace}
                                              </span>
                                            </div>
                                          </div>
                                          <div className='flex items-center justify-between bg-red-50 rounded px-2 py-1'>
                                            <span className='text-xs text-red-700'>
                                              Expired
                                            </span>
                                            <span className='text-xs font-semibold text-red-800'>
                                              {statusBreakdown.expired}
                                            </span>
                                          </div>
                                          <div className='flex items-center justify-between bg-primary/10 rounded px-2 py-1'>
                                            <span className='text-xs text-primary'>
                                              Training Booked
                                            </span>
                                            <span className='text-xs font-semibold text-primary'>
                                              {statusBreakdown.booked}
                                            </span>
                                          </div>
                                          <div className='flex items-center justify-between bg-primary/10 rounded px-2 py-1'>
                                            <span className='text-xs text-primary'>
                                              Quote Requested
                                            </span>
                                            <span className='text-xs font-semibold text-primary'>
                                              {statusBreakdown.quoting}
                                            </span>
                                          </div>
                                          <div className='flex items-center justify-between bg-red-50 rounded px-2 py-1'>
                                            <span className='text-xs text-red-700'>
                                              Missing
                                            </span>
                                            <span className='text-xs font-semibold text-red-800'>
                                              {statusBreakdown.missing}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </AccordionContent>
                                  </AccordionItem>
                                );
                              })}
                              {requirements.length === 0 && (
                                <div className='text-xs text-gray-500 text-center py-2'>
                                  No requirements configured
                                </div>
                              )}
                            </Accordion>
                          )}

                          {/* Employee Perspective */}
                          {coveragePerspective === 'employee' && (
                            <Accordion
                              type='single'
                              collapsible
                              className='space-y-1.5'
                            >
                              {filteredTeamMembers.map(member => {
                                // Get stats from employeeStatsMap (uses transformed statuses)
                                const stats = employeeStatsMap.get(
                                  member.id
                                ) || {
                                  totalAssigned: 0,
                                  valid: 0,
                                  expiring: 0,
                                  expired: 0,
                                  missing: 0,
                                  booked: 0,
                                  quoting: 0,
                                  compliancePercentage: 0
                                };

                                const hasAssignments = stats.totalAssigned > 0;
                                const validCount = stats.valid;
                                const expiringCount = stats.expiring;
                                const expiredCount = stats.expired;
                                const bookedCount = stats.booked;
                                const quotingCount = stats.quoting;
                                const missingCount = stats.missing;

                                const totalAssigned = stats.totalAssigned;
                                const compliantCount =
                                  validCount + expiringCount;
                                const percentage = hasAssignments
                                  ? stats.compliancePercentage
                                  : null;
                                const isCompliant =
                                  hasAssignments &&
                                  compliantCount >= totalAssigned;

                                // Calculate percentage widths for multi-colored progress bar
                                const validPercentage =
                                  totalAssigned > 0
                                    ? (validCount / totalAssigned) * 100
                                    : 0;
                                const expiringPercentage =
                                  totalAssigned > 0
                                    ? (expiringCount / totalAssigned) * 100
                                    : 0;
                                const expiredPercentage =
                                  totalAssigned > 0
                                    ? (expiredCount / totalAssigned) * 100
                                    : 0;
                                const missingPercentage =
                                  totalAssigned > 0
                                    ? (missingCount / totalAssigned) * 100
                                    : 0;

                                return (
                                  <AccordionItem
                                    key={member.id}
                                    value={member.id}
                                    className='bg-white rounded-lg border border-gray-200'
                                  >
                                    <AccordionTrigger className='p-2.5 hover:no-underline'>
                                      <div className='w-full'>
                                        <div className='flex items-start justify-between mb-1.5 gap-2'>
                                          <span className='text-xs font-medium text-gray-700 flex-1 text-left break-words'>
                                            {member.name}
                                          </span>
                                          <span
                                            className={`text-xs font-semibold whitespace-nowrap ${
                                              !hasAssignments
                                                ? 'text-gray-400'
                                                : isCompliant
                                                  ? 'text-green-600'
                                                  : 'text-amber-600'
                                            }`}
                                          >
                                            {hasAssignments
                                              ? `${compliantCount}/${totalAssigned}`
                                              : 'N/A'}
                                          </span>
                                        </div>
                                        {/* Multi-colored progress bar */}
                                        <div
                                          className={`w-full h-1.5 rounded-full overflow-hidden mr-6 flex ${!hasAssignments ? 'bg-gray-200' : 'bg-gray-100'}`}
                                        >
                                          {/* Valid (Green) */}
                                          {validPercentage > 0 && (
                                            <div
                                              className='h-full bg-green-500 transition-all duration-300'
                                              style={{
                                                width: `${Math.min(100, validPercentage)}%`
                                              }}
                                            />
                                          )}
                                          {/* Expiring (Yellow) */}
                                          {expiringPercentage > 0 && (
                                            <div
                                              className='h-full bg-yellow-500 transition-all duration-300'
                                              style={{
                                                width: `${Math.min(100 - validPercentage, expiringPercentage)}%`
                                              }}
                                            />
                                          )}
                                          {/* Expired (Red) */}
                                          {expiredPercentage > 0 && (
                                            <div
                                              className='h-full bg-red-500 transition-all duration-300'
                                              style={{
                                                width: `${Math.min(100 - validPercentage - expiringPercentage, expiredPercentage)}%`
                                              }}
                                            />
                                          )}
                                          {/* Missing (Red) */}
                                          {missingPercentage > 0 && (
                                            <div
                                              className='h-full bg-red-500 transition-all duration-300'
                                              style={{
                                                width: `${Math.min(100 - validPercentage - expiringPercentage - expiredPercentage, missingPercentage)}%`
                                              }}
                                            />
                                          )}
                                        </div>
                                      </div>
                                    </AccordionTrigger>
                                    <AccordionContent className='px-2.5 pb-2.5'>
                                      <div className='space-y-1.5 pt-2 border-t border-gray-100'>
                                        {/* Status breakdown */}
                                        <div className='space-y-1'>
                                          <div className='flex items-center justify-between bg-green-50 rounded px-2 py-1'>
                                            <span className='text-xs text-green-700'>
                                              Valid
                                            </span>
                                            <span className='text-xs font-semibold text-green-800'>
                                              {validCount}
                                            </span>
                                          </div>
                                          <div className='flex items-center justify-between bg-yellow-50 rounded px-2 py-1'>
                                            <span className='text-xs text-yellow-700'>
                                              Expiring Soon
                                            </span>
                                            <span className='text-xs font-semibold text-yellow-800'>
                                              {expiringCount}
                                            </span>
                                          </div>
                                          <div className='flex items-center justify-between bg-red-50 rounded px-2 py-1'>
                                            <span className='text-xs text-red-700'>
                                              Expired
                                            </span>
                                            <span className='text-xs font-semibold text-red-800'>
                                              {expiredCount}
                                            </span>
                                          </div>
                                          <div className='flex items-center justify-between bg-primary/10 rounded px-2 py-1'>
                                            <span className='text-xs text-primary'>
                                              Quote Requested
                                            </span>
                                            <span className='text-xs font-semibold text-primary'>
                                              {quotingCount}
                                            </span>
                                          </div>
                                          <div className='flex items-center justify-between bg-primary/10 rounded px-2 py-1'>
                                            <span className='text-xs text-primary'>
                                              Training Booked
                                            </span>
                                            <span className='text-xs font-semibold text-primary'>
                                              {bookedCount}
                                            </span>
                                          </div>
                                          <div className='flex items-center justify-between bg-red-50 rounded px-2 py-1'>
                                            <span className='text-xs text-red-700'>
                                              Missing
                                            </span>
                                            <span className='text-xs font-semibold text-red-800'>
                                              {missingCount}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </AccordionContent>
                                  </AccordionItem>
                                );
                              })}
                              {filteredTeamMembers.filter(m => {
                                const stats = employeeStatsMap.get(m.id);
                                return stats && stats.totalAssigned > 0;
                              }).length === 0 && (
                                <div className='text-xs text-gray-500 text-center py-2'>
                                  No team members with assigned requirements
                                </div>
                              )}
                            </Accordion>
                          )}
                        </div>
                      </motion.div>
                    ) : (
                      /* Collapsed view - compact icons */
                      <motion.div
                        key='collapsed'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className='flex flex-col items-center space-y-2 py-2'
                      >
                        {/* Valid */}
                        <button
                          onClick={() => toggleFilter('compliant')}
                          className={`relative group w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                            activeFilters.has('compliant')
                              ? 'bg-green-500 text-white'
                              : 'bg-white border border-gray-200 hover:border-green-300'
                          }`}
                          title={`Valid: ${sidebarStats.compliant || 0}`}
                        >
                          <CheckCircle className='h-5 w-5' />
                          {sidebarStats.compliant > 0 && (
                            <span
                              className={`absolute -top-1 -right-1 bg-green-600 text-white text-[9px] rounded-full h-4 flex items-center justify-center font-bold ${
                                sidebarStats.compliant > 99
                                  ? 'px-1 min-w-[20px]'
                                  : 'w-4'
                              }`}
                            >
                              {sidebarStats.compliant > 99
                                ? '99+'
                                : sidebarStats.compliant}
                            </span>
                          )}
                        </button>

                        {/* Expiring */}
                        <button
                          onClick={() => toggleFilter('expiring')}
                          className={`relative group w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                            activeFilters.has('expiring')
                              ? 'bg-yellow-500 text-white'
                              : 'bg-white border border-gray-200 hover:border-yellow-300'
                          }`}
                          title={`Expiring: ${sidebarStats.expiring || 0}`}
                        >
                          <Clock className='h-5 w-5' />
                          {sidebarStats.expiring > 0 && (
                            <span
                              className={`absolute -top-1 -right-1 bg-yellow-600 text-white text-[9px] rounded-full h-4 flex items-center justify-center font-bold ${
                                sidebarStats.expiring > 99
                                  ? 'px-1 min-w-[20px]'
                                  : 'w-4'
                              }`}
                            >
                              {sidebarStats.expiring > 99
                                ? '99+'
                                : sidebarStats.expiring}
                            </span>
                          )}
                        </button>

                        {/* Expired */}
                        <button
                          onClick={() => toggleFilter('expired')}
                          className={`relative group w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                            activeFilters.has('expired')
                              ? 'bg-red-500 text-white'
                              : 'bg-white border border-gray-200 hover:border-red-300'
                          }`}
                          title={`Expired: ${sidebarStats.expired || 0}`}
                        >
                          <AlertTriangle className='h-5 w-5' />
                          {sidebarStats.expired > 0 && (
                            <span
                              className={`absolute -top-1 -right-1 bg-red-600 text-white text-[9px] rounded-full h-4 flex items-center justify-center font-bold ${
                                sidebarStats.expired > 99
                                  ? 'px-1 min-w-[20px]'
                                  : 'w-4'
                              }`}
                            >
                              {sidebarStats.expired > 99
                                ? '99+'
                                : sidebarStats.expired}
                            </span>
                          )}
                        </button>

                        {/* Missing */}
                        <button
                          onClick={() => toggleFilter('missing')}
                          className={`relative group w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                            activeFilters.has('missing')
                              ? 'bg-red-500 text-white'
                              : 'bg-white border border-gray-200 hover:border-red-300'
                          }`}
                          title={`Missing: ${sidebarStats.missing || 0}`}
                        >
                          <XCircle className='h-5 w-5' />
                          {sidebarStats.missing > 0 && (
                            <span
                              className={`absolute -top-1 -right-1 bg-red-600 text-white text-[9px] rounded-full h-4 flex items-center justify-center font-bold ${
                                sidebarStats.missing > 99
                                  ? 'px-1 min-w-[20px]'
                                  : 'w-4'
                              }`}
                            >
                              {sidebarStats.missing > 99
                                ? '99+'
                                : sidebarStats.missing}
                            </span>
                          )}
                        </button>

                        <div className='w-8 border-t border-gray-300'></div>

                        {/* Quotes */}
                        <button
                          onClick={() => toggleFilter('quotes')}
                          className={`relative group w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                            activeFilters.has('quotes')
                              ? 'bg-purple-500 text-white'
                              : 'bg-white border border-gray-200 hover:border-purple-300'
                          }`}
                          title={`Quotes: ${sidebarStats.quotes || 0}`}
                        >
                          <FileText className='h-5 w-5' />
                          {sidebarStats.quotes > 0 && (
                            <span
                              className={`absolute -top-1 -right-1 bg-purple-600 text-white text-[9px] rounded-full h-4 flex items-center justify-center font-bold ${
                                sidebarStats.quotes > 99
                                  ? 'px-1 min-w-[20px]'
                                  : 'w-4'
                              }`}
                            >
                              {sidebarStats.quotes > 99
                                ? '99+'
                                : sidebarStats.quotes}
                            </span>
                          )}
                        </button>

                        {/* Booked */}
                        <button
                          onClick={() => toggleFilter('booked')}
                          className={`relative group w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                            activeFilters.has('booked')
                              ? 'bg-blue-500 text-white'
                              : 'bg-white border border-gray-200 hover:border-blue-300'
                          }`}
                          title={`Booked: ${sidebarStats.booked || 0}`}
                        >
                          <Calendar className='h-5 w-5' />
                          {sidebarStats.booked > 0 && (
                            <span
                              className={`absolute -top-1 -right-1 bg-blue-600 text-white text-[9px] rounded-full h-4 flex items-center justify-center font-bold ${
                                sidebarStats.booked > 99
                                  ? 'px-1 min-w-[20px]'
                                  : 'w-4'
                              }`}
                            >
                              {sidebarStats.booked > 99
                                ? '99+'
                                : sidebarStats.booked}
                            </span>
                          )}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </>
          )}

          {/* Space Assignment Picker (global/stack view) */}
          {spaceAssignPicker && (
            <Dialog open={true} onOpenChange={() => { if (!spaceAssignLoading) setSpaceAssignPicker(null); }}>
              <DialogContent className='max-w-md'>
                {!spaceAssignPicker.confirm ? (
                  <>
                    <DialogHeader>
                      <DialogTitle>
                        Assign {spaceAssignPicker.requirement.name}
                      </DialogTitle>
                      <DialogDescription>
                        Select a space to assign this qualification to {spaceAssignPicker.member.name}
                      </DialogDescription>
                    </DialogHeader>
                    <div className='space-y-2 py-2 max-h-[60vh] overflow-y-auto'>
                      {spaceAssignPicker.spaces
                        .sort((a, b) => {
                          const score = s => (s.hasMember ? 2 : 0) + (s.hasRequirement ? 1 : 0);
                          return score(b) - score(a);
                        })
                        .map(space => (
                          <button
                            key={space.id}
                            onClick={() => setSpaceAssignPicker(prev => ({ ...prev, confirm: space }))}
                            className='group w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-indigo-50 hover:border-indigo-300 transition-all text-left cursor-pointer'
                          >
                            <div className='flex-shrink-0 h-9 w-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center group-hover:bg-indigo-100 group-hover:border-indigo-300 transition-colors'>
                              <Plus className='h-4 w-4 text-gray-400 group-hover:text-indigo-600 transition-colors' />
                            </div>
                            <div className='flex-1 min-w-0'>
                              <p className='text-sm font-medium text-gray-900 truncate'>{space.name}</p>
                              <div className='flex items-center gap-3 mt-0.5'>
                                <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${
                                  space.hasMember ? 'text-green-700' : 'text-red-500'
                                }`}>
                                  {space.hasMember ? <CheckCircle className='h-3 w-3' /> : <X className='h-3 w-3' />}
                                  User {space.hasMember ? 'in space' : 'not in space'}
                                </span>
                                <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${
                                  space.hasRequirement ? 'text-green-700' : 'text-amber-600'
                                }`}>
                                  {space.hasRequirement ? <CheckCircle className='h-3 w-3' /> : <X className='h-3 w-3' />}
                                  Qualification {space.hasRequirement ? 'exists' : 'not on space'}
                                </span>
                              </div>
                            </div>
                          </button>
                        ))}
                    </div>
                  </>
                ) : (
                  <>
                    <DialogHeader>
                      <DialogTitle>Confirm Assignment</DialogTitle>
                      <DialogDescription>
                        The following actions will be taken in <strong>{spaceAssignPicker.confirm.name}</strong>
                      </DialogDescription>
                    </DialogHeader>
                    <div className='space-y-3 py-3'>
                      {/* What will happen */}
                      <div className='space-y-2'>
                        {!spaceAssignPicker.confirm.hasMember && (
                          <div className='flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200'>
                            <UserPlus className='h-5 w-5 text-blue-600 flex-shrink-0' />
                            <div>
                              <p className='text-sm font-medium text-blue-900'>Add user to space</p>
                              <p className='text-xs text-blue-700'>{spaceAssignPicker.member.name} will be added to {spaceAssignPicker.confirm.name}</p>
                            </div>
                          </div>
                        )}
                        {!spaceAssignPicker.confirm.hasRequirement && (
                          <div className='flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200'>
                            <Plus className='h-5 w-5 text-amber-600 flex-shrink-0' />
                            <div>
                              <p className='text-sm font-medium text-amber-900'>Add qualification to space</p>
                              <p className='text-xs text-amber-700'>{spaceAssignPicker.requirement.name} will be created in {spaceAssignPicker.confirm.name}</p>
                            </div>
                          </div>
                        )}
                        <div className='flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200'>
                          <CheckCircle className='h-5 w-5 text-green-600 flex-shrink-0' />
                          <div>
                            <p className='text-sm font-medium text-green-900'>Assign qualification</p>
                            <p className='text-xs text-green-700'>{spaceAssignPicker.requirement.name} will be assigned to {spaceAssignPicker.member.name}</p>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className='flex gap-2 pt-2'>
                        <Button
                          variant='outline'
                          size='sm'
                          className='flex-1'
                          disabled={spaceAssignLoading}
                          onClick={() => setSpaceAssignPicker(prev => ({ ...prev, confirm: null }))}
                        >
                          Back
                        </Button>
                        <Button
                          size='sm'
                          className='flex-1'
                          disabled={spaceAssignLoading}
                          onClick={() => handleSpaceAssignment(spaceAssignPicker.confirm, spaceAssignPicker.member, spaceAssignPicker.requirement)}
                        >
                          {spaceAssignLoading ? 'Assigning...' : 'Confirm'}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          )}

          {/* Qualification Details Dialog */}
          {dialogOpen && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogContent className='max-w-2xl max-h-[90vh] overflow-hidden flex flex-col'>
                <DialogHeader className='flex-shrink-0'>
                  <DialogTitle className='text-2xl font-bold'>
                    {selectedCell?.requirement?.name || 'Qualification Details'}
                  </DialogTitle>
                  <DialogDescription asChild>
                    <div className='mt-2'>
                      <p>Manage training requirements for{' '}
                      {selectedCell?.member?.name}</p>
                      {isStackView && (() => {
                        // Look up spaces from filteredRequirements since selectedCell may not carry them
                        const reqSpaces = selectedCell?.requirement?.spaces
                          || filteredRequirements.find(r => r.id === selectedCell?.requirement?.id)?.spaces;
                        if (!reqSpaces || reqSpaces.length === 0) return null;
                        return (
                          <div className='flex flex-wrap gap-1 mt-1.5'>
                            {reqSpaces.map(s => (
                              <span key={s.id} className='inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200'>
                                {s.name}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </DialogDescription>
                </DialogHeader>

                <div className='space-y-6 py-4 overflow-y-auto flex-1 pr-2'>
                  {/* Qualification Information */}
                  <div className='bg-gray-50 rounded-lg p-4 space-y-3'>
                    <h3 className='font-semibold text-lg flex items-center gap-2'>
                      <Info className='h-5 w-5 text-blue-500' />
                      Qualification Information
                    </h3>
                    <div className='grid grid-cols-2 gap-4 text-sm'>
                      <div>
                        <span className='text-gray-500'>Description:</span>
                        <p className='mt-1'>
                          {selectedCell?.requirement?.description ||
                            'No description available'}
                        </p>
                      </div>
                      <div>
                        <span className='text-gray-500'>Priority:</span>
                        <p className='mt-1 capitalize'>
                          {selectedCell?.requirement?.priority || 'Standard'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Current Status */}
                  <div className='bg-blue-50 rounded-lg p-4 space-y-3'>
                    <h3 className='font-semibold text-lg flex items-center gap-2'>
                      <FileCheck className='h-5 w-5 text-blue-600' />
                      Current Status
                    </h3>
                    <div className='flex items-center gap-4'>
                      <Badge
                        variant={
                          selectedCell?.status === 'valid'
                            ? 'default'
                            : selectedCell?.status === 'expired'
                              ? 'destructive'
                              : selectedCell?.status === 'soon'
                                ? 'warning'
                                : selectedCell?.status === 'missing'
                                  ? 'secondary'
                                  : 'secondary'
                        }
                        className='text-sm px-3 py-1'
                      >
                        {selectedCell?.status === 'add'
                          ? 'Not Started'
                          : selectedCell?.status === 'valid'
                            ? 'Valid'
                            : selectedCell?.status === 'expired'
                              ? 'Expired'
                              : selectedCell?.status === 'soon'
                                ? 'Expiring Soon'
                                : selectedCell?.status === 'missing'
                                  ? 'Missing - Never Certified'
                                  : selectedCell?.status === 'booked'
                                    ? 'Training Booked'
                                    : selectedCell?.status || 'Unknown'}
                      </Badge>
                      {selectedCell?.assignment?.expiry_date &&
                        selectedCell?.status === 'valid' && (
                          <span className='text-sm text-gray-600'>
                            Expires:{' '}
                            {new Date(
                              selectedCell.assignment.expiry_date
                            ).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </span>
                        )}
                      {selectedCell?.assignment?.due_date &&
                        selectedCell?.status !== 'valid' && (
                          <span className='text-sm text-gray-600'>
                            Due:{' '}
                            {new Date(
                              selectedCell.assignment.due_date
                            ).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </span>
                        )}
                    </div>
                  </div>

                  {/* Actions Section */}
                  <div className='space-y-4'>
                    <h3 className='font-semibold text-lg'>Actions</h3>

                    {/* Show Quote Details if status is quoting */}
                    {selectedCell?.status === 'quoting' && quoteDetails ? (
                      <div className='bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-5 space-y-4 border border-purple-200/50'>
                        {/* Header */}
                        <div className='flex items-center justify-between'>
                          <div className='flex items-center gap-3'>
                            <div className='p-2 bg-white rounded-lg shadow-sm'>
                              <FileText className='h-5 w-5 text-purple-600' />
                            </div>
                            <div>
                              <h4 className='font-semibold text-gray-900'>
                                Training Quote
                              </h4>
                              <p className='text-xs text-gray-500'>
                                Ref: #
                                {quoteDetails.external_quote_id || 'Pending'}
                              </p>
                            </div>
                          </div>
                          <Badge
                            className={`
                      ${
                        quoteDetails.status === 'booked'
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : quoteDetails.status === 'live'
                            ? 'bg-blue-100 text-blue-700 border-blue-200'
                            : 'bg-yellow-100 text-yellow-700 border-yellow-200'
                      }
                    `}
                          >
                            {quoteDetails.status === 'draft'
                              ? 'Draft'
                              : quoteDetails.status === 'live'
                                ? 'Live'
                                : quoteDetails.status === 'booked'
                                  ? 'Booked'
                                  : 'Unknown'}
                          </Badge>
                        </div>

                        {/* Details Grid */}
                        <div className='bg-white/80 backdrop-blur rounded-lg p-4 space-y-3'>
                          {/* Course Name - Full Width */}
                          <div className='pb-3 border-b border-gray-100'>
                            <p className='text-sm text-gray-600 mb-1'>Course</p>
                            <p className='font-semibold text-gray-900'>
                              {quoteDetails.course_name || 'Not specified'}
                            </p>
                          </div>

                          {/* Two Column Grid */}
                          <div className='grid grid-cols-2 gap-4'>
                            {/* Delegates */}
                            <div>
                              <p className='text-sm text-gray-600 mb-1 flex items-center gap-1'>
                                <Users className='h-3 w-3' />
                                Delegates
                              </p>
                              <p className='font-medium text-gray-900'>
                                {quoteDetails.delegates || 1}{' '}
                                {quoteDetails.delegates === 1
                                  ? 'person'
                                  : 'people'}
                              </p>
                            </div>

                            {/* Training Type/Location */}
                            <div>
                              <p className='text-sm text-gray-600 mb-1 flex items-center gap-1'>
                                <MapPin className='h-3 w-3' />
                                Location
                              </p>
                              <p className='font-medium text-gray-900'>
                                {quoteDetails.locations === 'Online' ? (
                                  <span className='flex items-center gap-1'>
                                    <Monitor className='h-3.5 w-3.5' />
                                    Online
                                  </span>
                                ) : (
                                  quoteDetails.locations || 'TBD'
                                )}
                              </p>
                            </div>

                            {/* Date Preference */}
                            <div>
                              <p className='text-sm text-gray-600 mb-1 flex items-center gap-1'>
                                <Calendar className='h-3 w-3' />
                                Preferred Date
                              </p>
                              <p className='font-medium text-gray-900'>
                                {(() => {
                                  const dateStr = quoteDetails.date_prefs;
                                  if (!dateStr || dateStr === 'Not specified')
                                    return 'Flexible';

                                  // Check if it's a YYYY-MM format
                                  if (/^\d{4}-\d{2}$/.test(dateStr)) {
                                    const [year, month] = dateStr.split('-');
                                    const monthNames = [
                                      'Jan',
                                      'Feb',
                                      'Mar',
                                      'Apr',
                                      'May',
                                      'Jun',
                                      'Jul',
                                      'Aug',
                                      'Sep',
                                      'Oct',
                                      'Nov',
                                      'Dec'
                                    ];
                                    return `${monthNames[parseInt(month) - 1]} ${year}`;
                                  }

                                  // Check if it's a date
                                  const date = new Date(dateStr);
                                  if (!isNaN(date)) {
                                    return date.toLocaleDateString('en-GB', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric'
                                    });
                                  }

                                  return dateStr;
                                })()}
                              </p>
                            </div>

                            {/* Cost if available */}
                            {quoteDetails.cost && (
                              <div>
                                <p className='text-sm text-gray-600 mb-1 flex items-center gap-1'>
                                  <span className='text-xs'>£</span>
                                  Estimated Cost
                                </p>
                                <p className='font-medium text-gray-900'>
                                  £
                                  {parseFloat(quoteDetails.cost).toLocaleString(
                                    'en-GB',
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    }
                                  )}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Additional Details if present */}
                          {quoteDetails.additional_details && (
                            <div className='pt-3 border-t border-gray-100'>
                              <p className='text-sm text-gray-600 mb-2'>
                                Additional Information
                              </p>
                              <div
                                className='text-sm text-gray-700 space-y-1'
                                dangerouslySetInnerHTML={{
                                  __html: quoteDetails.additional_details
                                }}
                              />
                            </div>
                          )}

                          {/* Timeline */}
                          <div className='pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500'>
                            <span className='flex items-center gap-1'>
                              <Clock className='h-3 w-3' />
                              Requested{' '}
                              {new Date(
                                quoteDetails.created_date
                              ).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                            {quoteDetails.updated_date &&
                              quoteDetails.updated_date !==
                                quoteDetails.created_date && (
                                <span>
                                  Updated{' '}
                                  {new Date(
                                    quoteDetails.updated_date
                                  ).toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short'
                                  })}
                                </span>
                              )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        {quoteDetails.status === 'booked' && (
                          <div className='flex gap-2'>
                            <button
                              onClick={() => handleBookFromQuote(quoteDetails)}
                              className='flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium'
                            >
                              <Calendar className='h-4 w-4' />
                              Proceed to Booking
                            </button>
                          </div>
                        )}

                        {/* Access Token (only show if debugging needed) */}
                        {false && quoteDetails.access_token && (
                          <div className='mt-4 p-3 bg-gray-50 rounded-lg'>
                            <p className='text-xs text-gray-600 mb-1'>
                              Quote Access Token:
                            </p>
                            <p className='font-mono text-xs break-all'>
                              {quoteDetails.access_token}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {/* Book Training */}
                        <div className='border rounded-lg p-4 space-y-3'>
                          <div className='flex items-center justify-between'>
                            <h4 className='font-medium flex items-center gap-2'>
                              <Calendar className='h-4 w-4 text-purple-500' />
                              Book Training
                            </h4>
                            <Badge variant='outline' className='text-xs'>
                              Request Quote
                            </Badge>
                          </div>

                          {/* Date Preference Selector */}
                          <div className='space-y-2'>
                            <Label className='text-sm font-medium'>
                              Date Preference
                            </Label>
                            <Select
                              value={datePreference}
                              onValueChange={setDatePreference}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value='specific'>
                                  Specific Date
                                </SelectItem>
                                <SelectItem value='month'>
                                  Month Range
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Date/Month Input based on preference */}
                          {datePreference === 'specific' ? (
                            <div className='space-y-2'>
                              <Label className='text-sm font-medium'>
                                Preferred Date
                              </Label>
                              <Input
                                type='date'
                                value={preferredDate}
                                onChange={e => setPreferredDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                              />
                            </div>
                          ) : (
                            <div className='space-y-2'>
                              <Label className='text-sm font-medium'>
                                Preferred Month
                              </Label>
                              <Select
                                value={preferredMonth}
                                onValueChange={setPreferredMonth}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder='Select month range' />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value='January'>
                                    January
                                  </SelectItem>
                                  <SelectItem value='February'>
                                    February
                                  </SelectItem>
                                  <SelectItem value='March'>March</SelectItem>
                                  <SelectItem value='April'>April</SelectItem>
                                  <SelectItem value='May'>May</SelectItem>
                                  <SelectItem value='June'>June</SelectItem>
                                  <SelectItem value='July'>July</SelectItem>
                                  <SelectItem value='August'>August</SelectItem>
                                  <SelectItem value='September'>
                                    September
                                  </SelectItem>
                                  <SelectItem value='October'>
                                    October
                                  </SelectItem>
                                  <SelectItem value='November'>
                                    November
                                  </SelectItem>
                                  <SelectItem value='December'>
                                    December
                                  </SelectItem>
                                  <SelectItem value='September or October'>
                                    September or October
                                  </SelectItem>
                                  <SelectItem value='Q1 (Jan-Mar)'>
                                    Q1 (Jan-Mar)
                                  </SelectItem>
                                  <SelectItem value='Q2 (Apr-Jun)'>
                                    Q2 (Apr-Jun)
                                  </SelectItem>
                                  <SelectItem value='Q3 (Jul-Sep)'>
                                    Q3 (Jul-Sep)
                                  </SelectItem>
                                  <SelectItem value='Q4 (Oct-Dec)'>
                                    Q4 (Oct-Dec)
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* Number of Delegates */}
                          <div className='space-y-2'>
                            <Label className='text-sm font-medium'>
                              Number of Delegates
                            </Label>
                            <Input
                              type='number'
                              min='1'
                              max='50'
                              value={delegatesCount}
                              onChange={e =>
                                setDelegatesCount(parseInt(e.target.value) || 1)
                              }
                            />
                          </div>

                          {/* Location Input */}
                          <div className='space-y-2'>
                            <Label className='text-sm font-medium'>
                              Training Location(s)
                            </Label>
                            <Input
                              type='text'
                              placeholder='e.g., Manchester, Stoke'
                              value={trainingLocation}
                              onChange={e =>
                                setTrainingLocation(e.target.value)
                              }
                            />
                            <p className='text-xs text-gray-500'>
                              Enter city names separated by commas
                            </p>
                          </div>

                          {/* Additional Notes */}
                          <div className='space-y-2'>
                            <Label className='text-sm font-medium'>
                              Additional Details (Optional)
                            </Label>
                            <Textarea
                              placeholder='Any specific requirements or notes...'
                              value={bookingNotes}
                              onChange={e => setBookingNotes(e.target.value)}
                              className='h-20'
                            />
                          </div>

                          <Button
                            onClick={handleBooking}
                            className='w-full bg-purple-600 hover:bg-purple-700'
                          >
                            <Calendar className='h-4 w-4 mr-2' />
                            Request Training Quote
                          </Button>
                        </div>

                        {/* Existing Quotes for this Course */}
                        {existingQuotes.length > 0 && (
                          <div className='mt-4 border rounded-lg p-4 bg-purple-50/50'>
                            <h4 className='font-medium flex items-center gap-2 mb-3'>
                              <FileText className='h-4 w-4 text-purple-600' />
                              Recent Quotes for{' '}
                              {selectedCell?.requirement?.name}
                            </h4>
                            <div className='space-y-2'>
                              {loadingQuotes ? (
                                <div className='text-sm text-gray-500'>
                                  Loading quotes...
                                </div>
                              ) : (
                                existingQuotes.map(quote => (
                                  <div
                                    key={quote.id}
                                    className='bg-white rounded-md p-3 border border-purple-200'
                                  >
                                    <div className='flex items-start justify-between'>
                                      <div className='flex-1'>
                                        <div className='flex items-center gap-2'>
                                          <Badge
                                            className={`text-xs ${
                                              quote.status === 'booked'
                                                ? 'bg-green-100 text-green-700'
                                                : quote.status === 'live'
                                                  ? 'bg-blue-100 text-blue-700'
                                                  : 'bg-yellow-100 text-yellow-700'
                                            }`}
                                          >
                                            {quote.status === 'booked'
                                              ? 'Booked'
                                              : quote.status === 'live'
                                                ? 'Live'
                                                : 'Pending'}
                                          </Badge>
                                          {quote.external_quote_id && (
                                            <span className='text-xs text-gray-500'>
                                              #{quote.external_quote_id}
                                            </span>
                                          )}
                                        </div>
                                        <p className='text-sm text-gray-700 mt-1'>
                                          {quote.delegates || 1} delegate
                                          {quote.delegates !== 1 ? 's' : ''}
                                          {quote.locations &&
                                            quote.locations !==
                                              'Not specified' &&
                                            ` • ${quote.locations}`}
                                        </p>
                                        <p className='text-xs text-gray-500 mt-1'>
                                          {quote.date_prefs &&
                                            quote.date_prefs !==
                                              'Not specified' &&
                                            `Preferred: ${quote.date_prefs} • `}
                                          {new Date(
                                            quote.created_date
                                          ).toLocaleDateString('en-GB')}
                                        </p>
                                        {quote.cost && (
                                          <p className='text-sm font-semibold text-purple-700 mt-1'>
                                            £
                                            {parseFloat(
                                              quote.cost
                                            ).toLocaleString('en-GB')}
                                          </p>
                                        )}
                                      </div>
                                      <button
                                        onClick={() =>
                                          navigate(
                                            `/space/${projectId}/bookings`
                                          )
                                        }
                                        className='text-purple-600 hover:text-purple-700 transition-colors'
                                      >
                                        <Eye className='h-4 w-4' />
                                      </button>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                            <button
                              onClick={() =>
                                navigate(`/space/${projectId}/bookings`)
                              }
                              className='w-full mt-3 text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center justify-center gap-1'
                            >
                              View all quotes
                              <ChevronRight className='h-4 w-4' />
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {selectedCell?.assignment?.status === 'active' && (
                      <>
                        <div className='relative'>
                          <div className='absolute inset-0 flex items-center'>
                            <span className='w-full border-t' />
                          </div>
                          <div className='relative flex justify-center text-xs uppercase'>
                            <span className='bg-white px-2 text-gray-500'>
                              Or
                            </span>
                          </div>
                        </div>

                        {/* Upload Certificate */}
                        <div className='border rounded-lg p-4 space-y-3'>
                          <h4 className='font-medium flex items-center gap-2'>
                            <Upload className='h-4 w-4 text-green-500' />
                            Upload Certificate
                          </h4>

                          {/* Certificate Number */}
                          <div className='space-y-2'>
                            <Label
                              htmlFor='cert-number'
                              className='text-sm font-medium'
                            >
                              Certificate Number (Optional)
                            </Label>
                            <Input
                              id='cert-number'
                              type='text'
                              placeholder='e.g., CERT-2024-001'
                              value={certificateNumber}
                              onChange={e =>
                                setCertificateNumber(e.target.value)
                              }
                              className='w-full'
                            />
                          </div>

                          {/* Expiry Date */}
                          <div className='space-y-2'>
                            <Label
                              htmlFor='expiry-date'
                              className='text-sm font-medium'
                            >
                              Certificate Expiry Date{' '}
                              <span className='text-red-500'>*</span>
                            </Label>
                            <Input
                              id='expiry-date'
                              type='date'
                              value={expiryDate}
                              onChange={e => setExpiryDate(e.target.value)}
                              min={new Date().toISOString().split('T')[0]}
                              className='w-full'
                              required
                            />
                            {expiryDate && (
                              <p className='text-xs text-gray-600'>
                                Valid until:{' '}
                                {new Date(expiryDate).toLocaleDateString(
                                  'en-GB',
                                  {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  }
                                )}
                              </p>
                            )}
                          </div>

                          {/* File Upload */}
                          <div className='space-y-2'>
                            <Label className='text-sm font-medium'>
                              Certificate Document{' '}
                              <span className='text-red-500'>*</span>
                            </Label>
                            <input
                              ref={fileInputRef}
                              type='file'
                              accept='.pdf,.jpg,.jpeg,.png'
                              onChange={e => setUploadFile(e.target.files[0])}
                              className='hidden'
                            />
                            <Button
                              variant='outline'
                              onClick={() => fileInputRef.current?.click()}
                              className='w-full'
                            >
                              <FileUp className='h-4 w-4 mr-2' />
                              {uploadFile
                                ? uploadFile.name
                                : 'Choose Certificate File'}
                            </Button>
                            {uploadFile && (
                              <div className='flex items-center justify-between p-2 bg-gray-50 rounded'>
                                <span className='text-sm text-gray-600 truncate'>
                                  {uploadFile.name}
                                </span>
                                <span className='text-xs text-gray-500'>
                                  {(uploadFile.size / 1024).toFixed(2)} KB
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Submit Button */}
                          <Button
                            onClick={handleUploadCertificate}
                            disabled={!uploadFile || !expiryDate}
                            className='w-full bg-green-600 hover:bg-green-700 disabled:opacity-50'
                          >
                            <CheckCircle className='h-4 w-4 mr-2' />
                            Complete & Upload Certificate
                          </Button>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Additional Information */}
                  {selectedCell?.assignment?.notes && (
                    <div className='bg-yellow-50 rounded-lg p-4'>
                      <h4 className='font-medium text-sm text-yellow-800 mb-2'>
                        Notes
                      </h4>
                      <p className='text-sm text-gray-700'>
                        {selectedCell.assignment.notes}
                      </p>
                    </div>
                  )}
                </div>

                <DialogFooter className='flex-shrink-0 pt-4 border-t'>
                  <Button
                    variant='outline'
                    onClick={() => setDialogOpen(false)}
                  >
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* Expanding Tile Card System - No duplicate tiles */}
          {/* Note: Original tile is handled by the AnimatePresence below, not here */}

          {/* Animated overlay and expanding card */}
          <AnimatePresence>
            {dockOpen && dockCell && (
              <>
                {/* Original tile stays in place underneath - creates the illusion */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{
                    opacity: dockOpen ? 0 : 1,
                    scale: dockOpen ? 0.95 : 1,
                    filter: dockOpen ? 'blur(2px)' : 'blur(0px)'
                  }}
                  transition={{
                    opacity: { duration: 0.2, delay: dockOpen ? 0 : 0.25 },
                    scale: {
                      duration: 0.35,
                      delay: dockOpen ? 0 : 0.15,
                      ease: [0.43, 0.13, 0.23, 0.96]
                    },
                    filter: { duration: 0.3, delay: dockOpen ? 0 : 0.2 }
                  }}
                  className='fixed bg-white flex items-center justify-center overflow-hidden z-[59]'
                  style={{
                    left: dockCell.tileData.x,
                    top: dockCell.tileData.y,
                    width: dockCell.tileData.width,
                    height: dockCell.tileData.height,
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <div className='flex flex-col items-center justify-center text-center h-full px-2 py-3 bg-pink-200'>
                    {dockCell.status === 'add' ? (
                      <div className='w-8 h-8 rounded-lg border border-dashed border-gray-300 flex items-center justify-center'>
                        <Plus
                          className='h-3.5 w-3.5 text-gray-400'
                          strokeWidth={1.5}
                        />
                      </div>
                    ) : (
                      <>
                        {dockCell.status === 'valid' && (
                          <CheckCircle
                            className='h-8 w-8 mb-1.5 text-green-500'
                            strokeWidth={1.5}
                          />
                        )}
                        {dockCell.status === 'soon' && (
                          <AlertTriangle
                            className='h-8 w-8 mb-1.5 text-amber-500'
                            strokeWidth={1.5}
                          />
                        )}
                        {dockCell.status === 'expired' && (
                          <XCircle
                            className='h-8 w-8 mb-1.5 text-red-500'
                            strokeWidth={1.5}
                          />
                        )}
                        {dockCell.status === 'missing' && (
                          <XCircle
                            className='h-8 w-8 mb-1.5 text-red-500'
                            strokeWidth={1.5}
                          />
                        )}
                        {dockCell.status === 'booked' && (
                          <Calendar
                            className='h-8 w-8 mb-1.5 text-blue-500'
                            strokeWidth={1.5}
                          />
                        )}
                        {dockCell.status === 'quoting' && (
                          <PoundSterling
                            className='h-8 w-8 mb-1.5 text-purple-500'
                            strokeWidth={1.5}
                          />
                        )}
                        <span className='text-[11px] font-normal text-gray-600 mb-1'>
                          {dockCell.status === 'valid'
                            ? 'Valid'
                            : dockCell.status === 'soon'
                              ? 'Soon'
                              : dockCell.status === 'expired'
                                ? 'Expired'
                                : dockCell.status === 'missing'
                                  ? 'Missing'
                                  : dockCell.status === 'booked'
                                    ? 'Booked'
                                    : dockCell.status === 'quoting'
                                      ? 'Quoting'
                                      : ''}
                        </span>
                        {dockCell.assignment && (
                          <span className='text-[10px] text-gray-400'>
                            {dockCell.status === 'valid' &&
                            dockCell.assignment.expiry_date
                              ? new Date(
                                  dockCell.assignment.expiry_date
                                ).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: '2-digit'
                                })
                              : dockCell.status === 'soon' &&
                                  dockCell.assignment.expiry_date
                                ? new Date(
                                    dockCell.assignment.expiry_date
                                  ).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: '2-digit'
                                  })
                                : dockCell.status === 'expired' &&
                                    dockCell.assignment.expiry_date
                                  ? new Date(
                                      dockCell.assignment.expiry_date
                                    ).toLocaleDateString('en-GB', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: '2-digit'
                                    })
                                  : dockCell.status === 'booked' &&
                                      dockCell.assignment.booking_date
                                    ? new Date(
                                        dockCell.assignment.booking_date
                                      ).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: '2-digit'
                                      })
                                    : ''}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </motion.div>

                {/* Background blur overlay with smooth transition */}
                <motion.div
                  initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
                  animate={{ opacity: 1, backdropFilter: 'blur(2px)' }}
                  exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
                  transition={{
                    duration: 0.5,
                    ease: [0.43, 0.13, 0.23, 0.96]
                  }}
                  className='fixed inset-0 bg-black/20 z-[60]'
                  style={{ WebkitBackdropFilter: 'inherit' }}
                  onClick={() => {
                    setDockOpen(false);
                    setTimeout(() => closeDock(), 400); // Clear after animation completes
                  }}
                />

                {/* Expanding card with Apple-style design */}
                <motion.div
                  initial={{
                    position: 'fixed',
                    left: dockCell.tileData.x,
                    top: dockCell.tileData.y,
                    width: dockCell.tileData.width,
                    borderRadius: 8,
                    opacity: 0,
                    scale: 0.8,
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                  }}
                  animate={{
                    left: (window.innerWidth - 400) / 2,
                    top: window.innerHeight * 0.2,
                    width: 400,
                    borderRadius: 20,
                    opacity: 1,
                    scale: 1,
                    boxShadow:
                      '0 20px 40px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.08)',
                    transition: {
                      type: 'spring',
                      damping: 32,
                      stiffness: 400,
                      mass: 0.6
                    }
                  }}
                  exit={{
                    left: dockCell.tileData.x,
                    top: dockCell.tileData.y,
                    width: dockCell.tileData.width,
                    borderRadius: 8,
                    opacity: 0,
                    scale: 0.8,
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    filter: 'blur(4px)',
                    transition: {
                      opacity: { duration: 0.2, delay: 0.15 },
                      filter: { duration: 0.25, delay: 0.1 },
                      default: {
                        duration: 0.35,
                        ease: [0.43, 0.13, 0.23, 0.96]
                      }
                    }
                  }}
                  className='fixed z-[70] bg-white/95 backdrop-blur-[2px] overflow-visible'
                  style={{
                    transformOrigin: 'center center',
                    backdropFilter: 'blur(2px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(2px) saturate(180%)'
                  }}
                >
                  {/* Expanded content with Apple design */}
                  <div className='flex flex-col'>
                    {/* Minimal Header */}
                    <div className='p-5 pb-0'>
                      <div className='flex items-start justify-between mb-4'>
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.1, duration: 0.3 }}
                          className='flex-1'
                        >
                          <h2 className='text-xl font-semibold text-gray-900 tracking-tight'>
                            {dockCell.requirement.name}
                          </h2>
                          <p className='text-sm text-gray-500 mt-0.5'>
                            {dockCell.member.name}
                          </p>
                          {isStackView && (() => {
                            // Find which space(s) this user is assigned this requirement in
                            const memberAssignments = assignments.filter(
                              a => a.team_member_id === dockCell.member.id
                                && a.requirement_id === dockCell.requirement.id
                                && a.project_id
                            );
                            const assignedSpaceIds = [...new Set(memberAssignments.map(a => a.project_id))];
                            if (assignedSpaceIds.length === 0) return null;
                            return (
                              <div className='flex flex-wrap gap-1 mt-1.5'>
                                {assignedSpaceIds.map(spaceId => {
                                  const spaceName = stackProjects?.find(p => p.id === spaceId)?.name || spaceId;
                                  return (
                                    <span key={spaceId} className='inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200'>
                                      {spaceName}
                                    </span>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </motion.div>
                        <motion.button
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          whileHover={{ backgroundColor: 'rgba(0,0,0,0.06)' }}
                          whileTap={{ scale: 0.96 }}
                          transition={{ delay: 0.1, duration: 0.2 }}
                          onClick={() => {
                            setDockOpen(false);
                            setTimeout(() => closeDock(), 400);
                          }}
                          className='p-1.5 rounded-full transition-colors'
                        >
                          <X
                            className='h-4 w-4 text-gray-400'
                            strokeWidth={2}
                          />
                        </motion.button>
                      </div>

                      {/* Clean Status Indicator */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.15, duration: 0.3 }}
                        className='flex items-center gap-2'
                      >
                        <div
                          className={`
                    inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium
                    ${
                      dockCell.status === 'valid'
                        ? 'bg-green-100 text-green-700'
                        : dockCell.status === 'expired'
                          ? 'bg-red-100 text-red-700'
                          : dockCell.status === 'soon'
                            ? 'bg-yellow-100 text-yellow-700'
                            : dockCell.status === 'missing'
                              ? 'bg-orange-100 text-orange-700'
                              : dockCell.status === 'booked'
                                ? 'bg-blue-100 text-blue-700'
                                : dockCell.status === 'quoting'
                                  ? 'bg-purple-100 text-purple-700'
                                  : 'bg-gray-100 text-gray-700'
                    }
                  `}
                        >
                          {dockCell.status === 'valid' && (
                            <CheckCircle className='h-4 w-4' />
                          )}
                          {dockCell.status === 'expired' && (
                            <XCircle className='h-4 w-4' />
                          )}
                          {dockCell.status === 'soon' && (
                            <AlertTriangle className='h-4 w-4' />
                          )}
                          {dockCell.status === 'missing' && (
                            <Clock className='h-4 w-4' />
                          )}
                          {dockCell.status === 'booked' && (
                            <Calendar className='h-4 w-4' />
                          )}
                          {dockCell.status === 'quoting' && (
                            <PoundSterling className='h-4 w-4' />
                          )}
                          <span className='capitalize'>
                            {dockCell.status === 'soon'
                              ? 'Expiring Soon'
                              : dockCell.status === 'quoting'
                                ? 'Quote Requested'
                                : dockCell.status}
                          </span>
                        </div>
                        {dockCell.status !== 'booked' && dockCell.status !== 'quoting' && (
                          <button
                            onClick={() => handleDockAction('override')}
                            className='text-xs text-gray-500 hover:text-gray-700 underline'
                          >
                            Change
                          </button>
                        )}
                      </motion.div>
                    </div>

                    {/* Card Content */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.3 }}
                      className='flex-1 px-5 pb-5'
                    >
                      {/* Certificate Section */}
                      {(() => {
                        const qual = dockCell.assignment?.qualification;
                        const evidenceUrl =
                          qual?.evidence_url ||
                          dockCell.assignment?.assignmentEvidenceUrl ||
                          dockCell.assignment?.evidence_url;
                        const evidenceFiles =
                          qual?.evidence_files ||
                          dockCell.assignment?.evidenceFiles ||
                          dockCell.assignment?.evidence_files ||
                          [];
                        const multipleFiles = Array.isArray(evidenceFiles) && evidenceFiles.length > 1;
                        const certNumber =
                          qual?.certificate_number ||
                          dockCell.assignment?.assignmentCertificateNumber ||
                          dockCell.assignment?.certificate_number;
                        const expiryDate =
                          qual?.expiry_date || dockCell.assignment?.expiry_date;
                        const completedDate =
                          qual?.completed_date ||
                          dockCell.assignment?.completed_date;
                        const overrideReason = qual?.override_reason;
                        const overrideByName = qual?.override_by_name;
                        const overrideDate = qual?.override_date;
                        const qualNotes = qual?.notes;

                        const getDaysUntilExpiry = dateStr => {
                          if (!dateStr) return null;
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const expiry = new Date(dateStr);
                          expiry.setHours(0, 0, 0, 0);
                          return Math.ceil(
                            (expiry - today) / (1000 * 60 * 60 * 24)
                          );
                        };

                        const daysUntilExpiry = getDaysUntilExpiry(expiryDate);
                        const hasAnyDetails =
                          completedDate ||
                          expiryDate ||
                          certNumber ||
                          evidenceUrl ||
                          overrideReason;

                        if (!hasAnyDetails) return null;

                        const handleViewCertificate = async (url) => {
                          try {
                            const viewUrl = await getViewableUrl(
                              url || evidenceUrl,
                              'certificates'
                            );
                            if (viewUrl) window.open(viewUrl, '_blank');
                          } catch (error) {
                            console.error('Error viewing certificate:', error);
                            toast.error('Failed to load certificate');
                          }
                        };

                        return (
                          <div className='pt-2 pb-3 border-b border-gray-100'>
                            <div className='flex items-center justify-between mb-2'>
                              <h4 className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>
                                {multipleFiles ? `Evidence (${evidenceFiles.length})` : 'Certificate'}
                              </h4>
                              {evidenceUrl && !multipleFiles && (
                                <button
                                  onClick={() => handleViewCertificate()}
                                  className='p-1.5 rounded hover:bg-gray-100 transition-colors'
                                  title='View certificate'
                                >
                                  <ExternalLink className='h-4 w-4 text-gray-400 hover:text-gray-600' />
                                </button>
                              )}
                            </div>
                            {/* Multi-file list */}
                            {multipleFiles && (
                              <div className='space-y-1 mb-2'>
                                {evidenceFiles.map((ef, idx) => {
                                  const docType = ef.doc_type || (ef.is_primary ? 'certificate' : 'other');
                                  const typeLabel = docType === 'certificate' ? 'Certificate' : docType === 'card' ? 'ID / Card' : 'Other';
                                  const typeColor = docType === 'certificate' ? 'text-blue-600' : docType === 'card' ? 'text-emerald-600' : 'text-gray-500';
                                  const TypeIcon = docType === 'certificate' ? FileText : docType === 'card' ? CreditCard : FileUp;
                                  return (
                                    <div
                                      key={idx}
                                      className='flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 transition-colors'
                                    >
                                      <TypeIcon className={`h-4 w-4 flex-none ${typeColor}`} />
                                      <span className='text-xs text-gray-700 truncate flex-1'>
                                        {ef.file_name || `File ${idx + 1}`}
                                      </span>
                                      <span className={`text-[10px] font-medium flex-none ${typeColor}`}>{typeLabel}</span>
                                      <button
                                        onClick={() => handleViewCertificate(ef.url)}
                                        className='p-1 rounded hover:bg-gray-200 transition-colors'
                                        title='View file'
                                      >
                                        <ExternalLink className='h-4 w-4 text-gray-400 hover:text-gray-700' />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <div className='space-y-1.5'>
                              {/* Override info */}
                              {overrideReason && (
                                <>
                                  <div className='flex items-center gap-1.5 mb-1'>
                                    <span className='inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border border-amber-300 bg-amber-50 text-amber-700'>
                                      {overrideReason === 'awaiting_certificate'
                                        ? 'Awaiting Certificate'
                                        : 'No Evidence Required'}
                                    </span>
                                  </div>
                                  {overrideByName && overrideDate && (
                                    <p className='text-[10px] text-gray-400'>
                                      Set by {overrideByName} on{' '}
                                      {new Date(overrideDate).toLocaleDateString('en-GB', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric'
                                      })}
                                    </p>
                                  )}
                                  {qualNotes && (
                                    <p className='text-xs text-gray-500 italic'>
                                      {qualNotes}
                                    </p>
                                  )}
                                </>
                              )}
                              {expiryDate && (
                                <div className='flex items-center justify-between'>
                                  <span className='text-xs text-gray-500'>
                                    Expires
                                  </span>
                                  <span
                                    className={`text-xs font-medium ${
                                      daysUntilExpiry < 0
                                        ? 'text-red-600'
                                        : daysUntilExpiry <= 30
                                          ? 'text-amber-600'
                                          : 'text-green-600'
                                    }`}
                                  >
                                    {new Date(expiryDate).toLocaleDateString(
                                      'en-GB',
                                      {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric'
                                      }
                                    )}
                                    {daysUntilExpiry !== null && (
                                      <span className='ml-1'>
                                        (
                                        {daysUntilExpiry < 0
                                          ? `${Math.abs(daysUntilExpiry)}d ago`
                                          : `${daysUntilExpiry}d`}
                                        )
                                      </span>
                                    )}
                                  </span>
                                </div>
                              )}
                              {completedDate && (
                                <div className='flex items-center justify-between'>
                                  <span className='text-xs text-gray-500'>
                                    Completed
                                  </span>
                                  <span className='text-xs font-medium text-gray-700'>
                                    {new Date(completedDate).toLocaleDateString(
                                      'en-GB',
                                      {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric'
                                      }
                                    )}
                                  </span>
                                </div>
                              )}
                              {certNumber && (
                                <div className='flex items-center justify-between'>
                                  <span className='text-xs text-gray-500'>
                                    Certificate No.
                                  </span>
                                  <span className='text-xs font-mono text-gray-700'>
                                    {certNumber}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Requirement Section */}
                      {(dockCell.requirement.description ||
                        dockCell.requirement.category ||
                        dockCell.requirement.renewal_period) && (
                        <div className='py-3 border-b border-gray-100'>
                          <h4 className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2'>
                            Requirement
                          </h4>
                          <div className='space-y-1.5'>
                            {dockCell.requirement.category && (
                              <div className='flex items-center justify-between'>
                                <span className='text-xs text-gray-500'>
                                  Category
                                </span>
                                <span className='text-xs font-medium text-gray-700'>
                                  {dockCell.requirement.category}
                                </span>
                              </div>
                            )}
                            {dockCell.requirement.renewal_period && (
                              <div className='flex items-center justify-between'>
                                <span className='text-xs text-gray-500'>
                                  Renewal
                                </span>
                                <span className='text-xs font-medium text-gray-700'>
                                  {dockCell.requirement.renewal_period}
                                </span>
                              </div>
                            )}
                            {dockCell.requirement.legal_requirement && (
                              <div className='flex items-center justify-between'>
                                <span className='text-xs text-gray-500'>
                                  Legal
                                </span>
                                <span className='text-xs font-medium text-red-600'>
                                  Required by Law
                                </span>
                              </div>
                            )}
                            {dockCell.requirement.description && (
                              <p className='text-xs text-gray-600 pt-1'>
                                {dockCell.requirement.description}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Actions Section */}
                      <div className='pt-3 space-y-2'>
                        {(dockCell.status === 'missing' ||
                          dockCell.status === 'expired' ||
                          dockCell.status === 'soon') && (
                          <>
                            <div className='flex gap-2'>
                              <Button
                                variant='secondary'
                                size='sm'
                                onClick={() => {
                                  window.location.href = `/profile/${dockCell.member.id}`;
                                }}
                                className='flex-1'
                              >
                                <Users className='h-4 w-4' />
                                Profile
                              </Button>
                              <Button
                                variant='default'
                                size='sm'
                                onClick={() =>
                                  handleDockAction('oneclick-quote')
                                }
                                className='flex-1'
                              >
                                <CalendarClock className='h-4 w-4' />
                                Quick Quote
                              </Button>
                            </div>
                            <div className='flex gap-2'>
                              <Button
                                variant='secondary'
                                size='sm'
                                onClick={() => handleDockAction('upload')}
                                className='flex-1'
                              >
                                <Upload className='h-4 w-4' />
                                {dockCell.assignment?.qualification
                                  ?.evidence_url
                                  ? 'Re-upload'
                                  : 'Upload'}
                              </Button>
                            </div>
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={() => handleDockAction('remove')}
                              className='w-full text-red-600 hover:text-red-700 hover:bg-red-50'
                            >
                              <X className='h-4 w-4' />
                              Unassign
                            </Button>
                          </>
                        )}

                        {dockCell.status === 'valid' && dockCell.assignment && (
                          <>
                            <div className='flex gap-2'>
                              <Button
                                variant='secondary'
                                size='sm'
                                onClick={() => {
                                  window.location.href = `/profile/${dockCell.member.id}`;
                                }}
                                className='flex-1'
                              >
                                <Users className='h-4 w-4' />
                                Profile
                              </Button>
                              <Button
                                variant='secondary'
                                size='sm'
                                onClick={() => handleDockAction('upload')}
                                className='flex-1'
                              >
                                <Upload className='h-4 w-4' />
                                Re-upload
                              </Button>
                            </div>
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={() => handleDockAction('remove')}
                              className='w-full text-red-600 hover:text-red-700 hover:bg-red-50'
                            >
                              <X className='h-4 w-4' />
                              Remove Assignment
                            </Button>
                          </>
                        )}
                      </div>

                      {/* Assignment notes if available - parse JSON for booking requests */}
                      {dockCell.assignment?.notes && (
                        <div className='pt-4 border-t border-gray-100'>
                          {dockCell.assignment.notes.startsWith(
                            'Booking Request:'
                          ) ? (
                            (() => {
                              try {
                                // Parse the booking request JSON from the notes
                                const jsonStr =
                                  dockCell.assignment.notes.replace(
                                    'Booking Request: ',
                                    ''
                                  );
                                const bookingData = JSON.parse(jsonStr);

                                return (
                                  <>
                                    <h4 className='text-sm font-semibold text-gray-700 mb-3'>
                                      {dockCell.status === 'booked'
                                        ? 'Booking Details'
                                        : 'Quote Request Details'}
                                    </h4>
                                    <div className='bg-purple-50 rounded-lg p-3 space-y-2'>
                                      {/* Date Preference */}
                                      <div className='flex items-center justify-between text-sm'>
                                        <span className='text-gray-600 flex items-center gap-1'>
                                          <Calendar className='h-3 w-3' />
                                          Preferred Date
                                        </span>
                                        <span className='font-medium text-gray-900'>
                                          {bookingData.datePreference ===
                                            'month' &&
                                          bookingData.preferredMonth
                                            ? (() => {
                                                const [year, month] =
                                                  bookingData.preferredMonth.split(
                                                    '-'
                                                  );
                                                const monthNames = [
                                                  'Jan',
                                                  'Feb',
                                                  'Mar',
                                                  'Apr',
                                                  'May',
                                                  'Jun',
                                                  'Jul',
                                                  'Aug',
                                                  'Sep',
                                                  'Oct',
                                                  'Nov',
                                                  'Dec'
                                                ];
                                                return `${monthNames[parseInt(month) - 1]} ${year}`;
                                              })()
                                            : bookingData.preferredDate
                                              ? new Date(
                                                  bookingData.preferredDate
                                                ).toLocaleDateString('en-GB', {
                                                  day: 'numeric',
                                                  month: 'short',
                                                  year: 'numeric'
                                                })
                                              : 'Flexible'}
                                        </span>
                                      </div>

                                      {/* Location */}
                                      <div className='flex items-center justify-between text-sm'>
                                        <span className='text-gray-600 flex items-center gap-1'>
                                          <MapPin className='h-3 w-3' />
                                          Location
                                        </span>
                                        <span className='font-medium text-gray-900'>
                                          {bookingData.location === 'Online' ? (
                                            <span className='flex items-center gap-1'>
                                              <Monitor className='h-3.5 w-3.5' />
                                              Online
                                            </span>
                                          ) : (
                                            bookingData.location || 'TBD'
                                          )}
                                        </span>
                                      </div>

                                      {/* Number of Attendees */}
                                      <div className='flex items-center justify-between text-sm'>
                                        <span className='text-gray-600 flex items-center gap-1'>
                                          <Users className='h-3 w-3' />
                                          Attendees
                                        </span>
                                        <span className='font-medium text-gray-900'>
                                          {bookingData.numberOfAttendees || 1}{' '}
                                          {bookingData.numberOfAttendees == 1
                                            ? 'person'
                                            : 'people'}
                                        </span>
                                      </div>

                                      {/* Additional Notes if present */}
                                      {bookingData.additionalNotes && (
                                        <div className='pt-2 border-t border-purple-100'>
                                          <p className='text-xs text-gray-600 mb-1'>
                                            Additional Notes
                                          </p>
                                          <p className='text-sm text-gray-700'>
                                            {bookingData.additionalNotes}
                                          </p>
                                        </div>
                                      )}
                                    </div>

                                    {/* Status */}
                                    <div className='mt-3 flex items-center justify-between'>
                                      <span className='text-xs text-gray-500'>
                                        Status
                                      </span>
                                      {dockCell.status === 'booked' ? (
                                        <Badge className='bg-green-100 text-green-700 border-green-200'>
                                          Booked
                                        </Badge>
                                      ) : dockCell.status === 'quoting' ? (
                                        <Badge className='bg-amber-100 text-amber-700 border-amber-200'>
                                          Awaiting Provider Response
                                        </Badge>
                                      ) : (
                                        <Badge className='bg-gray-100 text-gray-700 border-gray-200'>
                                          {dockCell.status}
                                        </Badge>
                                      )}
                                    </div>

                                    {/* Action Buttons */}
                                    <div className='mt-4 flex gap-2'>
                                      <Button
                                        variant='default'
                                        size='sm'
                                        onClick={() => {
                                          // Navigate to the specific quote/booking if quote_id exists, otherwise go to bookings page
                                          const url = dockCell.assignment
                                            ?.quote_id
                                            ? `/space/${projectId}/bookings/${dockCell.assignment.quote_id}`
                                            : `/space/${projectId}/bookings`;
                                          navigate(url);
                                          setDockOpen(false);
                                          setTimeout(() => closeDock(), 400);
                                        }}
                                        className='flex-1'
                                      >
                                        <Eye className='h-4 w-4' />
                                        {dockCell.status === 'booked'
                                          ? 'View Booking'
                                          : 'View Quote'}
                                      </Button>
                                      <Button
                                        variant='outline'
                                        size='sm'
                                        onClick={() => {
                                          // Edit quote/booking - open the quote modal with existing data
                                          setSelectedCell(dockCell);
                                          setQuoteModalOpen(true);
                                          setDockOpen(false);
                                          setTimeout(() => closeDock(), 400);
                                        }}
                                        className='flex-1'
                                      >
                                        <Edit2 className='h-4 w-4' />
                                        Edit Details
                                      </Button>
                                    </div>
                                  </>
                                );
                              } catch (e) {
                                // Fallback to raw notes if parsing fails
                                return (
                                  <>
                                    <h4 className='text-sm font-semibold text-gray-700 mb-2'>
                                      Notes
                                    </h4>
                                    <p className='text-sm text-gray-600 whitespace-pre-wrap'>
                                      {dockCell.assignment.notes}
                                    </p>
                                  </>
                                );
                              }
                            })()
                          ) : (
                            <>
                              <h4 className='text-sm font-semibold text-gray-700 mb-2'>
                                Notes
                              </h4>
                              <p className='text-sm text-gray-600 whitespace-pre-wrap'>
                                {dockCell.assignment.notes}
                              </p>
                            </>
                          )}
                        </div>
                      )}

                      {/* Action Buttons for Quoting Status (when notes don't have full booking details) */}
                      {dockCell.status === 'quoting' &&
                        dockCell.assignment?.notes &&
                        !dockCell.assignment.notes.startsWith(
                          'Booking Request:'
                        ) && (
                          <div className='pt-4 border-t border-gray-100 space-y-2'>
                            <Button
                              variant='default'
                              size='sm'
                              onClick={() => {
                                // Debug: Log the assignment to see what we have

                                // Navigate to the specific quote if quote_id exists, otherwise go to bookings page
                                const quoteUrl = dockCell.assignment?.quote_id
                                  ? `/space/${projectId}/bookings/${dockCell.assignment.quote_id}`
                                  : `/space/${projectId}/bookings`;

                                navigate(quoteUrl);
                                setDockOpen(false);
                                setTimeout(() => closeDock(), 400);
                              }}
                              className='w-full'
                            >
                              <Eye className='h-4 w-4' />
                              View Quote
                            </Button>
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={() => {
                                // Edit quote - open the quote modal with existing data
                                setSelectedCell(dockCell);
                                setQuoteModalOpen(true);
                                setDockOpen(false);
                                setTimeout(() => closeDock(), 400);
                              }}
                              className='w-full'
                            >
                              <Edit2 className='h-4 w-4' />
                              Edit Quote
                            </Button>
                          </div>
                        )}
                    </motion.div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* Modal Popups - Lazy loaded for performance */}
        {uploadModalOpen && (
          <Suspense fallback={null}>
            <UploadModal
              isOpen={uploadModalOpen}
              onClose={() => {
                setUploadModalOpen(false);
                setDroppedFile(null);
                setUploadModalInitialOverride(false);
              }}
              selectedCell={selectedCell}
              onUpload={handleModalUpload}
              initialFile={droppedFile}
              initialOverrideMode={uploadModalInitialOverride}
              teamMembers={teamMembers}
              requirements={requirements}
            />
          </Suspense>
        )}

        {/* AI Validation Warning Dialog */}
        {validationWarning && (
          <Dialog
            open={!!validationWarning}
            onOpenChange={open => {
              if (!open) {
                setValidationWarning(null);
              }
            }}
          >
            <DialogContent className='sm:max-w-[500px]'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <AlertTriangle className='h-5 w-5 text-amber-500' />
                  Certificate Mismatch Detected
                </DialogTitle>
                <DialogDescription>
                  The AI detected a potential mismatch between the certificate
                  and the selected qualification.
                </DialogDescription>
              </DialogHeader>

              <div className='space-y-4 py-4'>
                <div className='bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3'>
                  <div>
                    <p className='text-sm font-medium text-gray-700 mb-1'>
                      Certificate appears to be for:
                    </p>
                    <p className='text-base font-semibold text-amber-900'>
                      {validationWarning.extractedTitle}
                    </p>
                  </div>

                  <div className='border-t border-amber-200 pt-3'>
                    <p className='text-sm font-medium text-gray-700 mb-1'>
                      But you're uploading it for:
                    </p>
                    <p className='text-base font-semibold text-gray-900'>
                      {validationWarning.expectedTitle}
                    </p>
                  </div>
                </div>

                <p className='text-sm text-gray-600'>
                  Are you sure you want to proceed with this upload? The
                  certificate may not match the qualification requirements.
                </p>
              </div>

              <DialogFooter className='gap-2'>
                <Button
                  variant='outline'
                  onClick={() => {
                    setValidationWarning(null);
                  }}
                >
                  Cancel Upload
                </Button>
                <Button
                  variant='default'
                  className='bg-amber-600 hover:bg-amber-700'
                  onClick={() => {
                    setValidationWarning(null);
                    handleUploadCertificate(true); // Bypass validation on second attempt
                  }}
                >
                  Upload Anyway
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {quoteModalOpen && (
          <Suspense fallback={null}>
            <QuoteModalV2
              isOpen={quoteModalOpen}
              onClose={() => {
                setQuoteModalOpen(false);
              }}
              selectedCell={selectedCell}
              onQuote={async data => {
                // If it's a success callback from "Add to quote"
                if (data?.success && data?.assignmentId) {
                  // Close the dock popup as well
                  setDockOpen(false);
                  setTimeout(() => closeDock(), 400);

                  // Update the local assignments state to reflect the new status
                  setAssignments(prev =>
                    prev.map(a =>
                      a.id === data.assignmentId
                        ? { ...a, status: 'quoting' }
                        : a
                    )
                  );

                  // Refresh assignments from database
                  await fetchData();
                } else {
                  // Handle normal quote creation
                  handleModalBooking(data);
                }
              }}
            />
          </Suspense>
        )}

        {quickQuotePopupOpen && quickQuoteCellData && (
          <Suspense fallback={null}>
            <ProfileQuickQuotePopup
              isOpen={quickQuotePopupOpen}
              onClose={() => {
                setQuickQuotePopupOpen(false);
                setQuickQuoteCellData(null);
              }}
              userId={
                quickQuoteCellData.member?.user_id ||
                quickQuoteCellData.member?.id
              }
              userName={quickQuoteCellData.member?.name || 'Unknown'}
              userAvatar={quickQuoteCellData.member?.avatar_url}
              requirementId={quickQuoteCellData.requirement?.id}
              requirementName={quickQuoteCellData.requirement?.name}
              projectId={projectId}
              onSuccess={refetchMatrixData}
            />
          </Suspense>
        )}

        {bookingModalOpen && (
          <Suspense fallback={null}>
            <BookingModal
              isOpen={bookingModalOpen}
              onClose={() => setBookingModalOpen(false)}
              selectedCell={selectedCell}
              onBook={handleModalBooking}
            />
          </Suspense>
        )}

        {detailsModalOpen && (
          <Suspense fallback={null}>
            <DetailsModal
              isOpen={detailsModalOpen}
              onClose={() => setDetailsModalOpen(false)}
              selectedCell={selectedCell}
            />
          </Suspense>
        )}

        {removeModalOpen && (
          <Suspense fallback={null}>
            <RemoveModal
              isOpen={removeModalOpen}
              onClose={() => setRemoveModalOpen(false)}
              selectedCell={selectedCell}
              onRemove={handleModalRemove}
              projectId={effectiveProjectId}
              projects={isStackView ? stackProjects : null}
              assignments={assignments}
            />
          </Suspense>
        )}

        {crossSpaceModalOpen && (
          <Suspense fallback={null}>
            <CrossSpaceQualificationModal
              isOpen={crossSpaceModalOpen}
              onClose={() => setCrossSpaceModalOpen(false)}
              selectedCell={selectedCell}
              onAssign={async () => {
                // Assign the cross-space qualification to this space
                if (selectedCell?.member && selectedCell?.requirement) {
                  try {
                    await supabase.from('compliance_assignments').upsert(
                      {
                        project_id: projectId,
                        requirement_id: selectedCell.requirement.id,
                        team_member_id: selectedCell.member.id,
                        assigned: true
                      },
                      {
                        onConflict: 'project_id,requirement_id,team_member_id'
                      }
                    );
                    // Refresh matrix data
                    await fetchMatrixData();
                    toast.success('Qualification assigned successfully');
                  } catch (error) {
                    console.error('Error assigning qualification:', error);
                    toast.error('Failed to assign qualification');
                  }
                }
              }}
            />
          </Suspense>
        )}

        {/* Certificates Viewer Modal */}
        {certificatesModalOpen && (
          <Dialog
            open={certificatesModalOpen}
            onOpenChange={setCertificatesModalOpen}
          >
            <DialogContent className='max-w-4xl max-h-[90vh] overflow-hidden flex flex-col'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <FileText className='h-5 w-5 text-blue-600' />
                  Certificates for {selectedMemberCertificates?.member?.name}
                </DialogTitle>
                <DialogDescription>
                  Viewing{' '}
                  {selectedMemberCertificates?.certificates?.length || 0}{' '}
                  certificate
                  {selectedMemberCertificates?.certificates?.length !== 1
                    ? 's'
                    : ''}
                </DialogDescription>
              </DialogHeader>
              <div className='flex-1 overflow-y-auto py-4'>
                {selectedMemberCertificates?.certificates?.length > 0 ? (
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {selectedMemberCertificates.certificates.map(
                      (cert, index) => (
                        <div
                          key={index}
                          className='border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors'
                        >
                          <div className='flex items-start justify-between mb-3'>
                            <div className='flex-1'>
                              <h4 className='font-semibold text-sm text-gray-900 mb-1'>
                                {cert.requirement?.name ||
                                  'Unknown Requirement'}
                              </h4>
                              {cert.assignment?.certificate_number && (
                                <p className='text-xs text-gray-500'>
                                  Cert #: {cert.assignment.certificate_number}
                                </p>
                              )}
                              {cert.assignment?.expiry_date && (
                                <p className='text-xs text-gray-500'>
                                  Expires:{' '}
                                  {new Date(
                                    cert.assignment.expiry_date
                                  ).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            <Badge
                              variant={
                                cert.assignment?.status === 'expired'
                                  ? 'destructive'
                                  : 'secondary'
                              }
                              className={`text-xs ${
                                cert.assignment?.status === 'valid'
                                  ? 'bg-green-100 text-green-700 border-green-200'
                                  : cert.assignment?.status === 'soon'
                                    ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                                    : ''
                              }`}
                            >
                              {cert.assignment?.status || 'Unknown'}
                            </Badge>
                          </div>
                          {cert.signedUrl && (
                            <div className='aspect-video bg-gray-100 rounded overflow-hidden mb-3'>
                              {cert.assignment.evidence_url
                                ?.toLowerCase()
                                .endsWith('.pdf') ? (
                                <iframe
                                  src={cert.signedUrl}
                                  className='w-full h-full'
                                  title={cert.requirement?.name}
                                />
                              ) : (
                                <img
                                  src={cert.signedUrl}
                                  alt={cert.requirement?.name}
                                  className='w-full h-full object-contain'
                                />
                              )}
                            </div>
                          )}
                          <Button
                            variant='outline'
                            size='sm'
                            className='w-full'
                            onClick={() =>
                              window.open(cert.signedUrl, '_blank')
                            }
                          >
                            <Eye className='h-3.5 w-3.5 mr-2' />
                            Open in New Tab
                          </Button>
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <div className='text-center py-8 text-gray-500'>
                    No certificates found
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Context Menu Overlay - captures clicks to close menu */}
        <AnimatePresence>
          {contextMenuOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              className='fixed inset-0 z-[9998]'
              onClick={() => setContextMenuOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Context Menu */}
        <AnimatePresence>
          {contextMenuOpen && contextMenuCell && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.1 }}
              className='fixed z-[9999] bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[180px]'
              ref={el => {
                if (el) {
                  const rect = el.getBoundingClientRect();
                  const pad = 8;
                  let x = contextMenuPosition.x;
                  let y = contextMenuPosition.y;
                  if (x + rect.width > window.innerWidth - pad)
                    x = window.innerWidth - rect.width - pad;
                  if (y + rect.height > window.innerHeight - pad)
                    y = window.innerHeight - rect.height - pad;
                  if (x < pad) x = pad;
                  if (y < pad) y = pad;
                  el.style.left = `${x}px`;
                  el.style.top = `${y}px`;
                }
              }}
              style={{
                left: `${contextMenuPosition.x}px`,
                top: `${contextMenuPosition.y}px`
              }}
              onClick={e => e.stopPropagation()}
            >
              <div className='px-2 py-1.5 border-b border-gray-100'>
                <div className='text-xs font-semibold text-gray-900 truncate'>
                  {contextMenuCell.member?.name}
                </div>
                <div className='text-[10px] text-gray-500 truncate'>
                  {contextMenuCell.requirement?.name}
                </div>
              </div>

              {/* Options for missing, expired, or soon statuses */}
              {(contextMenuCell.status === 'missing' ||
                contextMenuCell.status === 'expired' ||
                contextMenuCell.status === 'soon') && (
                <>
                  {/* View Certificate(s) if evidence exists */}
                  {contextMenuCell.assignment?.evidence_url && (
                    <button
                      onClick={async () => {
                        try {
                          const url = await getViewableUrl(
                            contextMenuCell.assignment.evidence_url,
                            'certificates'
                          );
                          if (url) window.open(url, '_blank');
                        } catch (error) {
                          console.error('Error viewing certificate:', error);
                          toast.error('Failed to load certificate');
                        }
                        setContextMenuOpen(false);
                      }}
                      className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
                    >
                      <Eye className='h-4 w-4' />
                      {(() => {
                        const ef = contextMenuCell.assignment?.evidenceFiles || contextMenuCell.assignment?.evidence_files || [];
                        return ef.length > 1 ? `View Certificates (${ef.length})` : 'View Certificate';
                      })()}
                    </button>
                  )}

                  <button
                    onClick={() => handleContextMenuAction('upload')}
                    className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
                  >
                    <Upload className='h-4 w-4' />
                    Upload Certificate
                  </button>

                  <button
                    onClick={() => handleContextMenuAction('profile')}
                    className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
                  >
                    <Users className='h-4 w-4' />
                    Profile
                  </button>

                  <button
                    onClick={() => handleContextMenuAction('oneclick-quote')}
                    className='w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors flex items-center gap-2 text-blue-600 font-medium'
                  >
                    <CalendarClock className='h-4 w-4' />
                    Quick Quote
                  </button>

                  <button
                    onClick={() => handleContextMenuAction('mark-booked')}
                    className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
                  >
                    <CalendarCheck className='h-4 w-4' />
                    Mark as Booked
                  </button>

                  <button
                    onClick={() => handleContextMenuAction('details')}
                    className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
                  >
                    <Info className='h-4 w-4' />
                    View Details
                  </button>

                  <div className='border-t border-gray-100 mt-1 pt-1'>
                    <button
                      onClick={() => handleContextMenuAction('remove')}
                      className='w-full px-3 py-2 text-left text-sm hover:bg-red-50 transition-colors flex items-center gap-2 text-red-600'
                    >
                      <X className='h-4 w-4' />
                      Unassign
                    </button>
                  </div>
                </>
              )}

              {/* Options for valid status */}
              {contextMenuCell.status === 'valid' && (
                <>
                  {/* View Certificate(s) if evidence exists */}
                  {contextMenuCell.assignment?.evidence_url && (
                    <button
                      onClick={async () => {
                        try {
                          const url = await getViewableUrl(
                            contextMenuCell.assignment.evidence_url,
                            'certificates'
                          );
                          if (url) window.open(url, '_blank');
                        } catch (error) {
                          console.error('Error viewing certificate:', error);
                          toast.error('Failed to load certificate');
                        }
                        setContextMenuOpen(false);
                      }}
                      className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
                    >
                      <Eye className='h-4 w-4' />
                      {(() => {
                        const ef = contextMenuCell.assignment?.evidenceFiles || contextMenuCell.assignment?.evidence_files || [];
                        return ef.length > 1 ? `View Certificates (${ef.length})` : 'View Certificate';
                      })()}
                    </button>
                  )}

                  <button
                    onClick={() => handleContextMenuAction('details')}
                    className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
                  >
                    <Info className='h-4 w-4' />
                    View Details
                  </button>

                  <button
                    onClick={() => handleContextMenuAction('oneclick-quote')}
                    className='w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors flex items-center gap-2 text-blue-600 font-medium'
                  >
                    <CalendarClock className='h-4 w-4' />
                    Quick Quote
                  </button>

                  <div className='border-t border-gray-100 mt-1 pt-1'>
                    <button
                      onClick={() => handleContextMenuAction('remove')}
                      className='w-full px-3 py-2 text-left text-sm hover:bg-red-50 transition-colors flex items-center gap-2 text-red-600'
                    >
                      <X className='h-4 w-4' />
                      Unassign
                    </button>
                  </div>
                </>
              )}

              {/* Options for booked status */}
              {contextMenuCell.status === 'booked' && (
                <>
                  <button
                    onClick={() => {
                      navigate(`/space/${projectId}/bookings`);
                      setContextMenuOpen(false);
                    }}
                    className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
                  >
                    <Calendar className='h-4 w-4' />
                    View Bookings
                  </button>

                  <button
                    onClick={() => handleContextMenuAction('details')}
                    className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
                  >
                    <Info className='h-4 w-4' />
                    View Details
                  </button>

                  <div className='border-t border-gray-100 mt-1 pt-1'>
                    <button
                      onClick={() => handleContextMenuAction('remove')}
                      className='w-full px-3 py-2 text-left text-sm hover:bg-red-50 transition-colors flex items-center gap-2 text-red-600'
                    >
                      <X className='h-4 w-4' />
                      Unassign
                    </button>
                  </div>
                </>
              )}

              {/* Options for quoting status */}
              {contextMenuCell.status === 'quoting' && (
                <>
                  <button
                    onClick={() => {
                      navigate(`/space/${projectId}/bookings`);
                      setContextMenuOpen(false);
                    }}
                    className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
                  >
                    <FileText className='h-4 w-4' />
                    View Quotes
                  </button>

                  <button
                    onClick={() => handleContextMenuAction('details')}
                    className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
                  >
                    <Info className='h-4 w-4' />
                    View Details
                  </button>

                  <div className='border-t border-gray-100 mt-1 pt-1'>
                    <button
                      onClick={() => handleContextMenuAction('remove')}
                      className='w-full px-3 py-2 text-left text-sm hover:bg-red-50 transition-colors flex items-center gap-2 text-red-600'
                    >
                      <X className='h-4 w-4' />
                      Unassign
                    </button>
                  </div>
                </>
              )}

              {/* Options for unassigned (add) status */}
              {contextMenuCell.status === 'add' && (
                <>
                  <button
                    onClick={() => handleContextMenuAction('assign')}
                    className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
                  >
                    <Plus className='h-4 w-4' />
                    Assign
                  </button>

                  <button
                    onClick={() => handleContextMenuAction('assign-and-upload')}
                    className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
                  >
                    <Upload className='h-4 w-4' />
                    Assign & Upload Certificate
                  </button>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header Context Menu Overlay - captures clicks to close menu */}
        <AnimatePresence>
          {headerContextMenuOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              className='fixed inset-0 z-[9998]'
              onClick={() => setHeaderContextMenuOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Header Context Menu (for qualification column headers) */}
        <AnimatePresence>
          {headerContextMenuOpen && headerContextMenuQual && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.1 }}
              className='fixed z-[9999] bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[200px]'
              ref={el => {
                if (el) {
                  const rect = el.getBoundingClientRect();
                  const pad = 8;
                  let x = headerContextMenuPosition.x;
                  let y = headerContextMenuPosition.y;
                  if (x + rect.width > window.innerWidth - pad)
                    x = window.innerWidth - rect.width - pad;
                  if (y + rect.height > window.innerHeight - pad)
                    y = window.innerHeight - rect.height - pad;
                  if (x < pad) x = pad;
                  if (y < pad) y = pad;
                  el.style.left = `${x}px`;
                  el.style.top = `${y}px`;
                }
              }}
              style={{
                left: `${headerContextMenuPosition.x}px`,
                top: `${headerContextMenuPosition.y}px`
              }}
              onClick={e => e.stopPropagation()}
            >
              <div className='px-3 py-2 border-b border-gray-100'>
                <div className='text-xs font-semibold text-gray-900 truncate'>
                  {headerContextMenuQual.qual?.name}
                </div>
                <div className='text-[10px] text-gray-500'>
                  {headerContextMenuQual.qualStats?.required || 0} team members
                  • {headerContextMenuQual.qualStats?.compliancePercentage || 0}
                  % compliant
                </div>
              </div>

              <button
                onClick={() =>
                  handleAssignAllToQualification(headerContextMenuQual.qual)
                }
                className='w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors flex items-center gap-2 text-blue-600 font-medium'
              >
                <Users className='h-4 w-4' />
                Assign to All
              </button>

              <button
                onClick={() => {
                  toggleColumnHighlight(headerContextMenuQual.qual.id);
                  setHeaderContextMenuOpen(false);
                }}
                className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
              >
                <Columns className='h-4 w-4' />
                {highlightedColumns.has(headerContextMenuQual.qual?.id)
                  ? 'Remove Highlight'
                  : 'Highlight Column'}
              </button>

              <button
                onClick={() => {
                  setOpenQualificationGapId(headerContextMenuQual.qual.id);
                  setHeaderContextMenuOpen(false);
                }}
                className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700'
              >
                <BarChart3 className='h-4 w-4' />
                View GAP Analysis
              </button>

            </motion.div>
          )}
        </AnimatePresence>

        {/* Add Member Dialog */}
        {addMemberDialogOpen && (
          <AddMemberDialog
            open={addMemberDialogOpen}
            onOpenChange={setAddMemberDialogOpen}
            availableMembers={availableMembers}
            onAddMember={handleAddToMatrix}
          />
        )}

        {/* Add Qualification Dialog */}
        {addQualificationDialogOpen && (
          <AddQualificationDialog
            open={addQualificationDialogOpen}
            onOpenChange={setAddQualificationDialogOpen}
            availableQualifications={availableQualifications}
            onAddQualification={handleAddQualificationToMatrix}
            onCreateNew={() => setIsAddQualificationModalOpen(true)}
          />
        )}

        {/* Add Team Members to Qualification Dialog */}
        {addMembersDialogOpen && (
          <AddMembersToQualificationDialog
            open={addMembersDialogOpen}
            onOpenChange={setAddMembersDialogOpen}
            qualification={selectedQualificationForAdd}
            availableMembers={teamMembers.filter(member => {
              const hasAssignment = assignments.some(
                a =>
                  a.team_member_id === member.id &&
                  a.requirement_id === selectedQualificationForAdd?.id
              );
              return !hasAssignment;
            })}
            onAddMembers={memberIds => {
              setSelectedMembersToAdd(memberIds);
              handleAddMembersToQualification();
            }}
          />
        )}

        {/* Smart Upload Processing Overlay */}
        <AnimatePresence>
          {smartUploadProcessing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className='fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm'
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className='bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center'
              >
                <div className='relative mb-6'>
                  <div className='w-16 h-16 mx-auto border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin' />
                  <Sparkles className='w-6 h-6 text-blue-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' />
                </div>
                <h3 className='text-xl font-bold text-gray-900 mb-2'>
                  AI is analyzing your certificate
                </h3>
                <p className='text-gray-600'>
                  Reading the document to find the person and course name...
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Qualification Dialog */}
        <Suspense fallback={null}>
          {editQualDialogOpen && (
            <EditQualification
              isOpen={editQualDialogOpen}
              onClose={() => setEditQualDialogOpen(false)}
              selectedQualification={editQualSelected}
              onSuccess={refetchMatrixData}
              teamMemberCount={teamMembers?.length || 0}
            />
          )}
        </Suspense>

        {/* Smart Upload Confirmation Dialog */}
        {smartUploadConfirmation && (
          <SmartUploadConfirmationDialog
            open={!!smartUploadConfirmation}
            data={smartUploadConfirmation}
            onConfirm={handleConfirmSmartUpload}
            onDecline={handleDeclineSmartUpload}
            teamMembers={teamMembers}
            requirements={requirements}
          />
        )}

        {/* Bulk Upload Confirmation Dialog */}
        {bulkUploadConfirmation && (
          <BulkUploadConfirmationDialog
            open={!!bulkUploadConfirmation}
            data={bulkUploadConfirmation}
            isSaving={bulkUploadSaving}
            onConfirm={handleConfirmBulkUpload}
            onCancel={handleCancelBulkUpload}
          />
        )}

        {/* Add Qualification Modal */}
        {isAddQualificationModalOpen && (
          <Suspense fallback={null}>
            <AddQualification
              isOpen={isAddQualificationModalOpen}
              onClose={() => setIsAddQualificationModalOpen(false)}
              projects={isStackView && stackProjects?.length > 0
                ? stackProjects
                : [{ id: projectId, name: space?.name || 'Space' }]
              }
              onSuccess={handleQualificationCreated}
              defaultProjectId={isStackView ? undefined : projectId}
              teamMemberCount={teamMembers?.length || 0}
            />
          </Suspense>
        )}
      </div>

      {/* Delete View Confirmation Dialog */}
      {deleteViewDialogOpen && (
        <DeleteViewDialog
          open={deleteViewDialogOpen}
          onOpenChange={setDeleteViewDialogOpen}
          viewToDelete={viewToDelete}
          onConfirmDelete={async viewId => {
            await deleteView(viewId);
            setViewToDelete(null);
          }}
          onCancel={() => {
            setDeleteViewDialogOpen(false);
            setViewToDelete(null);
          }}
        />
      )}

      {/* Space Selector Dialog - For stack view assignments */}
      {isStackView && stackProjects && (
        <SpaceSelectorDialog
          open={spaceSelectorOpen}
          onOpenChange={setSpaceSelectorOpen}
          spaces={stackProjects}
          memberName={pendingAssignment?.member?.name || ''}
          qualificationName={pendingAssignment?.requirement?.name || ''}
          onConfirm={handleSpaceSelectionConfirm}
        />
      )}

      {/* Mark as Booked Confirmation Dialog */}
      <Dialog open={markBookedConfirmOpen} onOpenChange={(open) => { if (!open) { setMarkBookedConfirmOpen(false); setMarkBookedCell(null); } }}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Mark as Booked</DialogTitle>
            <DialogDescription>
              This will mark <span className='font-semibold text-gray-900'>{markBookedCell?.requirement?.name}</span> as booked for <span className='font-semibold text-gray-900'>{markBookedCell?.member?.name}</span>. This is a manual status change and will not be linked to any booking.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => { setMarkBookedConfirmOpen(false); setMarkBookedCell(null); }}>Cancel</Button>
            <Button onClick={handleConfirmMarkBooked}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drop Zone Banner - Covers full header area as a proper drop target.
          Events handled by the capture-phase global handler via dropBannerRef (React portal
          events don't reliably fire for portals to document.body). */}
      {isDraggingFiles &&
        createPortal(
          <div
            ref={dropBannerRef}
            className='fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center bg-gradient-to-b from-blue-600 via-blue-500 to-blue-400/90 backdrop-blur-sm shadow-lg border-b-2 border-blue-300/50'
            style={{ height: '112px' }}
          >
            <div className='flex items-center gap-4 px-6 py-3 bg-white/15 rounded-2xl border-2 border-dashed border-white/40 backdrop-blur-sm'>
              <div className='relative'>
                <div className='w-14 h-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30'>
                  <Plus className='w-7 h-7 text-white stroke-[3]' />
                </div>
                <div className='absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center shadow-lg border border-white/30'>
                  <Sparkles className='w-3 h-3 text-white' />
                </div>
              </div>
              <div>
                <p className='text-base font-bold text-white drop-shadow-sm'>
                  Drop here for AI Smart Match
                </p>
                <p className='text-sm text-white/80'>
                  Or drop directly on a cell to upload to that person
                </p>
              </div>
            </div>
          </div>,
          document.body
        )}
    </PageTransition>
  );
};

export default MatrixPage;
